import type { Lang, LocalizedName } from "@cafe-os/types";
import { GUEST_LANG_OVERLAY } from "./guestLangOverlay";

// strings — таблица UI-строк. Тип Partial: новые языки (zh/ky/uz) добавляются
// инкрементально без 1290 TS-ошибок, недостающие деградируют на ru через t().
// Полноту ЯДРА (kz/ru/en) страхует тест missingCoreLangKeys() — не компилятор.
const strings: Record<string, Partial<Record<Lang, string>>> = {
  // Баннер «новая версия деплоя» (versionWatcher.ts, @cafe-os/utils) — общий для
  // всех кабинетов (гость + staff), поэтому переведён на все 6 языков.
  "update_banner.title": {
    kz: "Жаңа нұсқа қолжетімді",
    ru: "Доступна новая версия",
    en: "A new version is available",
    zh: "有新版本可用",
    ky: "Жаңы версия жеткиликтүү",
    uz: "Yangi versiya mavjud",
  },
  "update_banner.subtitle": {
    kz: "Соңғы өзгерістерді көру үшін жаңартыңыз",
    ru: "Обновите, чтобы увидеть последние изменения",
    en: "Update to see the latest changes",
    zh: "更新以查看最新更改",
    ky: "Акыркы өзгөрүүлөрдү көрүү үчүн жаңылаңыз",
    uz: "So‘nggi o‘zgarishlarni ko‘rish uchun yangilang",
  },
  "update_banner.button": {
    kz: "Жаңарту",
    ru: "Обновить",
    en: "Update",
    zh: "更新",
    ky: "Жаңылоо",
    uz: "Yangilash",
  },
  "guest.welcome": {
    kz: "Orda OS — қонақ мәзірі",
    ru: "Orda OS — гостевое меню",
    en: "Orda OS — guest menu",
  },
  "guest.dev_home": {
    kz: "Әзірлеу басты беті",
    ru: "Служебная страница разработки",
    en: "Developer home",
  },
  "guest.table_open": {
    kz: "{label}",
    ru: "{label}",
    en: "{label}",
  },
  "guest.table_strip": {
    kz: "{label}",
    ru: "{label}",
    en: "{label}",
  },
  // Короткая формулировка (2026-07-25): подсказка стоит в узком зазоре между
  // чипами категорий и строкой поиска и не переносится (whitespace-nowrap),
  // поэтому длинная фраза вылезала бы за экран телефона.
  "guest.swipe_hint": {
    kz: "Солға-оңға свайп — тағам ауыстыру",
    ru: "Свайпайте влево-вправо — смена блюд",
    en: "Swipe left–right to switch dishes",
  },
  "guest.currency_switch_aria": {
    kz: "Валюта және баға көрсету",
    ru: "Валюта и курсы",
    en: "Currency and exchange rates",
  },
  "guest.currency_source_live": {
    kz: "ҚРҰБ",
    ru: "НБ РК",
    en: "NBK",
  },
  "guest.currency_source_cache": {
    kz: "кэш",
    ru: "кэш",
    en: "cached",
  },
  "guest.currency_source_offline": {
    kz: "офлайн",
    ru: "оффлайн",
    en: "offline",
  },
  "guest.currency_detail_kzt": {
    kz: "ҚРҰБ бағамдары · жаңартылды {time} ({source})",
    ru: "Курсы НБ РК · обновлено {time} ({source})",
    en: "NBK rates · updated {time} ({source})",
  },
  "guest.currency_detail_uzs": {
    kz: "1 ₸ ≈ {n} сум · ҚРҰБ · жаңартылды {time} ({source})",
    ru: "1 ₸ ≈ {n} сум · НБ РК · обновлено {time} ({source})",
    en: "1 ₸ ≈ {n} UZS · NBK · updated {time} ({source})",
  },
  "guest.currency_detail_other": {
    kz: "1 {symbol} = {rate} ₸ · ҚРҰБ · жаңартылды {time} ({source})",
    ru: "1 {symbol} = {rate} ₸ · НБ РК · обновлено {time} ({source})",
    en: "1 {symbol} = {rate} ₸ · NBK · updated {time} ({source})",
  },
  "guest.currency_opt_kzt": {
    kz: "ҚРҰБ бағамы ({date})",
    ru: "Курс НБРК ({date})",
    en: "NBK rate ({date})",
  },
  "guest.table_not_found": {
    kz: "Стол табылмады",
    ru: "Стол не найден",
    en: "Table not found",
  },
  "guest.table_denied": {
    kz: "Стол қолжетімсіз (QR токені дұрыс емес)",
    ru: "Стол недоступен (неверный токен QR)",
    en: "Table unavailable (invalid QR token)",
  },
  "guest.table_dev_no_token": {
    kz: "Даму режимі: URL-да token жоқ, бірақ кесте құлыпталған.",
    ru: "Режим разработки: в URL нет token, но стол требует токен.",
    en: "Dev mode: missing token in URL while table is locked.",
  },
  "guest.table_enter_name": {
    kz: "Атыңыз",
    ru: "Ваше имя",
    en: "Your name",
  },
  "guest.table_continue": {
    kz: "Жалғастыру",
    ru: "Продолжить",
    en: "Continue",
  },
  "guest.table_greeting": {
    kz: "Қош келдіңіз, {name}!",
    ru: "Добро пожаловать, {name}!",
    en: "Welcome, {name}!",
  },
  "guest.welcome_back": {
    kz: "Қайта оралғаныңызға қуаныштымыз, {name}!",
    ru: "С возвращением, {name}!",
    en: "Welcome back, {name}!",
  },
  "guest.name_collision_title": {
    kz: "«{name}» есімді қонақ үстелде бар",
    ru: "«{name}» уже за столом",
    en: "Someone named «{name}» is already at the table",
  },
  "guest.name_collision_question": {
    kz: "Бұл сізсіз бе, әлде атыңыз бір қонақ па?",
    ru: "Это вы возвращаетесь или это тёзка?",
    en: "Is this you returning, or just a namesake?",
  },
  "guest.name_collision_me": {
    kz: "Бұл мен",
    ru: "Это я",
    en: "It's me",
  },
  "guest.name_collision_namesake": {
    kz: "Атас (жаңа қонақ)",
    ru: "Тёзка (новый гость)",
    en: "Namesake (different guest)",
  },
  "guest.name_collision_namesake_hint": {
    kz: "Шатастырмас үшін есімге нөмір қосамыз: «{name}»",
    ru: "Чтобы не путать, добавим номер: «{name}»",
    en: "We'll add a number to avoid confusion: «{name}»",
  },
  "guest.name_collision_cancel": {
    kz: "Болдырмау",
    ru: "Отмена",
    en: "Cancel",
  },
  "guest.table_example_link": {
    kz: "Мысал: /table/demo?token=… (seed-table.example.json)",
    ru: "Пример: /table/demo?token=… (см. seed-table.example.json)",
    en: "Example: /table/demo?token=… (see seed-table.example.json)",
  },
  "guest.menu_title": {
    kz: "Мәзір",
    ru: "Меню",
    en: "Menu",
  },
  "guest.cart_title": {
    kz: "Себет",
    ru: "Корзина",
    en: "Cart",
  },
  "guest.add_to_cart": {
    kz: "Себетке",
    ru: "В корзину",
    en: "Add to cart",
  },
  "guest.ingredients": {
    kz: "Құрамы",
    ru: "Состав",
    en: "Ingredients",
  },
  "guest.close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "guest.call_waiter": {
    kz: "Даяшыны шақыру",
    ru: "Вызов официанта",
    en: "Call waiter",
  },
  "guest.send": {
    kz: "Жіберу",
    ru: "Отправить",
    en: "Send",
  },
  "guest.waiter_stub_toast": {
    kz: "Тапсырыс жүйеге қосылуда (жақында).",
    ru: "Отправка в систему появится позже.",
    en: "Sending to staff will be available soon.",
  },
  "guest.waiter_sending": {
    kz: "Жіберілуде…",
    ru: "Отправляем…",
    en: "Sending…",
  },
  "guest.waiter_sent": {
    kz: "Даяшыға хабар жіберілді",
    ru: "Официант уведомлён",
    en: "Waiter notified",
  },
  "guest.waiter_sent_hint": {
    kz: "Біздің қызметкер бірнеше минутта келеді.",
    ru: "Наш сотрудник подойдёт через минуту.",
    en: "Our staff will be with you shortly.",
  },
  "guest.waiter_send_failed": {
    kz: "Жіберілмеді. Қайта көріңіз.",
    ru: "Не удалось отправить. Попробуйте ещё раз.",
    en: "Failed to send. Please try again.",
  },
  "guest.waiter_note": {
    kz: "Ескерту (міндетті емес)",
    ru: "Комментарий (необязательно)",
    en: "Note (optional)",
  },
  "guest.waiter_call_again": {
    kz: "Қайта шақыру",
    ru: "Позвать снова",
    en: "Call again",
  },
  "guest.waiter_cooldown": {
    kz: "Қайта шақыруға {sec} с қалды",
    ru: "Позвать снова можно через {sec} с",
    en: "You can call again in {sec}s",
  },
  "guest.table_number": {
    kz: "Үстел",
    ru: "Стол",
    en: "Table",
  },
  "guest.no_description": {
    kz: "Құрамын даяшыдан сұраңыз.",
    ru: "Состав уточняйте у персонала.",
    en: "Ask staff for ingredients details.",
  },
  "guest.set_includes": {
    kz: "Сетке кіреді",
    ru: "В сет входит",
    en: "Set includes",
  },
  "guest.set_item_unavailable": {
    kz: "қазір жоқ",
    ru: "нет в наличии",
    en: "unavailable",
  },
  // 00466: опции блюда (размер / соусы).
  "guest.dish_options_required": {
    kz: "Міндетті",
    ru: "Обязательно",
    en: "Required",
  },
  "guest.dish_options_pick_one": {
    kz: "Біреуін таңдаңыз",
    ru: "Выберите один вариант",
    en: "Choose one",
  },
  "guest.dish_options_pick_up_to": {
    kz: "{n}-ге дейін таңдаңыз",
    ru: "Выберите до {n}",
    en: "Choose up to {n}",
  },
  "guest.dish_options_pick_range": {
    kz: "{min}–{max} таңдаңыз",
    ru: "Выберите от {min} до {max}",
    en: "Choose {min}–{max}",
  },
  "guest.dish_options_invalid": {
    kz: "Опцияларды таңдаңыз",
    ru: "Выберите опции блюда",
    en: "Choose the dish options",
  },
  "guest.place_order_soon": {
    kz: "Тапсырыс жақында",
    ru: "Оформление заказа скоро",
    en: "Checkout coming soon",
  },
  "guest.cart_empty": {
    kz: "Себет бос",
    ru: "Корзина пуста",
    en: "Cart is empty",
  },
  "guest.total": {
    kz: "Барлығы",
    ru: "Итого",
    en: "Total",
  },
  "guest.items_count": {
    kz: "{n} тауар",
    ru: "{n} поз.",
    en: "{n} items",
  },
  "guest.loading": {
    kz: "Жүктелуде…",
    ru: "Загрузка…",
    en: "Loading…",
  },
  "guest.session_connecting": {
    kz: "Сессия қосылуда…",
    ru: "Подключаем сессию стола…",
    en: "Connecting table session…",
  },
  "guest.wait_session": {
    kz: "Сессияны күтіңіз немесе жоғарыдағы қатені түзетіңіз.",
    ru: "Дождитесь сессии или исправьте ошибку выше.",
    en: "Wait for session or fix the error above.",
  },
  "guest.menu_after_name": {
    kz: "Атыңызды енгізгеннен кейін мәзір ашылады.",
    ru: "Меню и корзина откроются после ввода имени.",
    en: "Menu and cart unlock after you enter your name.",
  },
  "guest.public_menu_subtitle": {
    kz: "Кафе мәзірі",
    ru: "Меню кафе",
    en: "Café menu",
  },
  "guest.public_menu_cta_title": {
    kz: "Тапсырыс беру үшін",
    ru: "Чтобы заказать",
    en: "To place an order",
  },
  "guest.public_menu_cta_scan": {
    kz: "Столдағы QR-кодты сканерлеңіз",
    ru: "Отсканируйте QR-код на вашем столе",
    en: "Scan the QR code on your table",
  },
  "guest.public_menu_cta_demo": {
    kz: "Demo стол ашу",
    ru: "Открыть demo-стол",
    en: "Open demo table",
  },
  "guest.public_menu_loading": {
    kz: "Мәзір жүктелуде…",
    ru: "Загружаем меню…",
    en: "Loading menu…",
  },
  "guest.public_menu_empty": {
    kz: "Мәзір қазір бос",
    ru: "Меню сейчас пусто",
    en: "Menu is empty right now",
  },
  "guest.public_menu_error": {
    kz: "Мәзірді жүктеу мүмкін болмады",
    ru: "Не удалось загрузить меню",
    en: "Could not load the menu",
  },
  "guest.dish_order_locked_title": {
    kz: "Тапсырысқа қол жетімсіз",
    ru: "Заказ недоступен",
    en: "Ordering unavailable",
  },
  "guest.dish_order_locked_hint": {
    kz: "Бұл блюданы тапсырыс беру үшін столдағы QR-кодты сканерлеңіз.",
    ru: "Чтобы заказать это блюдо — отсканируйте QR-код на вашем столе.",
    en: "To order this dish, scan the QR code on your table.",
  },
  "guest.public_menu_no_categories": {
    kz: "Басқа",
    ru: "Прочее",
    en: "Other",
  },
  "guest.public_add": {
    kz: "Қосу",
    ru: "Добавить",
    en: "Add",
  },
  "guest.public_added_toast": {
    kz: "Себетке қосылды",
    ru: "Добавлено в корзину",
    en: "Added to cart",
  },
  "guest.public_cart_aria": {
    kz: "Себет",
    ru: "Корзина",
    en: "Cart",
  },
  "guest.public_cart_subtitle": {
    kz: "Тапсырысты безендіру үшін басыңыз",
    ru: "Нажмите для оформления",
    en: "Tap to checkout",
  },
  "guest.public_cart_checkout": {
    kz: "Безендіру",
    ru: "Оформить",
    en: "Checkout",
  },
  "guest.checkout_title": {
    kz: "Қалай тапсырыс беремін?",
    ru: "Как заказать?",
    en: "How to order?",
  },
  "guest.screen_loading": {
    kz: "Жүктелуде…",
    ru: "Загрузка…",
    en: "Loading…",
  },
  "guest.screen_refresh": {
    kz: "Жаңарту",
    ru: "Обновить",
    en: "Refresh",
  },
  "guest.screen_stuck_title": {
    kz: "Мәзірді жүктеу мүмкін болмады",
    ru: "Не удалось загрузить меню",
    en: "Couldn't load the menu",
  },
  "guest.screen_stuck_hint": {
    kz: "Байланыс үзілген сияқты. «Жаңарту» басыңыз — әдетте көмектеседі.",
    ru: "Похоже, соединение зависло. Нажмите «Обновить» — обычно это помогает.",
    en: "The connection seems stuck. Tap “Refresh” — that usually helps.",
  },
  "guest.error_ask_waiter": {
    kz: "Даяшыдан көмек сұраңыз.",
    ru: "Попросите официанта помочь.",
    en: "Ask a waiter for help.",
  },
  "guest.error_table_not_specified": {
    kz: "Үстел көрсетілмеген",
    ru: "Стол не указан",
    en: "No table specified",
  },
  "guest.join_auth_failed": {
    kz: "Қосылу мүмкін болмады. Интернетті тексеріп, қайталап көріңіз.",
    ru: "Не удалось подключиться. Проверьте интернет и попробуйте ещё раз.",
    en: "Couldn't connect. Check your internet and try again.",
    zh: "无法连接。请检查网络后重试。",
    ky: "Туташуу мүмкүн болгон жок. Интернетти текшерип, кайра аракет кылыңыз.",
    uz: "Ulanib boʻlmadi. Internetni tekshirib, qayta urinib koʻring.",
  },
  "guest.error_tenant_suspended": {
    kz: "Кафе уақытша қолжетімсіз. Қызмет тоқтатылды — мекеме әкімшісіне хабарласыңыз.",
    ru: "Кафе временно недоступно. Обслуживание приостановлено — обратитесь к администратору заведения.",
    en: "The café is temporarily unavailable. Service is suspended — contact the venue administrator.",
  },
  "guest.error_table_not_found": {
    kz: "Үстел табылмады",
    ru: "Стол не найден",
    en: "Table not found",
  },
  "guest.stale_qr_title": {
    kz: "QR-код ескірген",
    ru: "QR-код устарел",
    en: "QR code is out of date",
  },
  "guest.stale_qr_body": {
    kz: "Үстел жабылды. Мәзірді ашу үшін үстелдегі QR-кодты қайта сканерлеңіз.",
    ru: "Стол был закрыт. Отсканируйте QR-код на столе заново, чтобы открыть меню.",
    en: "The table was closed. Scan the QR code on the table again to open the menu.",
  },
  "guest.checkout_tab_delivery": {
    kz: "🛵 Үйге жеткізу",
    ru: "🛵 Доставка домой",
    en: "🛵 Delivery",
  },
  "guest.checkout_cart_title": {
    kz: "Себеттегі тағамдар",
    ru: "В корзине",
    en: "In cart",
  },
  "guest.checkout_total": {
    kz: "Барлығы",
    ru: "Итого",
    en: "Total",
  },
  "guest.checkout_delivery": {
    kz: "Жеткізу",
    ru: "Доставка",
    en: "Delivery",
  },
  "guest.checkout_fulfillment_delivery": {
    kz: "Курьермен жеткізу",
    ru: "Доставка курьером",
    en: "Courier delivery",
    zh: "快递配送",
    ky: "Курьер менен жеткирүү",
    uz: "Kuryer yetkazib berish",
  },
  "guest.checkout_fulfillment_pickup": {
    kz: "Өзім аламын",
    ru: "Самовывоз",
    en: "Pickup",
    zh: "自取",
    ky: "Өзүм алам",
    uz: "Olib ketish",
  },
  "guest.checkout_pickup_note": {
    kz: "Тапсырысты кафеден өзіңіз аласыз",
    ru: "Заберёте заказ сами в кафе",
    en: "You'll pick up your order at the café",
    zh: "您将自行到店取餐",
    ky: "Буйрутманы кафеден өзүңүз аласыз",
    uz: "Buyurtmani kafedan o'zingiz olasiz",
  },
  "guest.checkout_pickup_where": {
    kz: "Кафе қайда орналасқан",
    ru: "Где находится кафе",
    en: "Where the café is",
    zh: "咖啡厅位置",
    ky: "Кафе кайда жайгашкан",
    uz: "Kafe qayerda joylashgan",
  },
  "guest.checkout_pickup_route": {
    kz: "2ГИС-те маршрут",
    ru: "Маршрут в 2ГИС",
    en: "Route in 2GIS",
    zh: "在 2GIS 中查看路线",
    ky: "2ГИС-те маршрут",
    uz: "2GIS'da marshrut",
  },
  "guest.checkout_add_tableware": {
    kz: "Бір реттік ыдыс қосу",
    ru: "Добавить одноразовую посуду",
    en: "Add disposable tableware",
    zh: "添加一次性餐具",
    ky: "Бир жолку идиш кошуу",
    uz: "Bir martalik idish qo'shish",
  },
  "guest.checkout_free": {
    kz: "тегін",
    ru: "бесплатно",
    en: "free",
  },
  "guest.checkout_where_deliver": {
    kz: "Қайда жеткізу",
    ru: "Куда доставить",
    en: "Where to deliver",
  },
  "guest.checkout_pin_hint": {
    kz: "Жасыл пинді керек мекенжайға сүйреңіз — жеткізу құны автоматты есептеледі. Алтын шеңбер — кафенің жеткізу аймағы.",
    ru: "Перетащите зелёный пин на нужный адрес — стоимость доставки посчитается автоматически. Золотой круг — зона доставки кафе.",
    en: "Drag the green pin to your address — the delivery cost is calculated automatically. The gold circle is the café's delivery zone.",
  },
  "guest.checkout_km_from_cafe": {
    kz: " ({km} км кафеден)",
    ru: " ({km} км от кафе)",
    en: " ({km} km from café)",
  },
  "guest.checkout_out_of_zone": {
    kz: "Бұл нүкте жеткізу аймағынан тыс{dist}. Пинді жақынырақ жылжытыңыз.",
    ru: "Эта точка вне зоны доставки{dist}. Передвиньте пин ближе.",
    en: "This point is outside the delivery zone{dist}. Move the pin closer.",
  },
  "guest.checkout_in_zone": {
    kz: "Жеткізу аймағында",
    ru: "В зоне доставки",
    en: "In delivery zone",
  },
  "guest.checkout_km": {
    kz: "км",
    ru: "км",
    en: "km",
  },
  "guest.checkout_place_pin": {
    kz: "Жеткізуді есептеу үшін пин қойыңыз.",
    ru: "Поставьте пин, чтобы рассчитать доставку.",
    en: "Place the pin to calculate delivery.",
  },
  "guest.checkout_my_location": {
    kz: "Менің орналасқан жерім",
    ru: "Моё местоположение",
    en: "My location",
  },
  "guest.checkout_tap_my_location": {
    kz: "«Менің орналасқан жерім» түймесін басып, геолокацияға рұқсат беріңіз — немесе картадан пин қойыңыз.",
    ru: "Нажмите «Моё местоположение» и разрешите доступ к местоположению — или поставьте пин на карте.",
    en: "Tap “My location” and allow location access — or place the pin on the map.",
  },
  "guest.checkout_geo_denied": {
    kz: "Геолокацияға рұқсат жоқ. Оны браузерде рұқсат етіңіз немесе картадан пин қойыңыз.",
    ru: "Доступ к местоположению запрещён. Разрешите его в браузере или поставьте пин на карте.",
    en: "Location access is blocked. Allow it in your browser, or place the pin on the map.",
  },
  "guest.checkout_geo_failed": {
    kz: "Орналасқан жерді анықтау мүмкін болмады. Картадан пин қойыңыз.",
    ru: "Не удалось определить местоположение. Поставьте пин на карте.",
    en: "Couldn’t get your location. Place the pin on the map.",
  },
  "guest.checkout_saved_pin_hint": {
    kz: "Өткен реттегі нүкте қойылды. Мекенжай өзгерсе — картаны түртіп, пинді жаңа жерге қойыңыз.",
    ru: "Подставлена точка с прошлого раза. Если адрес другой — нажмите на карту и поставьте пин заново.",
    en: "Using your saved location. If the address is different, tap the map to move the pin.",
  },
  "guest.checkout_when_title": {
    kz: "Қашан жеткізу керек?",
    ru: "Когда привезти?",
    en: "When to deliver?",
  },
  "guest.checkout_when_title_pickup": {
    kz: "Қашан аласыз?",
    ru: "Когда заберёте?",
    en: "When will you pick it up?",
  },
  "guest.checkout_when_asap": {
    kz: "Мүмкіндігінше тез",
    ru: "Как можно скорее",
    en: "As soon as possible",
  },
  "guest.checkout_when_asap_sub": {
    kz: "Дайын болған соң бірден жеткіземіз",
    ru: "Привезём сразу, как будет готово",
    en: "We'll bring it as soon as it's ready",
  },
  "guest.checkout_when_asap_sub_pickup": {
    kz: "Дайын болған соң бірден аласыз",
    ru: "Заберёте сразу, как будет готово",
    en: "Pick it up as soon as it's ready",
  },
  "guest.checkout_when_scheduled": {
    kz: "Белгілі уақытқа",
    ru: "Ко времени",
    en: "At a set time",
  },
  "guest.checkout_when_scheduled_sub": {
    kz: "Ыңғайлы уақытты таңдаңыз",
    ru: "Выберите удобное время",
    en: "Pick a convenient time",
  },
  "guest.checkout_when_today": {
    kz: "Бүгін",
    ru: "Сегодня",
    en: "Today",
  },
  "guest.checkout_when_tomorrow": {
    kz: "Ертең",
    ru: "Завтра",
    en: "Tomorrow",
  },
  "guest.checkout_slot_expired": {
    kz: "Таңдалған уақыт өтіп кетті — қайта таңдаңыз.",
    ru: "Выбранное время истекло — выберите заново.",
    en: "The selected time has passed — please pick again.",
  },
  "guest.checkout_payment_method": {
    kz: "Төлеу тәсілі",
    ru: "Способ оплаты",
    en: "Payment method",
  },
  "guest.checkout_pay_cod_title": {
    kz: "Алғанда қолма-қол",
    ru: "Наличными при получении",
    en: "Cash on delivery",
  },
  "guest.checkout_pay_cod_sub": {
    kz: "Жеткізгенде курьерге төлеңіз",
    ru: "Заплатите курьеру при доставке",
    en: "Pay the courier on delivery",
  },
  "guest.checkout_pay_cod_title_pickup": {
    kz: "Алғанда қолма-қол",
    ru: "Наличными при получении",
    en: "Cash on pickup",
  },
  "guest.checkout_pay_cod_sub_pickup": {
    kz: "Тапсырысты алғанда кассада төлеңіз",
    ru: "Заплатите на кассе при получении",
    en: "Pay at the counter when collecting",
  },
  "guest.checkout_pay_transfer_title": {
    kz: "Kaspi нөміріне аудару",
    ru: "Перевод на номер Kaspi",
    en: "Kaspi transfer",
  },
  "guest.checkout_pay_transfer_sub": {
    kz: "Кафе нөміріне аударыңыз — кассир түскенін растайды",
    ru: "Переведите на номер кафе — кассир подтвердит поступление",
    en: "Transfer to the café's number — the cashier confirms receipt",
  },
  "guest.checkout_recipient": {
    kz: "Алушы: {name}",
    ru: "Получатель: {name}",
    en: "Recipient: {name}",
  },
  "guest.checkout_pay_remote_title": {
    kz: "Қашықтан Kaspi арқылы",
    ru: "Удалённо через Kaspi",
    en: "Remotely via Kaspi",
  },
  "guest.checkout_pay_remote_sub": {
    kz: "Кассир Kaspi сұрауын нөміріңізге жібереді. Төлегенде тапсырысты дайындаймыз.",
    ru: "Кассир пришлёт запрос Kaspi на ваш номер. Готовим заказ когда оплатите.",
    en: "The cashier sends a Kaspi request to your number. We prepare the order once you pay.",
  },
  "guest.checkout_kaspi_api_soon": {
    kz: "🚧 Автоматты Kaspi API төлемі — жақында",
    ru: "🚧 Автоматическая оплата Kaspi API — скоро",
    en: "🚧 Automatic Kaspi API payment — coming soon",
  },
  "guest.cart_decrement": {
    kz: "Азайту",
    ru: "Уменьшить",
    en: "Decrease",
  },
  "guest.cart_increment": {
    kz: "Көбейту",
    ru: "Увеличить",
    en: "Increase",
  },
  "guest.cart_remove": {
    kz: "Жою",
    ru: "Удалить",
    en: "Remove",
  },
  "guest.delivery_name_label": {
    kz: "Аты",
    ru: "Имя",
    en: "Name",
  },
  "guest.delivery_name_placeholder": {
    kz: "Сіздің атыңыз",
    ru: "Как вас зовут",
    en: "Your name",
  },
  "guest.delivery_phone_label": {
    kz: "Телефон",
    ru: "Телефон",
    en: "Phone",
  },
  "guest.delivery_address_label": {
    kz: "Жеткізу мекенжайы",
    ru: "Адрес доставки",
    en: "Delivery address",
  },
  "guest.delivery_address_placeholder": {
    kz: "Көше, үй, пәтер",
    ru: "Улица, дом, квартира",
    en: "Street, building, apartment",
  },
  "guest.delivery_notes_label": {
    kz: "Қосымша (міндетті емес)",
    ru: "Комментарий (необязательно)",
    en: "Notes (optional)",
  },
  "guest.delivery_notes_placeholder": {
    kz: "Кіреберіс, қабат, домофон коды",
    ru: "Подъезд, этаж, код домофона",
    en: "Entrance, floor, door code",
  },
  "guest.pickup_notes_placeholder": {
    kz: "Тапсырысқа тілектер",
    ru: "Пожелания к заказу",
    en: "Order notes",
    zh: "订单备注",
    ky: "Буйрутмага каалоолор",
    uz: "Buyurtmaga izohlar",
  },
  "guest.delivery_payment_title": {
    kz: "Төлеу әдісі",
    ru: "Способ оплаты",
    en: "Payment",
  },
  "guest.delivery_payment_cod": {
    kz: "Курьерге қолма-қол",
    ru: "Наличными курьеру",
    en: "Cash on delivery",
  },
  "guest.delivery_payment_kaspi_soon": {
    kz: "Kaspi QR — жақын арада",
    ru: "Kaspi QR — скоро",
    en: "Kaspi QR — coming soon",
  },
  "guest.delivery_submit": {
    kz: "Тапсырысты жіберу",
    ru: "Отправить заказ",
    en: "Send order",
  },
  "guest.delivery_submitting": {
    kz: "Жіберуде…",
    ru: "Отправляем…",
    en: "Sending…",
  },
  "guest.delivery_error": {
    kz: "Тапсырысты жіберу мүмкін болмады",
    ru: "Не удалось отправить заказ",
    en: "Could not send the order",
  },
  "guest.delivery_success_title": {
    kz: "Рақмет, {name}!",
    ru: "Спасибо, {name}!",
    en: "Thank you, {name}!",
  },
  "guest.delivery_success_body": {
    kz: "Тапсырыс #{orderId} қабылданды. Растау үшін 10 минут ішінде хабарласамыз.",
    ru: "Заказ #{orderId} принят. Перезвоним подтвердить в течение 10 минут.",
    en: "Order #{orderId} received. We will call back to confirm within 10 minutes.",
  },
  "guest.delivery_success_close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "guest.delivery_success_track": {
    kz: "Тапсырысты бақылау",
    ru: "Отслеживать заказ",
    en: "Track order",
  },
  "guest.track_title": {
    kz: "Тапсырысты бақылау",
    ru: "Отслеживание заказа",
    en: "Order tracking",
  },
  "guest.track_loading": {
    kz: "Жүктелуде…",
    ru: "Загрузка…",
    en: "Loading…",
  },
  "guest.track_not_found_title": {
    kz: "Тапсырыс табылмады",
    ru: "Заказ не найден",
    en: "Order not found",
  },
  "guest.track_not_found_body": {
    kz: "Сілтеме қате немесе ескірген болуы мүмкін. Қайта тексеріңіз.",
    ru: "Ссылка некорректна или устарела. Проверьте адрес.",
    en: "The link is incorrect or expired. Please check the URL.",
  },
  "guest.track_back": {
    kz: "Мәзірге қайту",
    ru: "Вернуться в меню",
    en: "Back to menu",
  },
  "guest.track_order_label": {
    kz: "Тапсырыс №{id}",
    ru: "Заказ №{id}",
    en: "Order №{id}",
  },
  "guest.track_address_label": {
    kz: "Жеткізу мекенжайы",
    ru: "Адрес доставки",
    en: "Delivery address",
  },
  "guest.track_items_label": {
    kz: "Тапсырыс құрамы",
    ru: "Состав заказа",
    en: "Order items",
  },
  "guest.track_total_label": {
    kz: "Жалпы сома",
    ru: "Итого",
    en: "Total",
  },
  "guest.track_delivery_fee": {
    kz: "Жеткізу",
    ru: "Доставка",
    en: "Delivery fee",
  },
  "guest.track_payment_cod": {
    kz: "Қолма-қол есеп",
    ru: "Оплата наличными",
    en: "Cash on delivery",
  },
  "guest.track_payment_prepaid": {
    kz: "Алдын ала төленді",
    ru: "Предоплата",
    en: "Prepaid",
  },
  "guest.track_payment_paid": {
    kz: "Төленді",
    ru: "Оплачено",
    en: "Paid",
  },
  "guest.track_payment_unpaid": {
    kz: "Төленбеген",
    ru: "Не оплачено",
    en: "Unpaid",
  },
  "guest.track_status_created": {
    kz: "Қабылданды",
    ru: "Принят",
    en: "Received",
  },
  "guest.track_status_kitchen": {
    kz: "Дайындалуда",
    ru: "Готовится",
    en: "Cooking",
  },
  "guest.track_status_ready": {
    kz: "Курьерді күтеді",
    ru: "Ждёт курьера",
    en: "Waiting for courier",
  },
  "guest.track_status_assigned": {
    kz: "Курьерге берілді",
    ru: "Передан курьеру",
    en: "Courier assigned",
  },
  "guest.track_status_picked_up": {
    kz: "Жолда",
    ru: "В пути",
    en: "On the way",
  },
  "guest.track_status_delivered": {
    kz: "Жеткізілді",
    ru: "Доставлен",
    en: "Delivered",
  },
  "guest.track_status_ready_pickup": {
    kz: "Беруге дайын",
    ru: "Готов к выдаче",
    en: "Ready for pickup",
    zh: "可取餐",
    ky: "Берүүгө даяр",
    uz: "Olib ketishga tayyor",
  },
  "guest.track_status_delivered_pickup": {
    kz: "Берілді",
    ru: "Выдан",
    en: "Picked up",
    zh: "已取餐",
    ky: "Берилди",
    uz: "Berildi",
  },
  "guest.track_badge_pickup": {
    kz: "Өзі алып кету",
    ru: "Самовывоз",
    en: "Pickup",
    zh: "自取",
    ky: "Өзү алып кетүү",
    uz: "Olib ketish",
  },
  "guest.track_badge_delivery": {
    kz: "Жеткізу",
    ru: "Доставка",
    en: "Delivery",
    zh: "配送",
    ky: "Жеткирүү",
    uz: "Yetkazib berish",
  },
  "guest.track_status_cancelled": {
    kz: "Жойылды",
    ru: "Отменён",
    en: "Cancelled",
  },
  "guest.track_eta": {
    kz: "~{n} мин қалды",
    ru: "~{n} мин осталось",
    en: "~{n} min left",
  },
  "guest.track_chat_courier": {
    kz: "Курьерге жазу",
    ru: "Написать курьеру",
    en: "Chat with courier",
  },
  "guest.track_games_cta": {
    kz: "Курьерді күтіп ойнау 🎮",
    ru: "Поиграть пока ждёшь 🎮",
    en: "Play while you wait 🎮",
  },
  "guest.track_subtotal_label": {
    kz: "Тағамдар",
    ru: "Блюда",
    en: "Dishes",
  },
  "guest.track_courier_map_title": {
    kz: "Курьер картада",
    ru: "Курьер на карте",
    en: "Courier on map",
  },
  "guest.track_courier_updated_now": {
    kz: "жаңа ғана жаңартылды",
    ru: "обновлено только что",
    en: "updated just now",
  },
  "guest.track_courier_updated_min": {
    kz: "{n} мин бұрын жаңартылды",
    ru: "обновлено {n} мин назад",
    en: "updated {n} min ago",
  },
  "guest.track_courier_no_gps": {
    kz: "Курьер әлі орналасуын бөліскен жоқ",
    ru: "Курьер ещё не поделился местоположением",
    en: "Courier hasn't shared location yet",
  },
  "guest.track_review_cta": {
    kz: "Пікір қалдыру",
    ru: "Оставить отзыв",
    en: "Leave a review",
  },
  "guest.review_title": {
    kz: "Тапсырысты бағалаңыз",
    ru: "Оцените заказ",
    en: "Rate your order",
  },
  "guest.review_rating_label": {
    kz: "Бағаңыз",
    ru: "Ваша оценка",
    en: "Your rating",
  },
  "guest.review_comment_label": {
    kz: "Пікір (міндетті емес)",
    ru: "Комментарий (необязательно)",
    en: "Comment (optional)",
  },
  "guest.review_comment_placeholder": {
    kz: "Сізге не ұнады, не ұнамады…",
    ru: "Что понравилось, что нет…",
    en: "What did you like or not like…",
  },
  "guest.review_submit": {
    kz: "Жіберу",
    ru: "Отправить",
    en: "Submit",
  },
  "guest.review_thanks": {
    kz: "Рақмет, пікіріңіз қабылданды!",
    ru: "Спасибо, отзыв принят!",
    en: "Thanks, review submitted!",
  },
  "guest.review_error": {
    kz: "Пікірді сақтау мүмкін болмады",
    ru: "Не удалось сохранить отзыв",
    en: "Could not save the review",
  },
  "guest.review_already": {
    kz: "Бұл тапсырыс үшін пікір қалдырылған",
    ru: "Отзыв на этот заказ уже оставлен",
    en: "A review for this order has already been left",
  },
  "guest.chat_title": {
    kz: "Курьермен чат",
    ru: "Чат с курьером",
    en: "Chat with courier",
  },
  "guest.chat_placeholder": {
    kz: "Хабарлама…",
    ru: "Сообщение…",
    en: "Message…",
  },
  "guest.chat_send": {
    kz: "Жіберу",
    ru: "Отправить",
    en: "Send",
  },
  "guest.chat_record": {
    kz: "Дауыстық жазу",
    ru: "Записать голосовое",
    en: "Record voice",
    zh: "录制语音",
    ky: "Үн жаздыруу",
    uz: "Ovozli yozish",
  },
  "guest.chat_recording": {
    kz: "Жазылып жатыр",
    ru: "Запись",
    en: "Recording",
    zh: "录音中",
    ky: "Жазылууда",
    uz: "Yozilmoqda",
  },
  "guest.chat_audio_cancel": {
    kz: "Болдырмау",
    ru: "Отменить",
    en: "Cancel",
    zh: "取消",
    ky: "Жокко чыгаруу",
    uz: "Bekor qilish",
  },
  "guest.chat_audio_unsupported": {
    kz: "Бұл браузер дауыстық жазуды қолдамайды",
    ru: "Этот браузер не поддерживает запись голоса",
    en: "This browser doesn't support voice recording",
    zh: "此浏览器不支持语音录制",
    ky: "Бул браузер үн жаздырууну колдоого албайт",
    uz: "Bu brauzer ovoz yozishni qo'llab-quvvatlamaydi",
  },
  "guest.chat_audio_denied": {
    kz: "Микрофонға рұқсат жоқ",
    ru: "Нет доступа к микрофону",
    en: "Microphone access denied",
    zh: "麦克风访问被拒绝",
    ky: "Микрофонго уруксат жок",
    uz: "Mikrofonga ruxsat yo'q",
  },
  "guest.chat_empty": {
    kz: "Әлі хабарлама жоқ. Бірінші болып жазыңыз.",
    ru: "Сообщений пока нет. Напишите первым.",
    en: "No messages yet. Be the first to write.",
  },
  "guest.chat_courier_unavailable": {
    kz: "Курьер тағайындалғанда чат қолжетімді болады",
    ru: "Чат станет доступен после назначения курьера",
    en: "Chat becomes available when a courier is assigned",
  },
  "guest.dev_link": { kz: "Dev", ru: "Dev", en: "Dev" },
  "guest.all_categories": {
    kz: "Барлығы",
    ru: "Все",
    en: "All",
  },
  "guest.no_supabase": {
    kz: "Supabase конфигурациясы жоқ (.env.local).",
    ru: "Нет конфигурации Supabase (.env.local).",
    en: "Missing Supabase configuration (.env.local).",
  },
  "guest.hint_bootstrap": {
    kz: "Кеңес: cafe-os ішінде npm run bootstrap:env",
    ru: "Подсказка: npm run bootstrap:env в папке cafe-os",
    en: "Hint: npm run bootstrap:env in cafe-os folder",
  },
  "guest.back_dev": {
    kz: "Служебтік бетке",
    ru: "На служебную страницу",
    en: "Developer page",
  },
  "guest.back": {
    kz: "Артқа",
    ru: "Назад",
    en: "Back",
  },
  "guest.table_path_hint": {
    kz: "Күтілетін жол:",
    ru: "Ожидается документ",
    en: "Expected document",
  },
  "guest.hero_greeting": {
    kz: "Қош келдіңіз, {name}",
    ru: "Добро пожаловать, {name}",
    en: "Welcome, {name}",
  },
  "guest.venue_default_name": {
    kz: "Әлем",
    ru: "Әлем",
    en: "Alem",
  },
  "guest.categories_label": {
    kz: "САНАТТАР",
    ru: "КАТЕГОРИИ",
    en: "CATEGORIES",
  },
  "guest.search_placeholder": {
    kz: "Тағамдарды іздеу…",
    ru: "Поиск блюд…",
    en: "Search dishes…",
  },
  "guest.all_dishes_section": {
    kz: "Барлық тағамдар",
    ru: "Все блюда",
    en: "All dishes",
  },
  "guest.badge_new": {
    kz: "NEW",
    ru: "NEW",
    en: "NEW",
  },
  "guest.badge_special": {
    kz: "SPECIAL",
    ru: "SPECIAL",
    en: "SPECIAL",
  },
  "guest.calories_line": {
    kz: "{n} ккал",
    ru: "{n} ккал",
    en: "{n} kcal",
  },

  "guest.sort_label": { kz: "Сұрыптау", ru: "Сортировка", en: "Sort" },
  "guest.sort_default": { kz: "Әдепкі", ru: "По умолчанию", en: "Default" },
  "guest.sort_price_asc": { kz: "Арзанынан", ru: "Дешевле → дороже", en: "Price ↑" },
  "guest.sort_price_desc": { kz: "Қымбатынан", ru: "Дороже → дешевле", en: "Price ↓" },

  "guest.filter_all": { kz: "Барлығы", ru: "Все", en: "All" },
  "guest.filter_special": { kz: "Хит", ru: "Хиты", en: "Specials" },
  "guest.filter_fav": { kz: "Таңдаулылар", ru: "Избранное", en: "Favorites" },

  "guest.popular_section": { kz: "Кафе хиттері", ru: "Хиты кафе", en: "Café hits" },
  "guest.favorites_section": { kz: "Таңдаулыларыңыз", ru: "Ваше избранное", en: "Your favorites" },
  "guest.no_results": {
    kz: "Бұл сұрау бойынша ештеңе табылмады",
    ru: "По этому запросу ничего не найдено",
    en: "Nothing found for this query",
  },

  "guest.hero_tag_hours": { kz: "09:00 – 00:00", ru: "09:00 – 00:00", en: "09:00 – 00:00" },
  "guest.hero_tag_service": {
    kz: "Қызмет ақысы 15%",
    ru: "Сервис 15% в счёт",
    en: "15% service fee",
  },
  "guest.hero_tag_service_pct": {
    kz: "Қызмет ақысы {pct}%",
    ru: "Сервис {pct}% в счёт",
    en: "{pct}% service fee",
  },
  // guest.hero_tag_wifi удалён 2026-08-21: хардкод Wi-Fi одного кафе (ALEM ·
  // alem2025) лежал в бандле каждого тенанта; Wi-Fi берётся из settings.

  "guest.ticker_happy": {
    kz: "Happy hours 16:00–18:00 · сусындарға −20%",
    ru: "Happy hours 16:00–18:00 · скидка −20% на напитки",
    en: "Happy hours 16:00–18:00 · −20% on drinks",
  },
  // 2026-08-21: был хардкод новинок одного кафе («салат „Әлем“ и тянутый
  // лагман») — крутился в тикере у ВСЕХ тенантов. Теперь нейтрально.
  "guest.ticker_new": {
    kz: "Мәзірде жаңа тағамдар — қараңыз",
    ru: "В меню есть новинки — загляните",
    en: "New dishes in the menu — take a look",
  },
  "guest.ticker_loyalty": {
    kz: "Баллдар: 1₸ = 1 балл · келесі тапсырыста пайдаланыңыз",
    ru: "Баллы: 1₸ = 1 балл, копятся автоматически",
    en: "Loyalty points: 1 KZT = 1 point, auto accrual",
  },

  "guest.fav_add": { kz: "Таңдаулыларға қосу", ru: "В избранное", en: "Add to favorites" },
  "guest.fav_remove": { kz: "Таңдаулылардан шығару", ru: "Убрать из избранного", en: "Remove from favorites" },

  "guest.nav_home": { kz: "Басты", ru: "Главная", en: "Home" },
  "guest.nav_catalog": { kz: "Каталог", ru: "Каталог", en: "Catalog" },
  "guest.nav_orders": { kz: "Тапсырыстар", ru: "Заказы", en: "Orders" },
  "guest.nav_menu": { kz: "Мәзір", ru: "Меню", en: "Menu" },
  "guest.nav_cart": { kz: "Себет", ru: "Корзина", en: "Cart" },
  "guest.nav_profile": { kz: "Профиль", ru: "Кабинет", en: "Profile" },
  // Объединённый таб «Заказ» (корзина + отправленные заказы) + таб «Категории».
  "guest.nav_order": { kz: "Тапсырыс", ru: "Заказ", en: "Order" },
  "guest.nav_categories": { kz: "Санаттар", ru: "Категории", en: "Categories" },
  "guest.order_tab_cart_section": { kz: "Себет", ru: "Корзина", en: "Cart" },
  "guest.order_tab_orders_section": {
    kz: "Жіберілген тапсырыстар",
    ru: "Отправленные заказы",
    en: "Sent orders",
  },
  "guest.order_tab_cart_empty_hint": {
    kz: "Себет бос — мәзірден тағам қосыңыз",
    ru: "Корзина пуста — добавьте блюда из меню",
    en: "Cart is empty — add dishes from the menu",
  },
  "guest.categories_title": { kz: "Санаттар", ru: "Категории", en: "Categories" },
  "guest.categories_dishes_n": {
    kz: "{n} тағам",
    ru: "{n} блюд",
    en: "{n} dishes",
  },

  "guest.orders_title": {
    kz: "Тапсырыстар",
    ru: "Заказы",
    en: "Orders",
  },
  "guest.orders_empty": {
    kz: "Әзірге тапсырыс жоқ. Себетке тағам қосып, тапсырысты растаңыз.",
    ru: "Заказов пока нет. Соберите корзину и подтвердите заказ.",
    en: "No orders yet. Add dishes to the cart and confirm the order.",
  },
  "guest.orders_go_to_cart": {
    kz: "Себетке өту",
    ru: "Перейти в корзину",
    en: "Go to cart",
  },
  "guest.your_waiter": {
    kz: "Сіздің даяшыңыз",
    ru: "Ваш официант",
    en: "Your waiter",
  },
  "guest.orders_repeat": {
    kz: "Қайталау",
    ru: "Повторить",
    en: "Repeat",
  },
  "guest.toast_repeat_done": {
    kz: "Себетке қосылды: {n} тағам",
    ru: "В корзину добавлено: {n} поз.",
    en: "Added to cart: {n} items",
  },
  "guest.toast_repeat_partial": {
    kz: "Қосылды: {n}, өткізілді: {m} (мәзірде жоқ)",
    ru: "Добавлено: {n}, пропущено: {m} (нет в меню)",
    en: "Added: {n}, skipped: {m} (not on the menu)",
  },
  "guest.toast_repeat_failed": {
    kz: "Қайталау сәтсіз аяқталды",
    ru: "Не удалось повторить заказ",
    en: "Failed to repeat the order",
  },
  "guest.cart_transfer_skipped": {
    kz: "{n} позиция үстелде қолжетімсіз — өткізілді",
    ru: "{n} поз. недоступно за столом — пропущено",
    en: "{n} item(s) unavailable at the table — skipped",
    zh: "{n} 项不适用于堂食——已跳过",
    ky: "{n} позиция үстөлдө жеткиликсиз — өткөрүлдү",
    uz: "{n} ta pozitsiya stolda mavjud emas — o‘tkazib yuborildi",
  },
  "guest.cart_transfer_skipped_all": {
    kz: "Жеткізу себетіндегі позициялар үстелде қолжетімсіз",
    ru: "Позиции из корзины доставки недоступны за столом",
    en: "Delivery cart items aren't available at the table",
    zh: "配送购物车中的商品不适用于堂食",
    ky: "Жеткирүү себетиндеги позициялар үстөлдө жеткиликсиз",
    uz: "Yetkazib berish savatidagi pozitsiyalar stolda mavjud emas",
  },
  "guest.orders_pending_title": {
    kz: "Стол растауын күтудеміз",
    ru: "Ожидаем подтверждения",
    en: "Waiting for confirmation",
  },
  "guest.orders_pending_count": {
    kz: "{n}/{total} қонақ растады",
    ru: "{n} из {total} подтвердили",
    en: "{n} of {total} confirmed",
  },
  "guest.orders_active_title": {
    kz: "Сіздің тапсырысыңыз",
    ru: "Ваш заказ",
    en: "Your order",
  },
  // Блюда, добавленные кассой/официантом (order_lines с bill_to_uid=NULL) —
  // без этой группы они не попадали НИ в «Ваш заказ», ни в «Общий», ни к
  // друзьям и были невидимы гостю (баг 2026-07-24, «Тапчан 1»).
  "guest.orders_staff_added": {
    kz: "Қызметкер қосқан",
    ru: "Добавлено персоналом",
    en: "Added by staff",
    zh: "员工添加",
    ky: "Кызматкер кошкон",
    uz: "Xodim qo‘shgan",
  },
  // С именем сотрудника (00432 get_session_order_staff): «кассир тоже участник».
  "guest.orders_staff_added_by": {
    kz: "Қосқан: {name}",
    ru: "Добавил(а): {name}",
    en: "Added by {name}",
    zh: "{name} 添加",
    ky: "Кошкон: {name}",
    uz: "Qo‘shgan: {name}",
  },
  "guest.orders_pay_now": {
    kz: "Төлеу",
    ru: "Оплатить",
    en: "Pay now",
  },
  "guest.orders_paid": {
    kz: "Төленді",
    ru: "Оплачено",
    en: "Paid",
  },
  "guest.orders_view_receipt": {
    kz: "Чекті қарау",
    ru: "Посмотреть чек",
    en: "View receipt",
  },
  "guest.orders_waiting_kitchen": {
    kz: "Тапсырысыңыз ас үйге жіберіледі…",
    ru: "Заказ отправляется на кухню…",
    en: "Sending order to kitchen…",
  },

  "guest.eta_remaining": {
    kz: "≈ {n} мин",
    ru: "≈ {n} мин",
    en: "≈ {n} min",
  },
  "guest.eta_almost_ready": {
    kz: "Жақын арада дайын",
    ru: "Скоро будет готово",
    en: "Almost ready",
  },

  "guest.dish_rating_label": {
    kz: "Бағаңыз",
    ru: "Ваша оценка",
    en: "Your rating",
  },
  "guest.dish_rating_saved": {
    kz: "Сақталды",
    ru: "Сохранено",
    en: "Saved",
  },

  "guest.recent_orders_title": {
    kz: "Соңғы тапсырыстар",
    ru: "Последние заказы",
    en: "Recent orders",
  },
  "guest.recent_orders_empty": {
    kz: "Тапсырыс әлі жоқ.",
    ru: "Заказов ещё не было.",
    en: "No past orders yet.",
  },
  "guest.recent_orders_repeat": {
    kz: "Қайта тапсырыс беру",
    ru: "Повторить заказ",
    en: "Repeat order",
  },
  "guest.recent_orders_repeated": {
    kz: "Себетке қосылды",
    ru: "Добавлено в корзину",
    en: "Added to cart",
  },

  "guest.leave_table_button": {
    kz: "Мен кетіп бара жатырмын",
    ru: "Я ухожу",
    en: "I'm leaving",
  },
  "guest.leave_table_hint": {
    kz: "Қалған тапсырыстарыңыз ортақ шотта қалады.",
    ru: "Ваши заказы остаются в общем счёте, оплата при общем расчёте.",
    en: "Your orders stay on the shared bill — paid at table checkout.",
  },
  "guest.leave_table_confirm": {
    kz: "Үстелден кетесіз бе?",
    ru: "Покинуть стол?",
    en: "Leave the table?",
  },
  "guest.leave_table_yes": {
    kz: "Иә, кетемін",
    ru: "Да, ухожу",
    en: "Yes, leave",
  },
  "guest.leave_table_no": {
    kz: "Бас тарту",
    ru: "Отмена",
    en: "Cancel",
  },
  "guest.left_table_banner": {
    kz: "Сіз үстелден кеттіңіз. Сау болыңыз!",
    ru: "Вы покинули стол. Спасибо!",
    en: "You've left the table. Thank you!",
  },
  "guest.guest_left_chip": {
    kz: "{name} кетті",
    ru: "{name} ушёл",
    en: "{name} left",
  },

  "guest.upsell_add": { kz: "Қосу", ru: "Добавить", en: "Add" },
  "guest.upsell_dismiss": { kz: "Жабу", ru: "Скрыть", en: "Dismiss" },

  "guest.grid_cols_label": {
    kz: "Қатардағы тағам саны",
    ru: "Блюд в ряду",
    en: "Items per row",
  },
  "guest.grid_cols_n": {
    kz: "{n} қатарда",
    ru: "{n} в ряду",
    en: "{n} per row",
  },

  "guest.home_hint": {
    kz: "Тағамдар төменде. Санат үшін «Каталог», сомалар үшін «Себет».",
    ru: "Блюда ниже на главной. «Каталог» — по категориям, «Корзина» — кто что заказал и суммы.",
    en: "Dishes below on Home. Catalog for categories; Cart for who ordered what and totals.",
  },
  "guest.catalog_title": {
    kz: "Санаттар",
    ru: "Категории",
    en: "Categories",
  },
  "guest.catalog_sub": {
    kz: "Санатты басыңыз — басты бетте сол санат ашылады",
    ru: "Нажмите категорию — на главной откроется эта категория",
    en: "Tap a category — Home scrolls to that category",
  },
  "guest.catalog_go_home": { kz: "Бастыға", ru: "На главную", en: "To Home" },

  "guest.table_mates_title": {
    kz: "Столда кім бар",
    ru: "Кто за столом",
    en: "At the table",
  },
  "guest.table_mates_sub": {
    kz: "Және кім не тапсырғаны (ортақ себет)",
    ru: "И кто что заказал (общая корзина)",
    en: "And who ordered what (shared cart)",
  },
  "guest.table_mates_empty": {
    kz: "Әзірге қонақтар тізімі бос",
    ru: "Пока нет списка гостей",
    en: "No guests listed yet",
  },
  "guest.table_mates_you": { kz: "Сіз", ru: "Вы", en: "You" },
  "guest.table_mates_guest": { kz: "Қонақ", ru: "Гость", en: "Guest" },
  "guest.table_orders_empty": {
    kz: "Себет бос",
    ru: "Корзина пуста",
    en: "Cart is empty",
  },
  "guest.table_line_qty": { kz: "{qty}× {label}", ru: "{qty}× {label}", en: "{qty}× {label}" },

  "guest.profile_title": { kz: "Жеке кабинет", ru: "Личный кабинет", en: "Your profile" },
  "guest.profile_sub": {
    kz: "Атыңызды өзгертіңіз (стол сессиясына сақталады)",
    ru: "Измените имя (сохранится в сессии стола)",
    en: "Change your name (saved to this table session)",
  },
  "guest.profile_save": { kz: "Сақтау", ru: "Сохранить", en: "Save" },
  "guest.profile_saved": { kz: "Сақталды", ru: "Сохранено", en: "Saved" },
  "guest.profile_lang": { kz: "Тіл", ru: "Язык", en: "Language" },

  "guest.profile_history_title": {
    kz: "Келулер тарихы",
    ru: "История визитов",
    en: "Visit history",
  },
  "guest.profile_history_visits": {
    kz: "Келулер",
    ru: "Визитов",
    en: "Visits",
  },
  "guest.profile_history_orders": {
    kz: "Тапсырыстар",
    ru: "Заказов",
    en: "Orders",
  },
  "guest.profile_history_total_spent": {
    kz: "Барлығы",
    ru: "Потрачено",
    en: "Total spent",
  },
  "guest.profile_history_avg_check": {
    kz: "Орт. чек",
    ru: "Средний чек",
    en: "Avg. check",
  },
  "guest.profile_history_first_seen": {
    kz: "Бірінші келу",
    ru: "Первый визит",
    en: "First visit",
  },
  "guest.profile_history_last_seen": {
    kz: "Соңғы келу",
    ru: "Последний визит",
    en: "Last visit",
  },
  "guest.profile_history_empty": {
    kz: "Тарих әлі жинақталмаған. Алғашқы тапсырысыңыздан кейін бұл жерде статистика пайда болады.",
    ru: "История ещё не накоплена. После первого заказа здесь появится статистика.",
    en: "No history yet. Stats will appear here after your first order.",
  },

  "guest.profile_favorites_title": {
    kz: "Таңдаулы тағамдар",
    ru: "Избранное",
    en: "Favorites",
  },
  "guest.profile_favorites_empty": {
    kz: "Таңдаулыларыңыз жоқ. Мәзірде ❤ басып, тағамдарды қосыңыз.",
    ru: "Пока пусто. Нажимайте ❤ на блюдах в меню, чтобы добавить.",
    en: "Nothing yet. Tap ❤ on dishes in the menu to add favorites.",
  },

  "guest.profile_badges_title": {
    kz: "Жетістіктер",
    ru: "Достижения",
    en: "Achievements",
  },
  "guest.profile_badges_progress": {
    kz: "{n}/{total}",
    ru: "{n}/{total}",
    en: "{n}/{total}",
  },
  "guest.badge_newcomer": { kz: "Жаңадан келген", ru: "Новичок", en: "Newcomer" },
  "guest.badge_newcomer_desc": {
    kz: "Алғашқы келуіңіз",
    ru: "Первый визит",
    en: "First visit",
  },
  "guest.badge_regular": { kz: "Тұрақты қонақ", ru: "Постоянный", en: "Regular" },
  "guest.badge_regular_desc": {
    kz: "5 келу",
    ru: "5 визитов",
    en: "5 visits",
  },
  "guest.badge_vip": { kz: "VIP", ru: "VIP", en: "VIP" },
  "guest.badge_vip_desc": {
    kz: "10 келу",
    ru: "10 визитов",
    en: "10 visits",
  },
  "guest.badge_devoted": { kz: "Адал қонақ", ru: "Преданный", en: "Devoted" },
  "guest.badge_devoted_desc": {
    kz: "30 келу",
    ru: "30 визитов",
    en: "30 visits",
  },
  "guest.badge_foodie": { kz: "Гурман", ru: "Гурман", en: "Foodie" },
  "guest.badge_foodie_desc": {
    kz: "10 тапсырыс",
    ru: "10 заказов",
    en: "10 orders",
  },
  "guest.badge_big_spender": { kz: "Үлкен шеф", ru: "Большой шеф", en: "Big spender" },
  "guest.badge_big_spender_desc": {
    kz: "100 000 ₸ артық жұмсаған",
    ru: "Потрачено больше 100 000 ₸",
    en: "Over 100 000 ₸ spent",
  },
  "guest.badge_connoisseur": { kz: "Білгір", ru: "Знаток", en: "Connoisseur" },
  "guest.badge_connoisseur_desc": {
    kz: "5+ таңдаулы тағам",
    ru: "5+ избранных блюд",
    en: "5+ favorite dishes",
  },

  "guest.profile_contacts_title": {
    kz: "Байланыс деректері",
    ru: "Контакты",
    en: "Contacts",
  },
  "guest.profile_contacts_hint": {
    kz: "Электронды чек және SMS-хабарламалар үшін.",
    ru: "Для электронного чека и SMS-уведомлений.",
    en: "For e-receipts and SMS notifications.",
  },
  "guest.profile_contacts_phone": { kz: "Телефон", ru: "Телефон", en: "Phone" },
  "guest.profile_contacts_email": { kz: "Email", ru: "Email", en: "Email" },
  "guest.profile_contacts_phone_invalid": {
    kz: "Дұрыс емес телефон. Үлгі: +7XXXXXXXXXX",
    ru: "Некорректный телефон. Формат: +7XXXXXXXXXX",
    en: "Invalid phone. Format: +7XXXXXXXXXX",
  },
  "guest.profile_contacts_email_invalid": {
    kz: "Дұрыс емес email",
    ru: "Некорректный email",
    en: "Invalid email",
  },

  "guest.profile_review_title": {
    kz: "Кафе туралы пікіріңіз",
    ru: "Ваш отзыв о кафе",
    en: "Your review",
  },
  "guest.profile_review_stars_label": {
    kz: "Бағаңыз",
    ru: "Ваша оценка",
    en: "Your rating",
  },
  "guest.profile_review_text_placeholder": {
    kz: "Не ұнады, не жақсартуға болады?",
    ru: "Что понравилось, что можно улучшить?",
    en: "What did you like, what could be better?",
  },
  "guest.profile_review_submit": {
    kz: "Пікір жіберу",
    ru: "Отправить отзыв",
    en: "Submit review",
  },
  "guest.profile_review_submitted": {
    kz: "Рахмет! Пікіріңіз жіберілді.",
    ru: "Спасибо! Отзыв отправлен.",
    en: "Thanks! Review submitted.",
  },
  "guest.profile_review_failed": {
    kz: "Жіберу мүмкін болмады",
    ru: "Не удалось отправить",
    en: "Could not submit",
  },

  "guest.profile_privacy_title": {
    kz: "Құпиялылық және құрылғы",
    ru: "Приватность и устройство",
    en: "Privacy & device",
  },
  "guest.profile_privacy_device": {
    kz: "Құрылғы",
    ru: "Устройство",
    en: "Device",
  },
  "guest.profile_privacy_fingerprint": {
    kz: "Идентификатор",
    ru: "Идентификатор",
    en: "Identifier",
  },
  "guest.profile_privacy_fingerprint_note": {
    kz: "Cookie тазаланғаннан кейін сізді тану үшін қолданылады.",
    ru: "Используется, чтобы узнать вас после очистки cookies.",
    en: "Used to recognise you after clearing cookies.",
  },
  "guest.profile_privacy_copy": { kz: "Көшіру", ru: "Копировать", en: "Copy" },
  "guest.profile_privacy_copied": { kz: "Көшірілді", ru: "Скопировано", en: "Copied" },
  "guest.profile_privacy_clear": {
    kz: "Менің тарихымды өшіру",
    ru: "Очистить мою историю",
    en: "Clear my history",
  },
  "guest.profile_privacy_clear_confirm": {
    kz: "Барлық тарих, есім, таңдаулылар жойылады. Жалғастырамыз ба?",
    ru: "История, имя, избранное и др. будут удалены. Продолжить?",
    en: "Your history, name, favorites and other data will be deleted. Continue?",
  },
  "guest.profile_privacy_clear_yes": {
    kz: "Иә, өшіру",
    ru: "Да, очистить",
    en: "Yes, clear",
  },
  "guest.profile_privacy_clear_no": {
    kz: "Бас тарту",
    ru: "Отмена",
    en: "Cancel",
  },

  "guest.legal_privacy_link": {
    kz: "Құпиялылық саясаты",
    ru: "Политика конфиденциальности",
    en: "Privacy Policy",
  },
  "guest.legal_back": {
    kz: "Мәзірге қайту",
    ru: "Вернуться в меню",
    en: "Back to menu",
  },
  "guest.consent_delivery": {
    kz: "Тапсырысты ресімдей отырып, мен дербес деректерімді өңдеуге келісемін —",
    ru: "Оформляя заказ, я соглашаюсь на обработку моих персональных данных —",
    en: "By placing the order, I consent to the processing of my personal data —",
  },
  "guest.consent_delivery_note": {
    kz: "Тапсырысты ресімдей отырып, сіз дербес деректерді өңдеуге келісім бересіз —",
    ru: "Оформляя заказ, вы соглашаетесь на обработку персональных данных —",
    en: "By placing the order, you agree to the processing of personal data —",
  },
  "guest.cookie_text": {
    kz: "Біз сервистің жұмысы және сізді тану үшін cookie қолданамыз. Жарнамалық трекерлер жоқ.",
    ru: "Мы используем cookie для работы сервиса и узнавания вас. Рекламных трекеров нет.",
    en: "We use cookies to run the service and recognise you. No advertising trackers.",
  },
  "guest.cookie_accept": {
    kz: "Қабылдау",
    ru: "Принять",
    en: "Accept",
  },

  "guest.name_invalid_empty": {
    kz: "Атыңызды енгізіңіз",
    ru: "Введите имя",
    en: "Please enter your name",
  },
  "guest.name_invalid_too_short": {
    kz: "Есім тым қысқа",
    ru: "Имя слишком короткое",
    en: "Name is too short",
  },
  "guest.name_invalid_too_long": {
    kz: "Есім тым ұзын",
    ru: "Имя слишком длинное",
    en: "Name is too long",
  },
  "guest.name_invalid_profanity": {
    kz: "Шынайы есімді қолданыңыз",
    ru: "Пожалуйста, используйте настоящее имя",
    en: "Please use a real name",
  },

  "guest.demo_banner_table": {
    kz: "Демо: экранда толық үлгі мәзір. Firestore-та қазір {n} тағам. `npm run seed:cloud` немесе консольдан мәзірді толтырыңыз — нақты мәзір көрінеді.",
    ru: "Демо: на экране полное примерное меню. В Firestore сейчас {n} блюд(а). Выполните `npm run seed:cloud` или добавьте блюда в консоли — отобразится ваше меню.",
    en: "Demo: full sample menu on screen. Firestore has {n} dish(es) right now. Run `npm run seed:cloud` or add dishes in the console — your real menu will show.",
  },
  "guest.cart_tab_title": { kz: "Себет", ru: "Корзина", en: "Cart" },
  "guest.cart_tab_sub": {
    kz: "Кім не алғаны, бағасы, жалпы және тең бөлу",
    ru: "Кто что заказал, цены, общий счёт и доля на человека",
    en: "Who ordered what, prices, total and equal split per guest",
  },
  "guest.cart_tab_empty": {
    kz: "Себет бос. Басты беттен тағам қосыңыз.",
    ru: "Корзина пуста. Добавьте блюда на главной.",
    en: "Cart is empty. Add dishes from Home.",
  },
  "guest.cart_tab_empty_title": {
    kz: "Себет әзірге бос",
    ru: "Корзина пока пуста",
    en: "Your cart is empty",
  },
  "guest.cart_tab_empty_hint": {
    kz: "Мәзірден бірінші тағамыңызды таңдаңыз — өзіңізге немесе ортақ дастарханға.",
    ru: "Выберите первое блюдо из меню — себе или на общий стол.",
    en: "Pick your first dish from the menu — for yourself or for the shared table.",
  },
  "guest.cart_tab_empty_cta": {
    kz: "Мәзірге өту",
    ru: "Открыть меню",
    en: "Open menu",
  },
  "guest.orders_tab_empty_title": {
    kz: "Тапсырыстар әлі жоқ",
    ru: "Заказов пока нет",
    en: "No orders yet",
  },
  "guest.orders_tab_empty_hint": {
    kz: "Себетке тағам қосып, тапсырыс беріңіз — мұнда оның күйі көрсетіледі.",
    ru: "Добавьте блюда в корзину и оформите заказ — здесь отобразится его статус.",
    en: "Add dishes to your cart and place an order — its status will appear here.",
  },
  "guest.orders_tab_empty_cta": {
    kz: "Себетке өту",
    ru: "Перейти в корзину",
    en: "Open cart",
  },
  "guest.session_abandoned_toast": {
    kz: "Стол сессиясы жабылды. QR-ды қайта сканерлеңіз.",
    ru: "Сессия стола закрыта. Отсканируйте QR-код заново.",
    en: "Table session was closed. Please scan the QR code again.",
  },
  "guest.cart_error_generic": {
    kz: "Себетті жаңарту мүмкін болмады",
    ru: "Не удалось обновить корзину",
    en: "Couldn’t update cart",
  },
  "guest.order_error_generic": {
    kz: "Тапсырысты жіберу мүмкін болмады",
    ru: "Не удалось отправить заказ",
    en: "Couldn’t place the order",
  },
  "guest.toast_order_placed": {
    kz: "✅ Тапсырыс ас үйге жіберілді",
    ru: "✅ Заказ отправлен на кухню",
    en: "✅ Order sent to the kitchen",
  },
  "guest.toast_added_to_cart": {
    kz: "Себетке қосылды",
    ru: "Добавлено в корзину",
    en: "Added to cart",
  },
  "guest.toast_waiting_auth": {
    kz: "Авторизацияны күтіңіз…",
    ru: "Подождите авторизацию…",
    en: "Please wait for authorization…",
  },
  "guest.toast_name_saved": {
    kz: "Аты сақталды",
    ru: "Имя сохранено",
    en: "Name saved",
  },
  "guest.toast_profile_saved": {
    kz: "Жеке деректер сақталды",
    ru: "Личные данные сохранены",
    en: "Personal details saved",
  },
  "guest.toast_profile_saved_pending": {
    kz: "✅ Сақталды (жеке деректер · көп ұзамай қолжетімді)",
    ru: "✅ Сохранено (личные данные · скоро будут доступны)",
    en: "✅ Saved (personal details · coming soon)",
  },
  "guest.toast_profile_saved_local": {
    kz: "Жергілікті сақталды · backend келесі фазада",
    ru: "Сохранено локально · backend в следующей фазе",
    en: "Saved locally · backend in next phase",
  },
  "guest.toast_review_thanks": {
    kz: "⭐ Пікіріңіз үшін рахмет!",
    ru: "⭐ Спасибо за отзыв!",
    en: "⭐ Thanks for the review!",
  },
  "guest.toast_history_cleared": {
    kz: "Тарих тазартылды",
    ru: "История очищена",
    en: "History cleared",
  },
  "guest.toast_repeat_order_stub": {
    kz: "🔁 Тапсырысты қайталау — келесі фазада",
    ru: "🔁 Повтор заказа — в следующей фазе",
    en: "🔁 Repeat order — in the next phase",
  },
  "guest.toast_payment_via_cashier_stub": {
    kz: "💳 Кассамен төлеу — келесі фазада",
    ru: "💳 Оплата через кассу — в следующей фазе",
    en: "💳 Pay at cashier — in the next phase",
  },
  "guest.toast_bill_requested": {
    kz: "Даяшыға хабарланды — төлемді қабылдауға келеді",
    ru: "Официант уведомлён — подойдёт принять оплату",
    en: "The waiter has been notified — they'll come to take payment",
  },
  "guest.toast_receipt_after_pay": {
    kz: "Чек төлемнен кейін қолжетімді болады",
    ru: "Чек будет доступен после оплаты",
    en: "Receipt will be available after payment",
  },
  "guest.toast_open_dish_via_catalog": {
    kz: "Тағамды ашу үшін Каталогқа өтіңіз",
    ru: "Перейдите в Каталог чтобы открыть блюдо",
    en: "Open the Catalog to view this dish",
  },
  "guest.prompt_link_email": {
    kz: "Тарих пен бонустарды сақтау үшін email енгізіңіз:",
    ru: "Введите email чтобы сохранить историю и бонусы:",
    en: "Enter your email to save history and bonuses:",
  },
  "guest.toast_email_link_sent": {
    kz: "📧 Сілтеме {email} мекенжайына жіберілді",
    ru: "📧 Ссылка отправлена на {email}",
    en: "📧 Link sent to {email}",
  },
  "guest.toast_error_prefix": {
    kz: "Қате",
    ru: "Ошибка",
    en: "Error",
  },
  "guest.cart_by_person": { kz: "Әр қонақ бойынша", ru: "По гостям", en: "By guest" },
  "guest.cart_line_fmt": {
    kz: "{qty}× {label} — {sum} ({unit}/дан)",
    ru: "{qty}× {label} — {sum} ({unit}/ед.)",
    en: "{qty}× {label} — {sum} ({unit} ea.)",
  },
  "guest.cart_person_subtotal": { kz: "Жиынтығы", ru: "Итого за гостя", en: "Guest subtotal" },
  "guest.cart_grand_total": { kz: "Барлығы стол үшін", ru: "Общий счёт стола", en: "Table total" },
  "guest.cart_split_title": { kz: "Тең бөлу (болжам)", ru: "Поровну (оценка)", en: "Equal split (estimate)" },
  "guest.cart_split_each": {
    kz: "Әр қонақ шамамен {amount} ({n} адамға)",
    ru: "Каждый примерно {amount} (на {n} чел.)",
    en: "About {amount} each ({n} guests)",
  },
  "guest.cart_split_hint": {
    kz: "Тең бөлу — қонақтар санына ({n}). Нақты төлем кассада/официантпен келісіледі.",
    ru: "Доля = общая сумма ÷ число гостей за столом ({n}). Оплата уточняется у персонала.",
    en: "Split = total ÷ guests at table ({n}). Confirm payment with staff.",
  },
  "guest.cart_scope_title": {
    kz: "Кім үшін?",
    ru: "Для кого?",
    en: "Who is this for?",
  },
  // Подзаголовок радиогруппы «Для кого» (2026-07-28): гости пропускали выбор,
  // поэтому у блока теперь есть явная инструкция, а не только вопрос.
  "guest.cart_scope_subtitle": {
    kz: "Тағам кімнің шотына жазылатынын таңдаңыз",
    ru: "Выберите, на чей счёт записать блюдо",
    en: "Choose whose bill this dish goes on",
  },
  "guest.cart_scope_self": {
    kz: "Өзіме (жеке шот)",
    ru: "Мне (на мой счёт)",
    en: "For me (my bill)",
  },
  "guest.cart_scope_shared": {
    kz: "Ортақ стол (шай, нан, компот…)",
    ru: "На стол общее (чай, хлеб, компот…)",
    en: "Shared table (tea, bread, drinks…)",
  },
  "guest.cart_scope_other": {
    kz: "Басқа қонаққа (кешігіп келеді / телефоны сөнді)",
    ru: "Другому гостю (опаздывает / сел телефон)",
    en: "For another guest (late / phone off)",
  },
  // Короткие ярлыки выбранного адресата — дублируются в липкой панели над
  // кнопкой «В корзину», чтобы гость видел, на чей счёт уйдёт блюдо.
  "guest.cart_scope_short_self": { kz: "Өзіме", ru: "Мне", en: "For me" },
  "guest.cart_scope_short_shared": { kz: "Ортақ столға", ru: "На стол общее", en: "Shared table" },
  "guest.cart_scope_short_other": { kz: "Қонаққа", ru: "Гостю", en: "A guest" },
  "guest.cart_scope_summary": { kz: "Жазылады: {target}", ru: "Записываем: {target}", en: "Goes to: {target}" },
  "guest.cart_scope_change": { kz: "өзгерту", ru: "изменить", en: "change" },
  // Полоса-выбор «Для кого» вне карточки блюда (меню и корзина, 2026-07-28).
  "guest.scope_bar_label": { kz: "Тапсырыс кімге", ru: "Заказ идёт", en: "Order goes to" },
  "guest.cart_scope_pick_guest": { kz: "Қонақты таңдаңыз", ru: "Выберите гостя", en: "Pick a guest" },
  "guest.cart_scope_no_others": {
    kz: "Әзірге басқа қонақ жоқ",
    ru: "Пока нет других гостей",
    en: "No other guests yet",
  },
  "guest.cart_scope_add_guest": {
    kz: "+ Жаңа қонақ қосу",
    ru: "+ Добавить гостя",
    en: "+ Add a guest",
  },
  "guest.cart_scope_new_guest_name": {
    kz: "Қонақтың аты",
    ru: "Имя гостя",
    en: "Guest name",
  },
  "guest.cart_scope_save_guest": {
    kz: "Қосу",
    ru: "Добавить",
    en: "Add",
  },
  "guest.cart_scope_cancel_guest": {
    kz: "Болдырмау",
    ru: "Отмена",
    en: "Cancel",
  },
  "guest.cart_scope_add_guest_err": {
    kz: "Қонақты қоса алмадық",
    ru: "Не удалось добавить гостя",
    en: "Failed to add guest",
  },
  "guest.cart_scope_other_hint": {
    kz: "Дос кешігіп жатыр ма? Оған тапсырыс беріңіз. Келгенде телефонындағы QR-ды сіздің телефоныңыздан скандайды да, тағамдары мен есебі оған көшеді.",
    ru: "Друг опаздывает? Закажите за него. Когда придёт — отсканирует QR с вашего телефона и его блюда + чек перейдут к нему.",
    en: "Friend running late? Order for them. When they arrive, they scan a QR from your phone and their dishes + bill move to them.",
  },
  "guest.claim_button": {
    kz: "Қонақ келді — QR көрсету",
    ru: "Гость пришёл — показать QR",
    en: "Guest arrived — show QR",
  },
  "guest.claim_qr_title": {
    kz: "{name} есебін көшіру",
    ru: "Передать чек: {name}",
    en: "Transfer to: {name}",
  },
  "guest.claim_qr_hint": {
    kz: "{name} осы QR-ды өзінің телефонымен скандасын. Барлық тапсырыстары мен үлесі оған өтеді.",
    ru: "Пусть {name} отсканирует этот QR со своего телефона. Все его блюда и доля перейдут к нему.",
    en: "Have {name} scan this QR with their phone. All their dishes and share will transfer to them.",
  },
  "guest.claim_qr_copy": {
    kz: "Сілтемені көшіру",
    ru: "Скопировать ссылку",
    en: "Copy link",
  },
  "guest.claim_qr_copied": {
    kz: "Көшірілді ✓",
    ru: "Скопировано ✓",
    en: "Copied ✓",
  },
  "guest.claim_prompt_title": {
    kz: "{name} болып табыласыз ба?",
    ru: "Вы — {name}?",
    en: "Are you {name}?",
  },
  "guest.claim_prompt_hint": {
    kz: "{name}-ге тапсырыс берілген. Растасаңыз, барлық тағамдары мен есебі сізге көшеді.",
    ru: "Заказы на имя {name} перейдут к вам, и в чеке будут как ваши.",
    en: "Orders billed to {name} will move to you in the bill.",
  },
  "guest.claim_confirm": {
    kz: "Иә, мен {name}",
    ru: "Да, это я",
    en: "Yes, that's me",
  },
  "guest.claim_decline": {
    kz: "Жоқ",
    ru: "Нет",
    en: "No",
  },
  "guest.claim_done": {
    kz: "Тапсырыстар сізге көшті ✓",
    ru: "Заказы перешли к вам ✓",
    en: "Orders transferred ✓",
  },
  "guest.claim_err": {
    kz: "Көшіру сәтсіз аяқталды",
    ru: "Не удалось передать заказы",
    en: "Transfer failed",
  },
  "guest.sound_on": { kz: "Дыбыс қосулы", ru: "Звук включён", en: "Sound on" },
  "guest.sound_off": { kz: "Дыбыс өшірулі", ru: "Звук выключен", en: "Sound off" },
  "guest.notify_order_ready": {
    kz: "🛎️ Тапсырысыңыз дайын!",
    ru: "🛎️ Ваш заказ готов!",
    en: "🛎️ Your order is ready!",
  },
  "guest.cart_primary_currency_hint": {
    kz: "Барлық төлемдер {primary} валютасында қабылданады. Курс — Қазақстан Ұлттық Банкі.",
    ru: "Все оплаты принимаются в {primary}. Курс — Нацбанк Казахстана.",
    en: "All payments are accepted in {primary}. Rates from National Bank of Kazakhstan.",
  },
  "guest.checkers_title": { kz: "Шашка", ru: "Шашки", en: "Checkers" },
  "guest.checkers_subtitle": {
    kz: "Үстелдегі екі қонаққа арналған онлайн ойын",
    ru: "Онлайн-игра для двух гостей за столом",
    en: "Online game for two guests at the table",
  },
  "guest.checkers_play_btn": {
    kz: "Шашка ойнау",
    ru: "Играть в шашки",
    en: "Play checkers",
  },
  "guest.checkers_create": {
    kz: "Жаңа партия бастау (мен — ақ)",
    ru: "Начать партию (я — белые)",
    en: "Start a game (I'm white)",
  },
  "guest.checkers_join": {
    kz: "Қара түсте қосылу",
    ru: "Присоединиться чёрными",
    en: "Join as black",
  },
  "guest.checkers_waiting_opponent": {
    kz: "Қарсыласты күтіп тұр…",
    ru: "Ждём соперника…",
    en: "Waiting for opponent…",
  },
  "guest.checkers_your_turn": { kz: "Сіздің жүрісіңіз", ru: "Ваш ход", en: "Your turn" },
  "guest.checkers_opponent_turn": {
    kz: "Қарсылас ойлап тұр…",
    ru: "Ход соперника…",
    en: "Opponent's turn…",
  },
  "guest.checkers_must_continue": {
    kz: "Сериясын жалғастыруыңыз керек",
    ru: "Продолжите серию взятий",
    en: "Continue capturing",
  },
  "guest.checkers_white": { kz: "Ақ", ru: "Белые", en: "White" },
  "guest.checkers_black": { kz: "Қара", ru: "Чёрные", en: "Black" },
  "guest.checkers_winner": {
    kz: "{name} жеңді 🏆",
    ru: "{name} победил 🏆",
    en: "{name} won 🏆",
  },
  "guest.checkers_draw": { kz: "Тең ойын", ru: "Ничья", en: "Draw" },
  "guest.checkers_resign": { kz: "Тапсыру", ru: "Сдаться", en: "Resign" },
  "guest.checkers_new_game": {
    kz: "Қайта бастау",
    ru: "Новая партия",
    en: "New game",
  },
  "guest.checkers_close": { kz: "Жабу", ru: "Закрыть", en: "Close" },
  "guest.checkers_err_generic": {
    kz: "Қателік: ойынды жүктеу мүмкін болмады",
    ru: "Ошибка: не удалось обновить игру",
    en: "Error: failed to update the game",
  },
  "guest.cart_scope_need_auth": {
    kz: "Себетке қосу үшін кіру керек",
    ru: "Нужна авторизация для корзины",
    en: "Sign-in required to add to cart",
  },
  "guest.cart_badge_shared": { kz: "Ортақ", ru: "Общее", en: "Shared" },
  "guest.cart_badge_for": {
    kz: "{name} шотына",
    ru: "На счёт: {name}",
    en: "Bill: {name}",
  },
  "guest.cart_added_by": {
    kz: "қосты: {name}",
    ru: "добавил: {name}",
    en: "added by {name}",
  },
  "guest.cart_settlement_title": {
    kz: "Төлемге болжам (жеке + ортақ ÷ адам)",
    ru: "К оплате (личное + общее ÷ гостей)",
    en: "Payment estimate (personal + shared ÷ guests)",
  },
  "guest.cart_settlement_line": {
    kz: "{name}: {personal} + (ортақ {shared} ÷ {n} = {share}) = {total}",
    ru: "{name}: {personal} + (общее {shared} ÷ {n} = {share}) = {total}",
    en: "{name}: {personal} + (shared {shared} ÷ {n} = {share}) = {total}",
  },
  "guest.cart_shared_pool": {
    kz: "Ортақ тағамдар сомасы",
    ru: "Сумма общих позиций",
    en: "Shared items total",
  },
  "guest.cart_order_submit": {
    kz: "Тапсырыс беру (бәрі растасын)",
    ru: "Заказать (все подтвердят)",
    en: "Place order (everyone confirms)",
  },
  "guest.cart_order_pending": {
    kz: "Растау күтілуде: {done}/{total}",
    ru: "Ожидаем подтверждения: {done}/{total}",
    en: "Awaiting confirmations: {done}/{total}",
  },
  "guest.cart_order_pending_oneline": {
    kz: "Растау: {done}/{total}",
    ru: "Подтверждения: {done}/{total}",
    en: "Confirming: {done}/{total}",
  },
  "guest.cart_order_sent_wait_oneline": {
    kz: "Тапсырыс жіберілді, күтіңіз.",
    ru: "Заказ отправлен, ожидайте.",
    en: "Order sent, please wait.",
  },
  "guest.cart_order_snapshot_title": {
    kz: "Тапсырыс құрамы",
    ru: "Состав заказа",
    en: "Order contents",
  },
  "guest.cart_order_waiting_names_short": {
    kz: "Күтеміз: {names}",
    ru: "Ждём: {names}",
    en: "Waiting: {names}",
  },
  "guest.cart_order_confirm": { kz: "Растаймын", ru: "Подтверждаю", en: "I confirm" },
  "guest.cart_order_cancel": { kz: "Болдырмау", ru: "Отменить заявку", en: "Cancel request" },
  "guest.cart_order_done": {
    kz: "Тапсырыс жіберілді. Күтіңіз.",
    ru: "Заказ отправлен. Ожидайте.",
    en: "Order sent. Please wait.",
  },
  "guest.cart_order_err": {
    kz: "Қате: {msg}",
    ru: "Ошибка: {msg}",
    en: "Error: {msg}",
  },
  "guest.cart_err_not_payer": {
    kz: "Бұл тағамды тек шотты төлейтін қонақ өзгерте алады.",
    ru: "Эту позицию может изменить только тот, кто её оплачивает.",
    en: "Only the guest paying for this item can change it.",
  },
  "guest.cart_err_line_not_found": {
    kz: "Тағам себетте табылмады (басқа қонақ жойған шығар).",
    ru: "Позиция не найдена (возможно, её удалил другой гость).",
    en: "Item not found in cart (another guest may have removed it).",
  },
  "guest.cart_err_not_signed_in": {
    kz: "Қайта кіруіңізді өтінеміз.",
    ru: "Пожалуйста, войдите заново.",
    en: "Please sign in again.",
  },
  "guest.cart_drawer_hint": {
    kz: "Тапсырыс үшін төмендегі «Себет» вкладкасына өтіңіз.",
    ru: "Чтобы отправить заказ, откройте вкладку «Корзина» внизу.",
    en: "To submit the order, open the Cart tab below.",
  },
  "guest.cart_remove_line": { kz: "Жою", ru: "Удалить", en: "Remove" },
  "guest.geo_blocked": {
    kz: "Сіз кафе аумағында емессіз (GPS). Қолжетімділік шектелген.",
    ru: "Вы вне зоны заведения (GPS). Доступ ограничен.",
    en: "You are outside the venue zone (GPS). Access restricted.",
  },
  "guest.geo_warn": {
    kz: "Ескерту: сіз кафе радиусының сыртындасыз. Жалғастыра аласыз.",
    ru: "Предупреждение: вы вне радиуса. Можно продолжить.",
    en: "Warning: outside venue radius. You may continue.",
  },
  "guest.geo_permission": {
    kz: "Орынды тексеру үшін геолокация рұқсатын беріңіз.",
    ru: "Разрешите геолокацию для проверки места.",
    en: "Allow location to verify you are on site.",
  },
  "guest.geo_unavailable": {
    kz: "Геолокацияны анықтау мүмкін емес. Қайталап көріңіз.",
    ru: "Не удалось определить местоположение. Повторите.",
    en: "Could not get location. Try again.",
  },

  "guest.dish_comment_label": {
    kz: "Тілек / пікір (міндетті емес)",
    ru: "Пожелание / комментарий (необязательно)",
    en: "Note / wish (optional)",
  },
  "guest.dish_comment_placeholder": {
    kz: "Мысалы: луксыз, ащы, жартылай пісірілген…",
    ru: "Например: без лука, острее, среднепрожаренный…",
    en: "e.g.: no onions, extra spicy, medium rare…",
  },
  "guest.dish_view_3d": {
    kz: "3D-да көру",
    ru: "Посмотреть в 3D",
    en: "View in 3D",
  },
  "guest.dish_view_in_ar": {
    kz: "AR-да үстелге қою",
    ru: "Поставить на стол (AR)",
    en: "Place on table (AR)",
  },
  "guest.dish_3d_hint": {
    kz: "Саусақпен бұрыңыз, екі саусақпен үлкейтіңіз",
    ru: "Крутите пальцем, увеличивайте двумя пальцами",
    en: "Drag to rotate, pinch to zoom",
  },
  "guest.dish_3d_loading": {
    kz: "Үлгі жүктелуде…",
    ru: "Загрузка модели…",
    en: "Loading model…",
  },
  "guest.dish_3d_ar_preparing": {
    kz: "AR-ды дайындап жатырмыз…",
    ru: "Готовим AR…",
    en: "Getting AR ready…",
  },
  "guest.dish_3d_ar_pre_hint": {
    kz: "Жақсы жарықтандырылған бетті табыңыз — мата жабылған үстел немесе мәзір.",
    ru: "Найдите хорошо освещённую поверхность — например, стол со скатертью или меню.",
    en: "Find a well-lit textured surface — a tablecloth or menu works best.",
  },
  "guest.dish_3d_failed": {
    kz: "Үлгіні жүктеу мүмкін болмады",
    ru: "Не удалось загрузить модель",
    en: "Failed to load model",
  },
  "guest.dish_3d_hint_with_ar": {
    kz: "Саусақпен бұрыңыз. AR түймесін бассаңыз — камераны үстелге бағыттап, тағамды қойыңыз",
    ru: "Крутите пальцем. Нажмите AR — наведите камеру на стол и поставьте блюдо",
    en: "Drag to rotate. Tap AR, point camera at the table and place the dish",
  },
  "guest.delivery_not_available": {
    kz: "Бұл кафеде жеткізу қолжетімсіз. Алып кетуді таңдаңыз.",
    ru: "Доставка в этом кафе недоступна. Выберите самовывоз.",
    en: "Delivery is unavailable for this cafe. Choose pickup.",
  },
  "guest.cart_line_actions": {
    kz: "Әрекеттер",
    ru: "Действия",
    en: "Actions",
  },
  "guest.cart_line_transfer_to": {
    kz: "Басқаға беру",
    ru: "Передать другому",
    en: "Transfer to…",
  },
  "guest.cart_line_make_shared": {
    kz: "Ортақ ету",
    ru: "Сделать общим",
    en: "Make shared",
  },
  "guest.cart_line_assign_to_guest": {
    kz: "Біреуге жазу",
    ru: "Назначить на одного",
    en: "Assign to one guest",
  },
  "guest.cart_line_pick_recipient": {
    kz: "Кімге?",
    ru: "Кому?",
    en: "Pick recipient",
  },
  "guest.cart_line_transfer_no_others": {
    kz: "Үстелде басқа қонақ жоқ",
    ru: "За столом нет других гостей",
    en: "No other guests at the table",
  },
  "guest.cart_line_transfer_pending_for_you": {
    kz: "{from} сізге осы тағамды беруді сұрайды",
    ru: "{from} хочет передать вам это блюдо",
    en: "{from} wants to transfer this dish to you",
  },
  "guest.cart_line_transfer_pending_share_to_you": {
    kz: "{from} осы ортақ тағамды сізге жазуды сұрайды",
    ru: "{from} хочет назначить это общее блюдо на вас",
    en: "{from} wants to assign this shared dish to you",
  },
  "guest.cart_line_transfer_accept": {
    kz: "Қабылдау",
    ru: "Принять",
    en: "Accept",
  },
  "guest.cart_line_transfer_reject": {
    kz: "Бас тарту",
    ru: "Отклонить",
    en: "Decline",
  },
  "guest.cart_line_transfer_waiting": {
    kz: "{to} жауабын күтудеміз ({secs} сек)",
    ru: "Ждём ответа {to} ({secs} сек)",
    en: "Waiting for {to} ({secs}s)",
  },
  "guest.cart_line_transfer_nudge": {
    kz: "Жауап ұзаққа созылды — қонақты түртіп қойыңыз",
    ru: "Долго не отвечает — ткни соседа",
    en: "Taking too long — give them a nudge",
  },
  "guest.cart_line_transfer_cancel": {
    kz: "Бас тарту",
    ru: "Отменить",
    en: "Cancel",
  },
  "guest.cart_line_transfer_blocked_left": {
    kz: "Бұл қонақ үстелден шықты",
    ru: "Этот гость уже ушёл",
    en: "This guest has left the table",
  },
  "guest.cart_line_transfer_blocked_order": {
    kz: "Тапсырыс жіберілді — енді өзгерту мүмкін емес",
    ru: "Заказ уже отправлен — изменить нельзя",
    en: "Order already submitted — can't change now",
  },
  "guest.cart_line_share_vote_proposal": {
    kz: "{from} осы тағамды ортақ ету ұсынады. Келісесіз бе?",
    ru: "{from} предлагает сделать это блюдо общим. Согласны?",
    en: "{from} wants to make this dish shared. OK?",
  },
  "guest.cart_line_share_vote_approve": {
    kz: "Иә",
    ru: "Да",
    en: "Yes",
  },
  "guest.cart_line_share_vote_reject": {
    kz: "Жоқ",
    ru: "Нет",
    en: "No",
  },
  "guest.cart_line_share_vote_waiting": {
    kz: "Дауыс беруді күту: {approved}/{quorum} · {secs} сек қалды",
    ru: "Ждём голосов: {approved}/{quorum} · осталось {secs} сек",
    en: "Awaiting votes: {approved}/{quorum} · {secs}s left",
  },
  "guest.cart_line_share_vote_self_already_voted": {
    kz: "Сіз бұл ұсынысты бердіңіз",
    ru: "Вы автор предложения",
    en: "You proposed this",
  },
  "guest.cart_line_share_vote_already_approved": {
    kz: "Сіз дауыс бердіңіз",
    ru: "Вы уже проголосовали",
    en: "You already voted",
  },
  "guest.cart_line_share_vote_blocked": {
    kz: "Сіз жақында ұсыныс жасадыңыз — басқа қонақтан сұраңыз",
    ru: "Вы недавно предлагали — попросите кого-то другого",
    en: "You proposed recently — ask another guest",
  },
  "guest.cart_line_share_vote_failed_timeout": {
    kz: "Уақыт бітті — кворум жиналмады",
    ru: "Время истекло — кворум не собрался",
    en: "Time's up — quorum not reached",
  },
  "guest.notify_pending_actions": {
    kz: "📩 Себетте {n} белсенді сұраныс — қарап шығыңыз",
    ru: "📩 В корзине {n} активный запрос — нажмите чтобы открыть",
    en: "📩 {n} pending request{plural} in cart — tap to open",
  },
  "guest.greeting": {
    kz: "Сәлем, {name}! 👋",
    ru: "Привет, {name}! 👋",
    en: "Hi, {name}! 👋",
  },
  "guest.post_add_upsell_title": {
    kz: "{added} үшін мынаны ұсынамыз:",
    ru: "К «{added}» часто берут:",
    en: "Goes well with «{added}»:",
  },
  "guest.post_add_upsell_add": {
    kz: "Қосу — {price}",
    ru: "Добавить — {price}",
    en: "Add for {price}",
  },
  "guest.post_add_upsell_skip": {
    kz: "Жоқ, рақмет",
    ru: "Нет, спасибо",
    en: "No, thanks",
  },
  "guest.dish_ar_session_failed": {
    kz: "AR сессиясын бастау мүмкін болмады",
    ru: "Не удалось запустить AR-сессию",
    en: "Could not start AR session",
  },

  "guest.demo_banner_dev": {
    kz: "Демо мәзір: Firestore толық жүктелгенде нақты мәзірге ауысады.",
    ru: "Демо-меню: после полной загрузки Firestore отобразится ваше меню.",
    en: "Demo menu: switches to your Firestore menu when it is fully loaded.",
  },

  "guest.ai_fab": {
    kz: "ИИ көмекші",
    ru: "ИИ-помощник",
    en: "AI helper",
  },
  "guest.ai_hint_ask_me": {
    kz: "Менен сұраңыз 👋",
    ru: "Спросите меня 👋",
    en: "Ask me 👋",
  },
  "guest.ai_hint_budget": {
    kz: "5000₸ ішіне ұсыныс жасай аламын 💡",
    ru: "Подскажу за 5000₸ 💡",
    en: "Suggest under 5000₸ 💡",
  },
  "guest.ai_hint_dastarkhan": {
    kz: "4 адамға дастархан құрамын 🍽️",
    ru: "Соберу дастархан на 4 🍽️",
    en: "I can plan a feast for 4 🍽️",
  },
  "guest.ai_hint_call_waiter": {
    kz: "Сізге бірдеңе керек пе? 🛎️",
    ru: "Нужна помощь? Позову официанта 🛎️",
    en: "Need help? I'll call the waiter 🛎️",
  },
  "guest.ai_hint_dish_question": {
    kz: "Тағам туралы сұрақ па? 🍲",
    ru: "Вопрос по блюду? 🍲",
    en: "Question about a dish? 🍲",
  },
  "guest.ai_title": {
    kz: "ИИ-көмекші — тағам таңдау",
    ru: "ИИ-помощник — выбор блюд",
    en: "AI helper — pick dishes",
  },
  "guest.ai_subtitle": {
    kz: "Бюджетіңізге сай дұрыс тағамды ұсынады",
    ru: "Подберёт блюда под ваш бюджет и пожелания",
    en: "Recommends dishes for your budget and tastes",
  },
  "guest.ai_budget_label": {
    kz: "Бюджет (₸, міндетті емес)",
    ru: "Бюджет (₸, необязательно)",
    en: "Budget (₸, optional)",
  },
  "guest.ai_input_placeholder": {
    kz: "Не қалайсыз? Мысалы: «3000 теңгеге ыстық тағам»",
    ru: "Что хотите? Например: «горячее блюдо до 3000 ₸»",
    en: "What do you want? e.g. «hot dish under 3000 ₸»",
  },
  "guest.ai_send": {
    kz: "Жіберу",
    ru: "Отправить",
    en: "Send",
  },
  "guest.ai_thinking": {
    kz: "Ойлап жатыр…",
    ru: "Думаю…",
    en: "Thinking…",
  },
  "guest.ai_clear": {
    kz: "Тазарту",
    ru: "Очистить",
    en: "Clear",
  },
  "guest.ai_close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "guest.ai_disclaimer": {
    kz: "ИИ кеңесі ғана. Алхимиктер мен аллергия бойынша даяшыдан тексеріңіз.",
    ru: "Только рекомендация ИИ. Уточняйте состав и аллергены у официанта.",
    en: "AI suggestion only. Confirm ingredients / allergens with staff.",
  },
  "guest.ai_starter_budget": {
    kz: "5000 теңгеге не алуға болады?",
    ru: "Что взять на 5000 ₸?",
    en: "What can I get for 5000 ₸?",
  },
  "guest.ai_starter_lunch": {
    kz: "Жеңіл түскі ас ұсыныңыз",
    ru: "Посоветуй лёгкий обед",
    en: "Suggest a light lunch",
  },
  "guest.ai_starter_share": {
    kz: "4 адамға ортақ дастархан",
    ru: "Стол на 4 человек (общее)",
    en: "Shared table for 4",
  },
  "guest.ai_err_signin": {
    kz: "Алдымен жүйеге кіру қажет.",
    ru: "Сначала войдите в сессию.",
    en: "Please join the session first.",
  },
  "guest.ai_err_no_key": {
    kz: "ИИ әлі қосылмаған (OPENAI_API_KEY). Сервер күтілуде.",
    ru: "ИИ ещё не подключён (OPENAI_API_KEY). Ожидаем серверный ключ.",
    en: "AI is not configured yet (OPENAI_API_KEY missing on server).",
  },
  "guest.ai_err_generic": {
    kz: "Қате шықты. Қайта көріңіз.",
    ru: "Произошла ошибка. Попробуйте ещё раз.",
    en: "Something went wrong. Please retry.",
  },
  "guest.ai_empty_hint": {
    kz: "Сұрақ жазыңыз немесе төмендегі үлгіні таңдаңыз.",
    ru: "Напишите вопрос или выберите подсказку ниже.",
    en: "Type a question or pick a sample below.",
  },

  "guest.cart_order_waiting_for": {
    kz: "Күтілуде: {names}",
    ru: "Ждём: {names}",
    en: "Waiting for: {names}",
  },
  "guest.cart_pay_btn": {
    kz: "Төлеу",
    ru: "Оплатить",
    en: "Pay now",
  },
  "guest.games_while_waiting_title": {
    kz: "Күтіп тұрып ойнаңыз",
    ru: "Поиграйте пока ждёте",
    en: "Play while you wait",
  },
  "guest.games_while_waiting_hint": {
    kz: "Кім төлейтінін дөңгелек шешсін 🎯",
    ru: "Колесо решит, кто оплачивает счёт 🎯",
    en: "Let the wheel decide who pays the bill 🎯",
  },
  "guest.games_play_wheel": {
    kz: "Кім төлейді?",
    ru: "Кто платит?",
    en: "Who pays?",
  },
  "guest.order_status_title": {
    kz: "Тапсырыс мәртебесі",
    ru: "Статус заказа",
    en: "Order status",
  },
  "guest.order_status_received": {
    kz: "Қабылданды",
    ru: "Принят",
    en: "Received",
  },
  "guest.order_status_cooking": {
    kz: "Дайындалуда",
    ru: "Готовится",
    en: "Cooking",
  },
  "guest.order_status_ready": {
    kz: "Дайын",
    ru: "Готов",
    en: "Ready",
  },
  "guest.order_status_served": {
    kz: "Берілді",
    ru: "Подан",
    en: "Served",
  },

  "guest.payment_title": {
    kz: "Есеп",
    ru: "Счёт",
    en: "Bill",
  },
  "guest.bill_title": {
    kz: "Үстел шоты",
    ru: "Счёт стола",
    en: "Table bill",
  },
  "guest.bill_orders": {
    kz: "Тапсырыстар",
    ru: "Заказы",
    en: "Orders",
  },
  "guest.payment_subtotal": {
    kz: "Тағамдар жиынтығы",
    ru: "Сумма блюд",
    en: "Food subtotal",
  },
  "guest.payment_service_charge": {
    kz: "Қызмет ақысы ({pct}%)",
    ru: "За обслуживание ({pct}%)",
    en: "Service charge ({pct}%)",
  },
  "guest.payment_tips": {
    kz: "Чаевые",
    ru: "Чаевые",
    en: "Tips",
  },
  "guest.payment_total": {
    kz: "Барлығы",
    ru: "Итого к оплате",
    en: "Total to pay",
  },
  "guest.payment_method": {
    kz: "Төлем тәсілі",
    ru: "Способ оплаты",
    en: "Payment method",
  },
  "guest.cashier_qr_title": {
    kz: "Кассирге QR көрсету",
    ru: "Показать QR кассиру",
    en: "Show QR to cashier",
  },
  "guest.cashier_qr_hint": {
    kz: "Кассир бұл QR-ды сканерлесе, сіздің столыңыз бен есебіңіз бірден ашылады.",
    ru: "Кассир сканирует этот QR — ваш стол и счёт откроются автоматически.",
    en: "Cashier scans this QR — your table and bill open automatically.",
  },
  "guest.cashier_qr_button": {
    kz: "Кассирде төлеу (QR)",
    ru: "Оплатить у кассира (QR)",
    en: "Pay at cashier (QR)",
  },
  "guest.payment_kaspi": { kz: "Kaspi QR", ru: "Kaspi QR", en: "Kaspi QR" },
  "guest.payment_cash": { kz: "Қолма-қол", ru: "Наличные", en: "Cash" },
  // ── Kaspi-сверка v1 (00179): гостевая заявка «Я перевёл» ──────────────────
  "guest.kaspi_transfer_title": {
    kz: "Kaspi аударымымен төлеу",
    ru: "Оплатить Kaspi-переводом",
    en: "Pay via Kaspi transfer",
  },
  "guest.kaspi_remote_title": {
    kz: "Kaspi қашықтан төлемді сұрау",
    ru: "Попросить удалённую оплату Kaspi",
    en: "Request remote Kaspi payment",
  },
  "guest.kaspi_transfer_phone_label": {
    kz: "Аударым нөмірі",
    ru: "Номер для перевода",
    en: "Transfer to number",
  },
  "guest.kaspi_transfer_holder": {
    kz: "Алушы: {name}",
    ru: "Получатель: {name}",
    en: "Recipient: {name}",
  },
  "guest.kaspi_transfer_copy": {
    kz: "Нөмірді көшіру",
    ru: "Скопировать номер",
    en: "Copy number",
  },
  "guest.kaspi_transfer_copied": {
    kz: "Көшірілді",
    ru: "Скопировано",
    en: "Copied",
  },
  "guest.kaspi_transfer_amount_label": {
    kz: "Аударым сомасы",
    ru: "Сумма перевода",
    en: "Transfer amount",
  },
  "guest.kaspi_transfer_amount_hint": {
    kz: "Үлесіңізді төлеу үшін соманы азайтуға болады",
    ru: "Можно уменьшить сумму, чтобы оплатить свою долю",
    en: "You may lower the amount to pay just your share",
  },
  "guest.kaspi_transfer_payer_label": {
    kz: "Kaspi-дегі жіберуші аты",
    ru: "Имя отправителя в Kaspi",
    en: "Sender name in Kaspi",
  },
  "guest.kaspi_transfer_payer_hint": {
    kz: "Кассир аударымды осы ат бойынша салыстырады",
    ru: "Кассир сверит перевод по этому имени",
    en: "Cashier will match the transfer by this name",
  },
  "guest.kaspi_transfer_submit": {
    kz: "Мен аудардым",
    ru: "Я перевёл",
    en: "I sent the transfer",
  },
  "guest.kaspi_transfer_update": {
    kz: "Өтінімді жаңарту",
    ru: "Обновить заявку",
    en: "Update claim",
  },
  "guest.kaspi_transfer_cancel": {
    kz: "Болдырмау",
    ru: "Отменить",
    en: "Cancel",
  },
  "guest.kaspi_transfer_pending": {
    kz: "Кассирдің растауын күтудеміз…",
    ru: "Ждём подтверждения кассира…",
    en: "Waiting for cashier confirmation…",
  },
  "guest.kaspi_transfer_confirmed": {
    kz: "Аударым расталды. Рақмет!",
    ru: "Перевод подтверждён. Спасибо!",
    en: "Transfer confirmed. Thank you!",
  },
  "guest.kaspi_transfer_rejected": {
    kz: "Аударым табылмады — кассаға жақындаңыз",
    ru: "Перевод не найден — подойдите к кассе",
    en: "Transfer not found — please see the cashier",
  },
  "guest.kaspi_transfer_expired": {
    kz: "Өтінім мерзімі өтті. Қайта жіберіңіз немесе кассирге айтыңыз",
    ru: "Заявка истекла. Отправьте снова или обратитесь к кассиру",
    en: "Claim expired. Submit again or contact the cashier",
  },
  "guest.kaspi_transfer_err_amount": {
    kz: "Сома 1-ден қалған шотқа дейін болуы керек",
    ru: "Сумма должна быть от 1 до остатка по счёту",
    en: "Amount must be between 1 and the remaining bill",
  },
  "guest.kaspi_transfer_err_name": {
    kz: "Жіберуші атын енгізіңіз (кемінде 2 әріп)",
    ru: "Укажите имя отправителя (минимум 2 символа)",
    en: "Enter the sender name (min 2 characters)",
  },
  // ── Удалёнка (00184): гость просит кассира отправить запрос оплаты ────────
  "guest.kaspi_mode_transfer": {
    kz: "Өзім аударамын",
    ru: "Переведу сам",
    en: "I'll transfer myself",
  },
  "guest.kaspi_mode_remote": {
    kz: "Қашықтан төлем",
    ru: "Удалённая оплата",
    en: "Remote payment",
  },
  "guest.kaspi_remote_hint": {
    kz: "Кассир Kaspi-ге төлем сұранысын жібереді — Kaspi қосымшасында растайсыз",
    ru: "Кассир отправит запрос оплаты в Kaspi — подтвердите его в приложении Kaspi",
    en: "The cashier will send a payment request to Kaspi — confirm it in the Kaspi app",
  },
  "guest.kaspi_remote_to_phone": {
    kz: "Kaspi сұранысы {phone} нөміріне жіберіледі — Kaspi қосымшасында растаңыз.",
    ru: "Kaspi-запрос придёт на номер {phone} — подтвердите его в приложении Kaspi.",
    en: "The Kaspi request will be sent to {phone} — confirm it in the Kaspi app.",
  },
  "guest.kaspi_remote_needs_phone": {
    kz: "Жоғарыдағы «Телефон» өрісіне нөміріңізді енгізіңіз — Kaspi сұранысы сол нөмірге жіберіледі.",
    ru: "Укажите номер телефона в поле «Телефон» выше — Kaspi-запрос придёт на него.",
    en: "Enter your phone number in the \"Phone\" field above — the Kaspi request will be sent there.",
  },
  "guest.kaspi_remote_phone_label": {
    kz: "Kaspi-дегі телефон нөміріңіз",
    ru: "Ваш номер телефона в Kaspi",
    en: "Your Kaspi phone number",
  },
  "guest.kaspi_remote_submit": {
    kz: "Төлем сұранысын сұрау",
    ru: "Попросить удалённую оплату",
    en: "Request remote payment",
  },
  "guest.kaspi_remote_pending": {
    kz: "Кассир Kaspi-ге төлем сұранысын жібереді — қосымшаны тексеріңіз…",
    ru: "Кассир отправит запрос оплаты в Kaspi — проверьте приложение…",
    en: "Cashier is sending a Kaspi payment request — check your app…",
  },
  "guest.kaspi_remote_err_phone": {
    kz: "ҚР нөмірін енгізіңіз (11 сан, 7/8-ден басталады)",
    ru: "Укажите номер КЗ (11 цифр, начинается с 7/8)",
    en: "Enter a valid KZ number (11 digits, starts with 7/8)",
  },
  // ── Видимость заявок ДРУГИХ гостей стола (общий статус оплаты, а не «новый») ─
  "guest.kaspi_someone": {
    kz: "Үстел қонағы",
    ru: "Гость стола",
    en: "A guest",
  },
  "guest.kaspi_other_waiting": {
    kz: "кассирдің растауын күтудеміз",
    ru: "ждём подтверждения кассира",
    en: "waiting for cashier confirmation",
  },
  "guest.kaspi_other_pending": {
    kz: "{name} өз үлесін аударып жатыр",
    ru: "{name} переводит свою часть",
    en: "{name} is transferring their share",
  },
  "guest.kaspi_other_pending_remote": {
    kz: "{name} қашықтан төлемді сұрады",
    ru: "{name} просит удалённую оплату",
    en: "{name} requested a remote payment",
  },
  "guest.kaspi_covered": {
    kz: "Шот қалдығын үстелдің басқа қонақтары жіберіп қойды — кассирді күтудеміз.",
    ru: "Остаток счёта уже отправлен другими участниками стола — ждём кассира.",
    en: "The rest of the bill was already submitted by other guests — waiting for the cashier.",
  },
  "guest.payment_cash_tips_hint": {
    kz: "Қолма-қол төлеген кезде шайпұлды (чай) официантқа тікелей беріңіз — сайтта чаевые көрсетілмейді.",
    ru: "При оплате наличными чаевые отдайте официанту напрямую — на сайте они недоступны.",
    en: "Tips aren't available for cash payments — give them to the waiter directly.",
  },
  "guest.payment_currency_disclaimer": {
    kz: "Сома {currency} валютасында көрсетілген, бірақ төлем {base} валютасында қабылданады. Бағам — ҰБ.",
    ru: "Сумма показана в {currency}, но оплата принимается в {base} по курсу НБ.",
    en: "Amount shown in {currency}, but payment is accepted in {base} at the central-bank rate.",
  },
  "guest.payment_card": { kz: "Карта", ru: "Картой", en: "Card" },
  "guest.payment_submit": {
    kz: "Төлеу",
    ru: "Оплатить",
    en: "Pay",
  },
  "guest.payment_tips_custom": {
    kz: "Өз мөлшерім",
    ru: "Своя сумма",
    en: "Custom",
  },

  "guest.receipt_title": {
    kz: "Чек",
    ru: "Чек",
    en: "Receipt",
  },
  "guest.receipt_table": {
    kz: "{label}",
    ru: "{label}",
    en: "{label}",
  },
  "guest.receipt_subtotal": { kz: "Тағамдар", ru: "Блюда", en: "Food" },
  "guest.receipt_service": {
    kz: "Қызмет ақысы",
    ru: "За обслуживание",
    en: "Service charge",
  },
  "guest.receipt_tips": { kz: "Чаевые", ru: "Чаевые", en: "Tips" },
  "guest.receipt_total": { kz: "Барлығы", ru: "Итого", en: "Total" },
  "guest.receipt_method": { kz: "Төлем тәсілі", ru: "Способ оплаты", en: "Payment method" },
  "guest.receipt_thanks": {
    kz: "Рақмет! Тағы күтеміз ✨",
    ru: "Спасибо! Будем рады видеть снова ✨",
    en: "Thank you! Come back soon ✨",
  },
  "guest.receipt_download": {
    kz: "Жүктеу / Басып шығару",
    ru: "Скачать / Распечатать",
    en: "Download / Print",
  },
  "guest.receipt_share": {
    kz: "Бөлісу",
    ru: "Поделиться",
    en: "Share",
  },
  "guest.receipt_close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "guest.receipt_paid_with": {
    kz: "Төлем: {method}",
    ru: "Оплачено: {method}",
    en: "Paid by: {method}",
  },
  "guest.receipt_thank_you": {
    kz: "Рахмет! Тағы келіңіз 🙏",
    ru: "Спасибо! Приходите ещё 🙏",
    en: "Thank you! Come again 🙏",
  },
  "guest.receipt_per_guest": {
    kz: "Қонақ бойынша",
    ru: "По гостям",
    en: "Per guest",
  },
  "guest.receipt_shared_pool": {
    kz: "Ортақ тағамдар",
    ru: "Общие блюда",
    en: "Shared dishes",
  },
  "guest.receipt_shared_split_note": {
    kz: "{n} қонаққа бөлінеді",
    ru: "Делится на {n} гостей",
    en: "Split between {n} guests",
  },
  "guest.receipt_no": {
    kz: "Чек №",
    ru: "Чек №",
    en: "Receipt #",
  },
  "guest.receipt_guest_subtotal": {
    kz: "Жиынтығы",
    ru: "Сумма",
    en: "Subtotal",
  },

  "guest.wheel_title": {
    kz: "Кім төлейді?",
    ru: "Кто платит?",
    en: "Who pays?",
  },
  "guest.wheel_subtitle": {
    kz: "Дөңгелек шешеді 🎯",
    ru: "Колесо решит 🎯",
    en: "The wheel decides 🎯",
  },
  "guest.wheel_spin": {
    kz: "Айналдыру",
    ru: "Крутить",
    en: "Spin",
  },
  "guest.wheel_spin_again": {
    kz: "Тағы айналдыру",
    ru: "Ещё раз",
    en: "Spin again",
  },
  "guest.wheel_loser_label": {
    kz: "Бүгін төлейді:",
    ru: "Сегодня платит:",
    en: "Pays today:",
  },
  "guest.wheel_disclaimer": {
    kz: "Тек көңіл көтеру үшін 😄 нақты есеп ережелер бойынша бөлінеді.",
    ru: "Это только для смеха 😄 Настоящий счёт делится по обычным правилам.",
    en: "Just for fun 😄 The real bill is split as usual.",
  },
  "guest.wheel_need_guests": {
    kz: "Ойнау үшін кемінде 2 қонақ керек.",
    ru: "Чтобы крутить, нужно минимум 2 гостя за столом.",
    en: "Need at least 2 guests at the table.",
  },
  "guest.wheel_close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  // ─── Стол ашылғанда: «Кім бар» (атты енгізу экранында) ───
  "guest.name_screen_already_at_table": {
    kz: "Стол басында:",
    ru: "За столом сейчас:",
    en: "Already at the table:",
  },
  "guest.name_screen_table_empty": {
    kz: "Сіз бірінші келдіңіз — стол сізді күтіп тұр.",
    ru: "Вы пришли первым — стол ваш.",
    en: "You're first to arrive — the table is yours.",
  },
  // ─── Стол қанша уақыт ашық тұр (себет + касса) ───
  "guest.cart_table_opened_label": {
    kz: "Стол ашылды:",
    ru: "Стол открыт:",
    en: "Table opened:",
  },
  "guest.open_duration_just_now": {
    kz: "жаңа ғана",
    ru: "только что",
    en: "just now",
  },
  "guest.open_duration_min": {
    kz: "{min} мин бұрын",
    ru: "{min} мин назад",
    en: "{min} min ago",
  },
  "guest.open_duration_hr": {
    kz: "{hr} сағ бұрын",
    ru: "{hr} ч назад",
    en: "{hr} h ago",
  },
  "guest.open_duration_hr_min": {
    kz: "{hr} сағ {min} мин бұрын",
    ru: "{hr} ч {min} мин назад",
    en: "{hr} h {min} min ago",
  },
  // ─── Личные данные гостя (телефон / email / день рождения / аллергии) ───
  "guest.profile_personal_title": {
    kz: "Жеке деректер",
    ru: "Личные данные",
    en: "Personal info",
  },
  "guest.profile_personal_hint": {
    kz: "Электрондық чек, туған күн жеңілдігі және ИИ-кеңесі үшін.",
    ru: "Для электронного чека, скидки на день рождения и подсказок ИИ.",
    en: "Used for the e-receipt, birthday discount and AI tips.",
  },
  "guest.profile_birthday": {
    kz: "Туған күн",
    ru: "День рождения",
    en: "Birthday",
  },
  "guest.dob_day": {
    kz: "Күн",
    ru: "День",
    en: "Day",
  },
  "guest.dob_month": {
    kz: "Ай",
    ru: "Месяц",
    en: "Month",
  },
  "guest.dob_year": {
    kz: "Жыл",
    ru: "Год",
    en: "Year",
  },
  "guest.profile_birthday_hint": {
    kz: "Туған күніңізде автоматты жеңілдік аласыз.",
    ru: "В ваш день рождения автоматически даём скидку.",
    en: "You'll get an automatic discount on your birthday.",
  },
  "guest.profile_birthday_invalid": {
    kz: "Күн форматы дұрыс емес (YYYY-MM-DD).",
    ru: "Неверный формат даты (YYYY-MM-DD).",
    en: "Invalid date format (YYYY-MM-DD).",
  },
  "guest.profile_allergies": {
    kz: "Аллергия / диета",
    ru: "Аллергии / диета",
    en: "Allergies / diet",
  },
  "guest.profile_allergies_hint": {
    kz: "ИИ-көмекшіге және ас үйге беріледі. Мысалы: жаңғақ, лактоза, глютен.",
    ru: "Передаётся ИИ-помощнику и на кухню. Например: орехи, лактоза, глютен.",
    en: "Passed to the AI helper and to the kitchen. E.g. nuts, lactose, gluten.",
  },
  "guest.profile_allergies_placeholder": {
    kz: "жаңғақ, лактоза…",
    ru: "орехи, лактоза…",
    en: "nuts, lactose…",
  },
  "guest.profile_allergies_add_more": {
    kz: "тағы қосу…",
    ru: "ещё…",
    en: "add more…",
  },
  "guest.profile_allergies_add": {
    kz: "Қосу",
    ru: "Добавить",
    en: "Add",
  },
  "guest.profile_allergies_remove": {
    kz: "{name} жою",
    ru: "Удалить «{name}»",
    en: "Remove {name}",
  },
  // ─── Auth (push notifications toggle, аудит waiter 2026-07-06) ───
  "auth.push_title": {
    kz: "Push-хабарламалар",
    ru: "Push-уведомления",
    en: "Push notifications",
  },
  "auth.push_status_active": {
    kz: "Қосулы",
    ru: "Активны",
    en: "Active",
  },
  "auth.push_status_off": {
    kz: "Өшірулі",
    ru: "Выключены",
    en: "Off",
  },
  "auth.push_status_not_configured": {
    kz: "Бапталмаған",
    ru: "Не настроены",
    en: "Not set up",
  },
  "auth.push_status_blocked": {
    kz: "Браузер бұғаттады",
    ru: "Браузер заблокировал",
    en: "Blocked by browser",
  },
  "auth.push_status_unavailable": {
    kz: "Қолжетімсіз",
    ru: "Недоступно",
    en: "Unavailable",
  },
  "auth.push_status_error": {
    kz: "Қате",
    ru: "Ошибка",
    en: "Error",
  },
  "auth.push_enable": {
    kz: "Хабарламаларды қосу",
    ru: "Включить уведомления",
    en: "Enable notifications",
  },
  "auth.push_disable": {
    kz: "Хабарламаларды өшіру",
    ru: "Отключить уведомления",
    en: "Disable notifications",
  },
  "auth.push_denied_hint": {
    kz: "Браузер хабарламаларды бұғаттады. Сайт баптауларын ашыңыз (URL жанындағы 🔒 белгісі), «Notifications» рұқсат етіңіз де, бетті қайта жүктеңіз.",
    ru: "Браузер заблокировал уведомления. Открой настройки сайта (значок 🔒 рядом с URL) и разреши «Notifications», затем перезагрузи страницу.",
    en: "The browser blocked notifications. Open the site settings (the 🔒 icon next to the URL), allow “Notifications”, then reload the page.",
  },
  "auth.push_unsupported_hint": {
    kz: "Браузер Push API-ды қолдамайды. iOS-та тек Safari 16.4+ және тек PWA ретінде орнатылса жұмыс істейді («Үй экранына қосу»).",
    ru: "Браузер не поддерживает Push API. На iOS работает только в Safari 16.4+ и только если установить как PWA («Добавить на экран “Домой”»).",
    en: "The browser doesn't support the Push API. On iOS it works only in Safari 16.4+ and only when installed as a PWA (“Add to Home Screen”).",
  },
  "auth.push_wait": {
    kz: "Күте тұрыңыз…",
    ru: "Подождите…",
    en: "Please wait…",
  },
  // ─── Auth (welcome / email link) ───
  "auth.welcome_title": {
    kz: "Қош келдіңіз!",
    ru: "Добро пожаловать!",
    en: "Welcome!",
  },
  "auth.welcome_subtitle": {
    kz: "Қалай жалғастырамыз?",
    ru: "Как продолжим?",
    en: "How would you like to continue?",
  },
  "auth.continue_temporary": {
    kz: "Уақытша қонақ ретінде кіру",
    ru: "Войти временным гостем",
    en: "Continue as a guest",
  },
  "auth.continue_temporary_hint": {
    kz: "Тіркеусіз. Дерек тек осы құрылғыда сақталады.",
    ru: "Без регистрации. Данные сохраняются только на этом устройстве.",
    en: "No sign-up. Your data is kept only on this device.",
  },
  "auth.continue_with_email": {
    kz: "Email арқылы кіру",
    ru: "Войти по email",
    en: "Sign in with email",
  },
  "auth.continue_with_email_hint": {
    kz: "Профиль, аллергия, жинаған баллдарыңыз кез келген құрылғыда қолжетімді болады.",
    ru: "Профиль, аллергии, накопленные баллы будут доступны с любого устройства.",
    en: "Your profile, allergies and loyalty points stay with you on any device.",
  },
  "auth.email_label": {
    kz: "Email",
    ru: "Email",
    en: "Email",
  },
  "auth.name_label": {
    kz: "Атыңыз",
    ru: "Ваше имя",
    en: "Your name",
  },
  "auth.name_placeholder": {
    kz: "Айдар",
    ru: "Айдар",
    en: "Aidar",
  },
  "auth.name_required": {
    kz: "Атыңызды жазыңыз.",
    ru: "Введите имя.",
    en: "Please enter your name.",
  },
  "auth.email_invalid": {
    kz: "Email форматы дұрыс емес.",
    ru: "Неверный формат email.",
    en: "Invalid email format.",
  },
  "auth.email_form_hint": {
    kz: "Сізге сілтемесі бар хат жібереміз. Хат ішіндегі түймені басу арқылы кіресіз — пароль қажет емес.",
    ru: "Отправим письмо со ссылкой. Тапнете по ней — войдёте без пароля.",
    en: "We'll email you a magic link. Tap it to sign in — no password needed.",
  },
  "auth.send_link": {
    kz: "Сілтеме жіберу",
    ru: "Отправить ссылку",
    en: "Send link",
  },
  "auth.email_sending": {
    kz: "Жіберіліп жатыр…",
    ru: "Отправляем…",
    en: "Sending…",
  },
  "auth.email_link_sent_title": {
    kz: "Хат жіберілді",
    ru: "Письмо отправлено",
    en: "Email sent",
  },
  "auth.email_link_sent_to": {
    kz: "Хат жіберілді: {email}",
    ru: "Отправили на {email}",
    en: "Sent to {email}",
  },
  "auth.email_link_check_inbox": {
    kz: "Поштаңызды осы құрылғыда ашыңыз да, хаттағы сілтемені басыңыз — автоматты түрде кіресіз.",
    ru: "Откройте почту прямо на этом устройстве и тапните по ссылке — войдёте автоматически.",
    en: "Open your inbox on this device and tap the link — you'll sign in automatically.",
  },
  "auth.email_link_resend": {
    kz: "Қайта жіберу",
    ru: "Отправить ещё раз",
    en: "Resend",
  },
  "auth.email_link_resend_in": {
    kz: "{sec} с кейін қайта жіберуге болады",
    ru: "Повторить через {sec} с",
    en: "Resend in {sec}s",
  },
  "auth.email_link_use_other": {
    kz: "Басқа email пайдалану",
    ru: "Использовать другой email",
    en: "Use a different email",
  },
  "auth.email_link_failed": {
    kz: "Сілтеме жіберілмеді. Қайта көріңіз.",
    ru: "Не удалось отправить ссылку. Попробуйте ещё раз.",
    en: "Failed to send the link. Try again.",
  },
  "auth.email_link_not_enabled": {
    kz: "Email Link қолданушы тарапында қосылмаған. Әкімшіге хабарлаңыз.",
    ru: "Email Link не включён на сервере. Сообщите администратору.",
    en: "Email Link sign-in is not enabled on the server. Contact the admin.",
  },
  "auth.email_link_quota": {
    kz: "Шектен асып кеттіңіз. Бірнеше минуттан кейін қайта көріңіз.",
    ru: "Превышен лимит. Попробуйте через несколько минут.",
    en: "Rate limit exceeded. Try again in a few minutes.",
  },
  "auth.email_completing_title": {
    kz: "Аккаунт расталуда…",
    ru: "Подтверждаем аккаунт…",
    en: "Verifying your account…",
  },
  "auth.email_completing_hint": {
    kz: "Бірнеше секунд күтіңіз.",
    ru: "Подождите пару секунд.",
    en: "This will take a moment.",
  },
  "auth.email_completing_failed": {
    kz: "Кіру сәтсіз аяқталды. Сілтеме ескірген немесе бұрын ашылған шығар.",
    ru: "Не удалось войти. Возможно, ссылка устарела или уже использована.",
    en: "Sign-in failed. The link may have expired or already been used.",
  },
  "auth.email_completing_retry": {
    kz: "Қайталап көру",
    ru: "Попробовать снова",
    en: "Try again",
  },
  "auth.email_verified_badge": {
    kz: "Email расталды",
    ru: "Email подтверждён",
    en: "Email verified",
  },
  "auth.profile_link_email_cta": {
    kz: "Email арқылы аккаунт жасау",
    ru: "Закрепить за email",
    en: "Sign in with email",
  },
  "auth.signout": {
    kz: "Шығу және тазарту",
    ru: "Выйти и сбросить",
    en: "Sign out and reset",
  },
  "auth.back": {
    kz: "Артқа",
    ru: "Назад",
    en: "Back",
  },
  "auth.or": {
    kz: "немесе",
    ru: "или",
    en: "or",
  },
  // ─── Login / PIN screen (00161 multilingual) ───
  "auth.pin_label": {
    kz: "PIN-код",
    ru: "PIN-код",
    en: "PIN code",
  },
  "auth.sign_in": {
    kz: "Кіру",
    ru: "Войти",
    en: "Sign in",
  },
  "auth.signing_in": {
    kz: "Кіріп жатырмыз…",
    ru: "Входим…",
    en: "Signing in…",
  },
  "auth.checking_pin": {
    kz: "PIN тексерілуде…",
    ru: "Проверяем PIN…",
    en: "Checking PIN…",
  },
  // wrong_pin — НЕ системная ошибка: гость просто ошибся PIN'ом.
  "auth.pin_wrong": {
    kz: "PIN дұрыс емес. Қайта теріңіз.",
    ru: "Неверный PIN. Введите снова.",
    en: "Wrong PIN. Enter it again.",
  },
  // unknown / INTERNAL_ERROR — реальная поломка системы (отлична от wrong_pin).
  "auth.err_system": {
    kz: "Жүйе қатесі. Кейінірек қайталаңыз.",
    ru: "Системная ошибка. Попробуйте позже.",
    en: "System error. Please try again later.",
  },
  "auth.err_network": {
    kz: "Желі қатесі. Байланысты тексеріңіз.",
    ru: "Нет связи с сервером. Проверьте интернет.",
    en: "No connection. Check your internet.",
  },
  // Кнопка самолечения в network-баннере: сносит битую сохранённую сессию +
  // кэш/service worker и перезагружает — one-tap эквивалент «очистить данные
  // сайта», который раньше приходилось делать руками (см. authSelfHeal.ts).
  "auth.recover_relogin": {
    kz: "Жаңартып қайта кіру",
    ru: "Обновить и войти заново",
    en: "Refresh & sign in again",
  },
  "auth.rate_limited": {
    kz: "🔒 Тым көп әрекет. {sec}с күтіңіз",
    ru: "🔒 Слишком много попыток. Подождите {sec}с",
    en: "🔒 Too many attempts. Wait {sec}s",
  },
  "auth.tab_locked": {
    kz: "Сессия басқа қойындыда ашық",
    ru: "Сессия уже открыта в другой вкладке",
    en: "Session is already open in another tab",
  },
  // Кафе приостановлено платформой (00199 / TENANT_SUSPENDED). Fallback, если
  // сервер не прислал текст — обычно показывается server message.
  "auth.tenant_suspended": {
    kz: "Кафе уақытша тоқтатылған. Мекеме әкімшісіне хабарласыңыз.",
    ru: "Кафе временно приостановлено. Обратитесь к администратору заведения.",
    en: "The cafe is temporarily suspended. Contact the venue administrator.",
  },
  "auth.takeover": {
    kz: "Басқаруды алу",
    ru: "Перехватить управление",
    en: "Take over",
  },

  // ─── Экраны входа: то, что оставалось русским для всех (2026-07-26) ──────
  // Кабинеты держим на kz/ru/en — ровно те языки, что предлагает useAuthLang.
  // Раньше эти строки были захардкожены, и сотрудник с казахским интерфейсом
  // получал русский текст (а на PIN-экране — казахскую рамку с русской плашкой).

  // Названия ролей — общие для RoleGrid и StaffPickerByRole.
  "auth.role_owner": { kz: "Иесі", ru: "Владелец", en: "Owner" },
  "auth.role_admin": { kz: "Әкімші", ru: "Админ", en: "Admin" },
  "auth.role_manager": { kz: "Менеджер", ru: "Менеджер", en: "Manager" },
  "auth.role_accountant": { kz: "Бухгалтер", ru: "Бухгалтер", en: "Accountant" },
  "auth.role_cashier": { kz: "Кассир", ru: "Кассир", en: "Cashier" },
  "auth.role_waiter": { kz: "Даяшы", ru: "Официант", en: "Waiter" },
  "auth.role_cook": { kz: "Аспаз", ru: "Повар", en: "Cook" },
  "auth.role_chef": { kz: "Бас аспаз", ru: "Шеф-повар", en: "Chef" },
  "auth.role_purchaser": { kz: "Сатып алушы", ru: "Закупщик", en: "Purchaser" },
  "auth.role_warehouse": { kz: "Қоймашы", ru: "Кладовщик", en: "Storekeeper" },
  "auth.role_receiver": { kz: "Қабылдаушы", ru: "Приёмщик", en: "Receiver" },
  "auth.role_courier": { kz: "Курьер", ru: "Курьер", en: "Courier" },
  "auth.role_host": { kz: "Хостес", ru: "Хостес", en: "Host" },

  // Названия кабинетов — плашка на экране входа и подстановка в «нет доступа».
  "auth.app_admin": { kz: "Әкімші", ru: "Админ", en: "Admin" },
  "auth.app_cashier": { kz: "Касса", ru: "Касса", en: "Cashier" },
  "auth.app_kitchen": { kz: "Ас үй", ru: "Кухня", en: "Kitchen" },
  "auth.app_waiter": { kz: "Даяшы", ru: "Официант", en: "Waiter" },
  "auth.app_accountant": { kz: "Бухгалтерия", ru: "Бухгалтерия", en: "Accounting" },
  "auth.app_purchase": { kz: "Сатып алу", ru: "Закупки", en: "Purchasing" },
  "auth.app_receiver": { kz: "Қабылдау", ru: "Приёмщик", en: "Receiving" },
  "auth.app_storekeeper": { kz: "Қойма", ru: "Кладовщик", en: "Storeroom" },
  "auth.app_courier": { kz: "Курьер", ru: "Курьер", en: "Courier" },
  "auth.app_generic": { kz: "бұл қосымша", ru: "это приложение", en: "this app" },

  "auth.loading": { kz: "Жүктелуде…", ru: "Загрузка…", en: "Loading…" },

  // Перехват сессии.
  "auth.takeover_locked_app": { kz: "{app} — бұғатталған", ru: "{app} — заблокировано", en: "{app} — locked" },
  "auth.takeover_locked": { kz: "Бұғатталған", ru: "Заблокировано", en: "Locked" },
  "auth.takeover_other_tab": {
    kz: "Сессия басқа қойындыда ашық",
    ru: "Сессия уже открыта в другой вкладке",
    en: "The session is already open in another tab",
  },
  "auth.takeover_other_tab_off": {
    kz: "Басқа қойынды ажыратылады",
    ru: "Другая вкладка будет отключена",
    en: "The other tab will be disconnected",
  },

  // «Аккаунт не настроен» / «Приложение в разработке».
  "auth.na_heading_no_account": { kz: "Аккаунт бапталмаған", ru: "Аккаунт не настроен", en: "Account is not set up" },
  "auth.na_heading_not_built": { kz: "Қосымша әзірленуде", ru: "Приложение в разработке", en: "App is in development" },
  "auth.na_body_no_access": {
    kz: "Қолжетімсіз: {app}. Кафе әкімшісіне хабарласыңыз — ол сізге қажетті рөлмен аккаунт ашып, PIN орнатады.",
    ru: "Нет доступа: {app}. Обратитесь к администратору кафе — он создаст вам аккаунт с нужной ролью и установит PIN.",
    en: "No access: {app}. Contact the cafe administrator — they will create an account with the right role and set a PIN.",
  },
  "auth.na_body_not_built_role": {
    kz: "Аккаунтыңыз дұрыс бапталған, бірақ «{role}» рөліне арналған қосымша әзірленуде. Жақында қосамыз — әзірге шығып, басқа рөлмен кіруге болады (егер бірнеше рөліңіз болса).",
    ru: "Ваш аккаунт настроен корректно, но приложение для роли «{role}» ещё в разработке. Скоро добавим — а пока можно выйти и войти под другой ролью (если у вас их несколько).",
    en: "Your account is set up correctly, but the app for the «{role}» role is still in development. We'll add it soon — meanwhile you can sign out and sign in with another role (if you have several).",
  },
  "auth.na_body_not_built": {
    kz: "Аккаунтыңыз дұрыс бапталған, бірақ бұл рөлге арналған қосымша әзірленуде. Жақында қосамыз.",
    ru: "Ваш аккаунт настроен корректно, но приложение для этой роли ещё в разработке. Скоро добавим.",
    en: "Your account is set up correctly, but the app for this role is still in development. We'll add it soon.",
  },
  "auth.na_tips_no_account": { kz: "Не істеу керек", ru: "Что делать", en: "What to do" },
  "auth.na_tips_not_built": { kz: "Әзірге", ru: "Пока что", en: "For now" },
  "auth.na_tip_admin": { kz: "Кафе әкімшісіне хабарласыңыз", ru: "Обратитесь к администратору кафе", en: "Contact the cafe administrator" },
  "auth.na_tip_ask_account": {
    kz: "Қызметкер аккаунтын ашуын сұраңыз",
    ru: "Попросите создать вам аккаунт сотрудника",
    en: "Ask them to create a staff account for you",
  },
  "auth.na_tip_get_pin": { kz: "PIN-кодыңызды алыңыз", ru: "Получите свой PIN-код", en: "Get your PIN code" },
  "auth.na_tip_login_again": {
    kz: "Басты бет арқылы қайта кіріңіз",
    ru: "Войдите снова через главную страницу",
    en: "Sign in again from the home page",
  },
  "auth.na_tip_active": { kz: "Аккаунт белсенді — PIN сақталған", ru: "Аккаунт активен — PIN сохранён", en: "The account is active — the PIN is saved" },
  "auth.na_tip_wait": {
    kz: "Рөліңізге арналған қосымшаның шығуын күтіңіз",
    ru: "Подождите выхода приложения для вашей роли",
    en: "Wait for the app for your role to be released",
  },
  "auth.na_tip_other_role": {
    kz: "Бірнеше рөліңіз болса — шығып, басқасымен кіріңіз",
    ru: "Если у вас несколько ролей — выйдите и войдите под другой",
    en: "If you have several roles — sign out and sign in with another",
  },
  "auth.na_btn_other_role": {
    kz: "Шығып, басқа рөлмен кіру",
    ru: "Выйти и войти под другой ролью",
    en: "Sign out and sign in with another role",
  },
  "auth.na_btn_other_pin": {
    kz: "Шығып, басқа PIN енгізу",
    ru: "Выйти и попробовать другой PIN",
    en: "Sign out and try another PIN",
  },
  "auth.na_current_role": { kz: "Ағымдағы рөл:", ru: "Текущая роль:", en: "Current role:" },
  "auth.na_role_unset": { kz: "берілмеген", ru: "не задана", en: "not set" },

  // Грид ролей (шаг 1 входа).
  "auth.rg_who_works": { kz: "Кім жұмыс істеп жатыр?", ru: "Кто работает?", en: "Who's working?" },
  "auth.rg_empty_title": { kz: "Кафеде қызметкерлер жоқ", ru: "В кафе нет сотрудников", en: "No staff in this cafe" },
  "auth.rg_empty_hint": {
    kz: "Бірінші кірер алдында Admin → Қызметкерлер бөлімінде қызметкер қосыңыз.",
    ru: "Добавьте сотрудников в Admin → Сотрудники перед первым входом.",
    en: "Add staff in Admin → Staff before the first sign-in.",
  },
  // В kz/ru форма не меняется («1 адам», «1 чел.»), в en — меняется.
  "auth.rg_people_count": { kz: "{n} адам", ru: "{n} чел.", en: "{n} people" },
  "auth.rg_people_count_one": { kz: "{n} адам", ru: "{n} чел.", en: "{n} person" },
  "auth.rg_footer_tail": {
    kz: "кафе бизнесінің операциялық жүйесі",
    ru: "операционная система кафе",
    en: "the operating system for cafes",
  },

  // Выбор сотрудника (шаг 2 входа).
  "auth.sp_search": { kz: "Аты бойынша іздеу…", ru: "Найти по имени…", en: "Search by name…" },
  "auth.sp_not_found": {
    kz: "Мұндай атпен ешкім табылмады",
    ru: "Никого не нашли с таким именем",
    en: "No one found with that name",
  },
  "auth.sp_role_empty": { kz: "Бұл рөлде әзірге ешкім жоқ", ru: "В этой роли пока никого нет", en: "No one in this role yet" },
  "auth.sp_set_pin": { kz: "🔐 PIN орнату", ru: "🔐 Установить PIN", en: "🔐 Set a PIN" },
  "auth.sp_remember": {
    kz: "Осы құрылғыда мені есте сақтау",
    ru: "Запомнить меня на этом устройстве",
    en: "Remember me on this device",
  },

  // Вход по почте (кабинеты; у гостя свой набор auth.email_*).
  "auth.staff_email_title": { kz: "Поштамен кіру", ru: "Вход по почте", en: "Sign in by email" },
  "auth.staff_email_invalid": {
    kz: "Дұрыс email енгізіңіз — мысалы, owner@cafe.kz",
    ru: "Введите корректный email — например, owner@cafe.kz",
    en: "Enter a valid email — for example, owner@cafe.kz",
  },
  "auth.staff_email_too_many": {
    kz: "Тым көп әрекет. Бір минут күтіп, қайта көріңіз.",
    ru: "Слишком много попыток. Подождите минуту и попробуйте снова.",
    en: "Too many attempts. Wait a minute and try again.",
  },
  "auth.staff_email_send_failed": {
    kz: "Сілтемені жіберу мүмкін болмады. Байланысты тексеріп, қайталаңыз.",
    ru: "Не удалось отправить ссылку. Проверьте соединение и попробуйте ещё раз.",
    en: "Could not send the link. Check your connection and try again.",
  },
  "auth.staff_email_check_inbox": { kz: "Кіріс поштаны тексеріңіз", ru: "Проверьте входящие", en: "Check your inbox" },
  "auth.staff_email_hint_named": {
    kz: "{name} — жұмыс поштаңызды енгізіңіз, кіру сілтемесін жібереміз",
    ru: "{name} — введите рабочую почту, мы пришлём ссылку для входа",
    en: "{name} — enter your work email and we'll send a sign-in link",
  },
  "auth.staff_email_hint": {
    kz: "Жұмыс поштаңызды енгізіңіз — кіру сілтемесін жібереміз",
    ru: "Введите рабочую почту — мы пришлём ссылку для входа",
    en: "Enter your work email — we'll send a sign-in link",
  },
  "auth.staff_email_sent_prefix": {
    kz: "✓ Пошта қызметкер профилінде көрсетілген болса — кіру сілтемесі жіберілді:",
    ru: "✓ Если почта указана в профиле сотрудника — ссылка для входа отправлена на",
    en: "✓ If the email is listed in the staff profile — the sign-in link has been sent to",
  },
  "auth.staff_email_open_letter": {
    kz: "«Вход в Orda OS» хатын ашып, батырманы басыңыз. Сілтеме 1 сағат жарамды. Бір минутта келмесе — «Спам» бумасын тексеріңіз немесе иесінен поштаңызды «Қызметкерлер» бөліміне қосуын сұраңыз.",
    ru: "Откройте письмо «Вход в Orda OS» и нажмите кнопку. Ссылка действует 1 час. Не пришло за минуту — проверьте «Спам» или попросите владельца указать вашу почту в разделе «Сотрудники».",
    en: "Open the «Вход в Orda OS» email and tap the button. The link is valid for 1 hour. If it hasn't arrived within a minute — check «Spam» or ask the owner to add your email under «Staff».",
  },
  "auth.staff_email_use_browser": {
    kz: "Сілтемені кәдімгі браузерде ашыңыз (пошта қосымшасының ішкі қарау режимінде емес) — кіру осы құрылғыда сақталады.",
    ru: "Открывайте ссылку в обычном браузере (не во встроенном просмотрщике почтового приложения) — вход сохранится на этом устройстве.",
    en: "Open the link in a regular browser (not the mail app's built-in viewer) — the sign-in will persist on this device.",
  },
  "auth.staff_email_back_choice": { kz: "← Кіру тәсіліне оралу", ru: "← Назад к выбору входа", en: "← Back to sign-in options" },
  "auth.staff_email_get_link": { kz: "Сілтеме алу", ru: "Получить ссылку", en: "Get the link" },
  "auth.staff_email_footer": {
    kz: "Иелерге, әкімшілерге және менеджерлерге арналған. Кіргеннен кейін күнделікті жылдам кіру үшін PIN орнатуға болады.",
    ru: "Для владельцев, админов и менеджеров. После входа сможете задать PIN для быстрого ежедневного входа.",
    en: "For owners, admins and managers. After signing in you can set a PIN for quick daily access.",
  },

  // AI-подсказка «применить действие» (общий пузырь).
  "auth.ab_done": { kz: "Дайын", ru: "Готово", en: "Done" },
  "auth.ab_failed": { kz: "Сәтсіз аяқталды", ru: "Не удалось", en: "Failed" },
  "auth.ab_applying": { kz: "⏳ Орындалуда…", ru: "⏳ Выполняю…", en: "⏳ Applying…" },
  "auth.ab_apply": { kz: "Қолдану · {label}", ru: "Применить · {label}", en: "Apply · {label}" },
  "auth.enter_pin_continue": {
    kz: "Жалғастыру үшін PIN енгізіңіз",
    ru: "Введите PIN для продолжения",
    en: "Enter your PIN to continue",
  },
  "auth.email_login_cta": {
    kz: "✉️ Поштамен кіру",
    ru: "✉️ Войти по почте",
    en: "✉️ Sign in by email",
  },
  // FirstPinSetupScreen
  "auth.set_pin_title": {
    kz: "PIN орнату",
    ru: "Установите PIN",
    en: "Set a PIN",
  },
  "auth.confirm_pin_title": {
    kz: "PIN растаңыз",
    ru: "Подтвердите PIN",
    en: "Confirm PIN",
  },
  "auth.set_pin_subtitle": {
    kz: "{name} үшін алғашқы кіру — 6 саннан тұратын PIN ойлап табыңыз",
    ru: "Первый вход для {name} — придумайте 6-значный PIN",
    en: "First sign-in for {name} — choose a 6-digit PIN",
  },
  "auth.confirm_pin_subtitle": {
    kz: "Тексеру үшін PIN-ді қайта енгізіңіз",
    ru: "Введите PIN ещё раз для проверки",
    en: "Enter the PIN again to confirm",
  },
  "auth.saving_pin": {
    kz: "PIN сақталуда…",
    ru: "Сохраняем PIN…",
    en: "Saving PIN…",
  },
  "auth.saving": {
    kz: "Сақталуда…",
    ru: "Сохраняем…",
    en: "Saving…",
  },
  "auth.done_signing_in": {
    kz: "✓ Дайын. Кіріп жатырмыз…",
    ru: "✓ Готово. Входим…",
    en: "✓ Done. Signing in…",
  },
  "auth.done": {
    kz: "✓ Дайын",
    ru: "✓ Готово",
    en: "✓ Done",
  },
  "auth.weak_pin": {
    kz: "PIN тым қарапайым. Басқасын таңдаңыз.",
    ru: "Слишком простой PIN. Выберите другой.",
    en: "PIN is too simple. Choose another one.",
  },
  "auth.pin_mismatch": {
    kz: "PIN сәйкес келмейді. Қайта теріңіз.",
    ru: "PIN не совпадает. Введите ещё раз.",
    en: "PINs don’t match. Enter again.",
  },
  "auth.set_pin_failed": {
    kz: "PIN орнату мүмкін болмады",
    ru: "Не удалось установить PIN",
    en: "Could not set the PIN",
  },
  "auth.pin_already_set": {
    kz: "PIN орнатылған. Қайта орнату үшін менеджерге хабарласыңыз.",
    ru: "PIN уже установлен. Обратитесь к менеджеру для сброса.",
    en: "PIN is already set. Ask a manager to reset it.",
  },
  "auth.back_to_staff": {
    kz: "Қызметкерді таңдауға оралу",
    ru: "Назад к выбору сотрудника",
    en: "Back to staff selection",
  },
  "auth.pin_hint": {
    kz: "PIN — 6 сан, «123456» / «111111» емес",
    ru: "PIN — 6 цифр, не «123456» / «111111»",
    en: "PIN is 6 digits, not “123456” / “111111”",
  },
  "auth.pin_hint_remember": {
    kz: "Есте сақтаңыз — онсыз кіре алмайсыз",
    ru: "Запомните его — без него вы не сможете войти",
    en: "Remember it — you can’t sign in without it",
  },
  "auth.pin_keypad": {
    kz: "PIN пернетақтасы",
    ru: "Цифровая клавиатура",
    en: "PIN keypad",
  },
  "auth.pin_backspace": {
    kz: "Өшіру",
    ru: "Стереть",
    en: "Backspace",
  },
  "guest.name_collision_its_me_hint": {
    kz: "Email-ді растасаңыз, аккаунтыңыз кез келген құрылғыдан қол жетімді болады.",
    ru: "Подтвердив email, вы получите доступ к своему профилю с любого устройства.",
    en: "Verify your email to access your profile from any device.",
  },
  // ─── Баллы лояльности ───
  "guest.loyalty_title": {
    kz: "Лоялдық баллдары",
    ru: "Баллы лояльности",
    en: "Loyalty points",
  },
  "guest.loyalty_balance_label": {
    kz: "Балансыңыз",
    ru: "Ваш баланс",
    en: "Your balance",
  },
  "guest.loyalty_points": {
    kz: "балл",
    ru: "баллов",
    en: "pts",
  },
  "guest.loyalty_total_earned": {
    kz: "Барлығы жинадыңыз: {total}",
    ru: "Всего заработано: {total}",
    en: "Total earned: {total}",
  },
  "guest.loyalty_rule_hint": {
    kz: "Әр төленген 1 ₸ — 1 балл. Болашақта баллдарды жеңілдікке ауыстыруға болады.",
    ru: "1 ₸ оплаты = 1 балл. В будущем баллы можно будет обменять на скидку.",
    en: "1 ₸ paid = 1 point. You'll be able to redeem points for a discount soon.",
  },
  "guest.loyalty_redeem_code_label": {
    kz: "Кассада есептен шығару коды",
    ru: "Код для списания на кассе",
    en: "Redemption code for the register",
  },
  "guest.loyalty_redeem_code_hint": {
    kz: "Баллдарды есептен шығару үшін төлеу кезінде осы кодты кассирге айтыңыз.",
    ru: "Назовите этот код кассиру при оплате, чтобы списать баллы.",
    en: "Tell this code to the cashier at checkout to redeem your points.",
  },
  "guest.loyalty_empty": {
    kz: "Әзірге операциялар жоқ. Алғашқы тапсырысты беріп, баллдарды жинаңыз.",
    ru: "Пока операций нет. Сделайте первый заказ — баллы появятся автоматически.",
    en: "No transactions yet. Place your first order to start earning.",
  },
  "guest.loyalty_days_ago": {
    kz: "{days} күн бұрын",
    ru: "{days} дн. назад",
    en: "{days} d ago",
  },
  // ─── Фото-отзывы (DishRatingRow) ───
  "guest.dish_rating_add_photo": {
    kz: "Фото",
    ru: "Фото",
    en: "Photo",
  },
  "guest.dish_rating_photo_need_stars": {
    kz: "Алдымен жұлдыздарды таңдаңыз.",
    ru: "Сначала поставьте звёзды.",
    en: "Choose a rating first.",
  },
  "guest.dish_rating_photo_too_large": {
    kz: "Фото өте үлкен (3 МБ-тан кем болуы керек).",
    ru: "Фото слишком большое (до 3 МБ).",
    en: "Photo is too big (max 3 MB).",
  },
  "guest.dish_rating_photo_invalid_type": {
    kz: "JPG / PNG / WEBP қажет.",
    ru: "Нужен JPG / PNG / WEBP.",
    en: "JPG / PNG / WEBP only.",
  },
  "guest.dish_rating_photo_upload_failed": {
    kz: "Фотоны жүктеу мүмкін болмады.",
    ru: "Не удалось загрузить фото.",
    en: "Failed to upload photo.",
  },
  "guest.dish_rating_photo_alt": {
    kz: "Менің фото-пікірім",
    ru: "Моё фото к отзыву",
    en: "My review photo",
  },
  "guest.dish_rating_photo_remove": {
    kz: "Фотоны жою",
    ru: "Удалить фото",
    en: "Remove photo",
  },
  // ─── Строки, переведённые с локальных kz/ru/en-тернаров (2026-07-25) ───
  // Раньше эти тексты были захардкожены прямо в компонентах трёхъязычными
  // тернарами, и гости с интерфейсом на zh/ky/uz видели русский. zh/ky/uz —
  // в guestLangOverlay.ts.
  "guest.review_rate_heading": {
    kz: "Бағалаңыз",
    ru: "Оставить отзыв",
    en: "Rate us",
  },
  "guest.review_rate_sub": {
    kz: "Бір клик — үлкен көмек!",
    ru: "Один тап — большая помощь!",
    en: "One tap — huge help!",
  },
  "guest.geo_block_title": {
    kz: "Сіз кафеде жоқсыз",
    ru: "Вы не в кафе",
    en: "You're not at the café",
  },
  "guest.geo_block_subtitle": {
    kz: "Тапсырыс беру үшін кафеге жақын болыңыз",
    ru: "Чтобы сделать заказ, нужно находиться в кафе",
    en: "You must be physically at the café to order",
  },
  "guest.geo_block_distance": {
    kz: "Кафеге дейін {distance} (рұқсат: {radius})",
    ru: "{distance} от кафе (разрешено: {radius})",
    en: "{distance} from the café (allowed: {radius})",
  },
  "guest.geo_block_unknown": {
    kz: "Орналасу анықталмады",
    ru: "Местоположение не определено",
    en: "Location could not be determined",
  },
  // Единицы расстояния в гео-экране: раньше «км»/«м» были по-русски для всех.
  "guest.unit_km": {
    kz: "км",
    ru: "км",
    en: "km",
  },
  "guest.unit_m": {
    kz: "м",
    ru: "м",
    en: "m",
  },
  "guest.geo_block_rechecking": {
    kz: "Тексерілуде...",
    ru: "Проверяю...",
    en: "Checking...",
  },
  "guest.geo_block_recheck": {
    kz: "Қайта тексеру",
    ru: "Проверить снова",
    en: "Check again",
  },
  "guest.geo_block_hint": {
    kz: "Браузерде геолокацияны рұқсат еттіңіз бе?",
    ru: "Убедитесь, что геолокация разрешена в браузере.",
    en: "Make sure location access is allowed in your browser.",
  },
  "guest.geo_outside_warn": {
    kz: "Сіз кафеден тыс жерсіз. Дегенмен, тапсырыс бере аласыз.",
    ru: "Вы, похоже, не в кафе. Но вы всё равно можете продолжить.",
    en: "You appear to be outside the café. You can still proceed.",
  },
  "guest.cart_order_shared": {
    kz: "Ортақты тапсыру",
    ru: "Заказать общее",
    en: "Order shared",
  },
  "guest.cart_order_mine": {
    kz: "Өзімдікін тапсыру",
    ru: "Заказать моё",
    en: "Order mine",
  },
  "guest.cart_order_for_guest": {
    kz: "{name} үшін тапсыру",
    ru: "Заказать за {name}",
    en: "Order for {name}",
  },
  "guest.cart_pick_for_yourself": {
    kz: "Өзіңізге тағам таңдаңыз немесе ортаққа қосыңыз",
    ru: "Выберите блюда себе или добавьте в общее",
    en: "Pick dishes for yourself or add to shared",
  },
  "guest.orders_shared_title": {
    kz: "Жалпы тапсырыс",
    ru: "Общий заказ",
    en: "Shared order",
  },
  "guest.orders_share_of_shared": {
    kz: "Ортақтан үлесі",
    ru: "Доля общего",
    en: "Share of shared",
  },
  "guest.orders_service_charge": {
    kz: "Қызмет ақысы",
    ru: "Сервисный сбор",
    en: "Service charge",
  },
  "guest.orders_items_subtotal": {
    kz: "Тағамдар сомасы",
    ru: "Сумма блюд",
    en: "Items subtotal",
  },
  "guest.orders_service_split": {
    kz: "Қызмет ақысы {percent}% — қонақтар арасында бөлінген",
    ru: "Сервисный сбор {percent}% — распределён между гостями",
    en: "Service charge {percent}% — split between guests",
  },
  "guest.orders_bill_fully_paid": {
    kz: "Шот толық төленді",
    ru: "Счёт полностью оплачен",
    en: "Bill fully paid",
  },
  "guest.orders_remaining": {
    kz: "Қалды",
    ru: "Осталось",
    en: "Remaining",
  },
  "guest.dish_guest_not_arrived_err": {
    kz: "Алдымен алдыңғы келмеген қонақтың тапсырысын жіберіңіз",
    ru: "Сначала отправьте заказ за предыдущего гостя",
    en: "First send the previous not-arrived guest's order",
  },
  "guest.about_us": {
    kz: "Біз туралы",
    ru: "О нас",
    en: "About us",
  },
  "guest.table_word": {
    kz: "Үстел",
    ru: "Стол",
    en: "Table",
  },
  "guest.join_greeting": {
    kz: "Сәлеметсіз бе!",
    ru: "Добро пожаловать!",
    en: "Welcome!",
  },
  "guest.join_guests_at_table": {
    kz: "Үстелде бар",
    ru: "Уже за столом",
    en: "At the table",
  },
  "guest.join_name_placeholder": {
    kz: "Айдос, Динара...",
    ru: "Айдос, Динара...",
    en: "Alex, Maria...",
  },
  "guest.join_name_label": {
    kz: "Атыңыз",
    ru: "Ваше имя",
    en: "Your name",
  },
  "guest.join_button": {
    kz: "Қосылу →",
    ru: "Войти →",
    en: "Join →",
  },
  "guest.join_connecting": {
    kz: "Қосылуда…",
    ru: "Подключение…",
    en: "Connecting…",
  },
  "guest.join_footer": {
    kz: "Мобильді тапсырыс · Қосымшасыз",
    ru: "Мобильный заказ · Без приложения",
    en: "Mobile order · No app needed",
  },
  "guest.join_enter_name_err": {
    kz: "Атыңызды жазыңыз",
    ru: "Введите имя",
    en: "Enter your name",
  },
  "guest.table_guests_title": {
    kz: "Үстелде",
    ru: "За столом",
    en: "At the table",
  },
  "guest.table_opened_word": {
    kz: "ашылды",
    ru: "открыт",
    en: "opened",
  },
  "guest.table_guest_waiting": {
    kz: "күтудеміз",
    ru: "ждём",
    en: "waiting",
  },
  "guest.time_just_now": {
    kz: "жаңа ғана",
    ru: "только что",
    en: "just now",
  },
  "guest.time_minutes_ago": {
    kz: "{n} мин бұрын",
    ru: "{n} мин назад",
    en: "{n}m ago",
  },
  "guest.time_hours_ago": {
    kz: "{n} сағ бұрын",
    ru: "{n} ч назад",
    en: "{n}h ago",
  },
  "guest.about_phone": {
    kz: "Телефон",
    ru: "Телефон",
    en: "Phone",
  },
  "guest.about_daily": {
    kz: "Күнделікті",
    ru: "Ежедневно",
    en: "Daily",
  },
  "guest.about_sites_socials": {
    kz: "Сайттар және әлеуметтік желілер",
    ru: "Сайты и соцсети",
    en: "Sites & socials",
  },
  "guest.about_contacts": {
    kz: "Байланыс",
    ru: "Контакты",
    en: "Contacts",
  },
  "guest.landing_open_now": {
    kz: "Ашық",
    ru: "Открыто",
    en: "Open",
  },
  "guest.landing_back_to_photo": {
    kz: "Фотоға оралу",
    ru: "Вернуться к фото",
    en: "Back to photo",
  },
  "guest.landing_call_us": {
    kz: "Қоңырау шалу",
    ru: "Позвонить",
    en: "Call us",
  },
  "guest.landing_open_2gis": {
    kz: "2ГИС-те ашу",
    ru: "Открыть в 2ГИС",
    en: "Open in 2GIS",
  },
  "guest.landing_yandex_maps": {
    kz: "Яндекс Картада",
    ru: "Яндекс.Карты",
    en: "Yandex Maps",
  },
  "guest.landing_order_delivery": {
    kz: "Жеткізуге тапсырыс",
    ru: "Заказать доставку",
    en: "Order delivery",
  },
  "guest.landing_book_table": {
    kz: "Үстел брондау",
    ru: "Забронировать стол",
    en: "Book a table",
  },
  "guest.landing_or_call": {
    kz: "немесе қоңырау шалыңыз:",
    ru: "или позвоните:",
    en: "or call us:",
  },
  // Часы работы. kz без падежного суффикса: «-де/-да/-те/-та» зависит от
  // гласной часа, с цифрами надёжнее нейтральная форма.
  "guest.landing_opens_at": {
    kz: "Ашылады: {time}",
    ru: "Откроемся в {time}",
    en: "Opens at {time}",
  },
  "guest.landing_open_till": {
    kz: "Ашық · жабылады: {time}",
    ru: "Открыто · до {time}",
    en: "Open · till {time}",
  },
  "guest.landing_time_left_hm": {
    kz: "тағы {h} сағ {m} мин",
    ru: "ещё {h} ч {m} мин",
    en: "{h} h {m} min left",
  },
  "guest.landing_time_left_m": {
    kz: "тағы {m} мин",
    ru: "ещё {m} мин",
    en: "{m} min left",
  },
  "guest.landing_hot": {
    kz: "Ыстық!",
    ru: "Горячо!",
    en: "Hot!",
  },
  "guest.landing_cooked_like_home": {
    kz: "Үйдегідей дайындаймыз",
    ru: "Готовим как дома",
    en: "Cooked like home",
  },
  "guest.landing_signature_dishes": {
    kz: "Фирмалық тағамдар",
    ru: "Фирменные блюда",
    en: "Signature dishes",
  },
  "guest.landing_what_to_try": {
    kz: "Не татып көру керек",
    ru: "Что попробовать",
    en: "What to try",
  },
  "guest.landing_prices_in_kzt": {
    kz: "бағалар ₸",
    ru: "цены в ₸",
    en: "prices in ₸",
  },
  "guest.landing_full_menu": {
    kz: "Барлық мәзір",
    ru: "Всё меню",
    en: "Full menu",
  },
  "guest.landing_visit_us": {
    kz: "Қонаққа келіңіз",
    ru: "Заходите в гости",
    en: "Visit us",
  },
  "guest.landing_how_to_find": {
    kz: "Бізді қалай табуға болады",
    ru: "Как нас найти",
    en: "How to find us",
  },
  "guest.landing_table_is_set": {
    kz: "Дастарқан жайылды",
    ru: "Стол уже накрыт",
    en: "The table is set",
  },
  "guest.landing_order_or_visit": {
    kz: "Жеткізуге тапсырыс беріңіз немесе қонаққа келіңіз.",
    ru: "Закажите доставку или приходите в гости.",
    en: "Order delivery or come visit us.",
  },
  "guest.landing_menu": {
    kz: "Мәзір",
    ru: "Меню",
    en: "Menu",
  },
  "guest.landing_booking": {
    kz: "Брондау",
    ru: "Бронь",
    en: "Booking",
  },
  "guest.landing_book_short": {
    kz: "Брондау",
    ru: "Бронь",
    en: "Book",
  },
  "guest.landing_quick_actions": {
    kz: "Жылдам әрекеттер",
    ru: "Быстрые действия",
    en: "Quick actions",
  },
  "guest.landing_delivery_short": {
    kz: "Жеткізу",
    ru: "Доставка",
    en: "Delivery",
  },
  "guest.landing_call_short": {
    kz: "Қоңырау шалу",
    ru: "Позвонить",
    en: "Call",
  },
  "guest.nav_aria": {
    kz: "Навигация",
    ru: "Навигация",
    en: "Navigation",
  },
  "guest.about_website": {
    kz: "Сайт",
    ru: "Сайт",
    en: "Website",
  },

  // ─── Игры за столом (2026-07-26) ─────────────────────────────────────────
  // Раньше строки игр были захардкожены по-русски прямо в JSX: гость на
  // kz/en/zh/ky/uz видел русский текст. zh/ky/uz — в guestLangOverlay.ts;
  // инвариант полноты держит missingGuestLangKeys().

  // Общие для сетевых партий (шахматы / шашки / морской бой).
  "guest.game_you_paren": { kz: "(сіз)", ru: "(вы)", en: "(you)" },
  "guest.game_spectating": { kz: "· бақылап отырсыз", ru: "· смотрите", en: "· spectating" },
  "guest.game_turn_p1": { kz: "1-ойыншының кезегі", ru: "Ход игрока 1", en: "Player 1's turn" },
  "guest.game_turn_p2": { kz: "2-ойыншының кезегі", ru: "Ход игрока 2", en: "Player 2's turn" },
  "guest.game_your_turn": { kz: "Сіздің кезегіңіз", ru: "Ваш ход", en: "Your turn" },
  "guest.game_opponent_turn": { kz: "Қарсыластың кезегі…", ru: "Ход соперника…", en: "Opponent's turn…" },
  "guest.game_draw": { kz: "Тең ойын", ru: "Ничья", en: "Draw" },
  "guest.game_winner_named": { kz: "{name} жеңді", ru: "Победил {name}", en: "{name} won" },
  "guest.game_opponent_left_you_win": {
    kz: "Қарсылас шықты — сіз жеңдіңіз 🎉",
    ru: "Соперник вышел — вы победили 🎉",
    en: "Opponent left — you won 🎉",
  },
  "guest.game_you_won": { kz: "Сіз жеңдіңіз! 🎉", ru: "Вы победили! 🎉", en: "You won! 🎉" },
  "guest.game_you_left": { kz: "Сіз ойыннан шықтыңыз", ru: "Вы вышли из партии", en: "You left the game" },
  "guest.game_you_lost": { kz: "Сіз жеңілдіңіз", ru: "Вы проиграли", en: "You lost" },
  "guest.game_rematch": { kz: "↺ Реванш", ru: "↺ Реванш", en: "↺ Rematch" },
  "guest.game_new_match": { kz: "↺ Жаңа ойын", ru: "↺ Новая партия", en: "↺ New game" },
  "guest.game_player1": { kz: "1-ойыншы", ru: "Игрок 1", en: "Player 1" },
  "guest.game_player2": { kz: "2-ойыншы", ru: "Игрок 2", en: "Player 2" },

  // Лобби сетевой партии (NetworkGame).
  "guest.game_join_failed": { kz: "Ойынға кіру мүмкін болмады", ru: "Не удалось войти в игру", en: "Could not join the game" },
  "guest.game_connecting_table": { kz: "Үстелге қосылудамыз…", ru: "Подключаемся к столу…", en: "Connecting to the table…" },
  "guest.game_waiting_opponent": { kz: "Қарсыласты күтудеміз…", ru: "Ждём соперника…", en: "Waiting for an opponent…" },
  "guest.game_waiting_opponent_hint": {
    kz: "Үстелдегі тағы біреу осы ойынды ашып, «Ойнау» батырмасын бассын — ойын бірден басталады.",
    ru: "Пусть кто-то ещё за столом откроет эту игру и нажмёт «Играть» — партия начнётся сразу.",
    en: "Have someone else at the table open this game and tap «Play» — the match starts right away.",
  },
  "guest.game_cancel": { kz: "Болдырмау", ru: "Отменить", en: "Cancel" },
  "guest.game_to_lobby": { kz: "лоббиге", ru: "к лобби", en: "to lobby" },
  "guest.game_spectating_pair": {
    kz: "👀 Бақылаудамыз: {p1} vs {p2}",
    ru: "👀 Смотрим: {p1} vs {p2}",
    en: "👀 Watching: {p1} vs {p2}",
  },
  "guest.game_play_with_guests": {
    kz: "Үстелдегі қонақтармен ойнау",
    ru: "Играть с гостями за столом",
    en: "Play with guests at the table",
  },
  "guest.game_lobby_hint": {
    kz: "«Ойнау» батырмасын басыңыз — сізді басқа қонақпен қосады. Ойнаушы көп болса — жаңа ойын басталады.",
    ru: "Нажмите «Играть» — вас соединит с другим гостем. Если играющих много — начнётся новая партия.",
    en: "Tap «Play» — you'll be matched with another guest. If many are playing, a new match starts.",
  },
  "guest.game_running_matches": { kz: "Жүріп жатқан ойындар ({n})", ru: "Идут партии ({n})", en: "Matches in progress ({n})" },
  "guest.game_watch": { kz: "қарау", ru: "смотреть", en: "watch" },

  // Переключатель режима + сложность бота.
  "guest.game_mode_net": { kz: "🌐 Қонақтармен", ru: "🌐 С гостями", en: "🌐 With guests" },
  "guest.game_mode_solo": { kz: "🤖 Жалғыз", ru: "🤖 Соло", en: "🤖 Solo" },
  "guest.game_bot_difficulty": { kz: "Боттың деңгейі", ru: "Сложность бота", en: "Bot difficulty" },
  "guest.game_difficulty_easy": { kz: "Оңай", ru: "Легко", en: "Easy" },
  "guest.game_difficulty_medium": { kz: "Орташа", ru: "Средне", en: "Medium" },
  "guest.game_difficulty_hard": { kz: "Қиын", ru: "Сложно", en: "Hard" },

  // Игра с другом по ссылке / быстрый матч (InviteFriendButton).
  "guest.game_find_opponent": { kz: "Қарсылас табу", ru: "Найти соперника", en: "Find an opponent" },
  "guest.game_play_with_friend_link": {
    kz: "Досыңмен сілтеме арқылы ойнау",
    ru: "Играть с другом по ссылке",
    en: "Play with a friend by link",
  },
  "guest.game_too_many_rooms": {
    kz: "Ашық бөлмелер тым көп — ескілерін жабыңыз.",
    ru: "Слишком много открытых комнат — закройте старые.",
    en: "Too many open rooms — close the old ones.",
  },
  "guest.game_start_failed": { kz: "Ойынды бастау мүмкін болмады.", ru: "Не удалось начать игру.", en: "Could not start the game." },

  // Шахматы.
  "guest.chess_mate_white": { kz: "Мат! Ақтар жеңді", ru: "Мат! Победили белые", en: "Checkmate! White wins" },
  "guest.chess_mate_black": { kz: "Мат! Қаралар жеңді", ru: "Мат! Победили чёрные", en: "Checkmate! Black wins" },
  "guest.chess_stalemate": { kz: "Пат — тең ойын", ru: "Пат — ничья", en: "Stalemate — draw" },
  "guest.chess_turn_white": { kz: "Ақтардың жүрісі", ru: "Ход белых", en: "White to move" },
  "guest.chess_turn_black": { kz: "Қаралардың жүрісі", ru: "Ход чёрных", en: "Black to move" },
  "guest.chess_check_suffix": { kz: " · шах!", ru: " · шах!", en: " · check!" },
  "guest.chess_solo_hint": {
    kz: "Сіз — ақтар, бот — қаралар. Алдымен фигураны, сосын шаршыны басыңыз.",
    ru: "Вы — белые, бот — чёрные. Тапни фигуру, потом клетку.",
    en: "You're white, the bot is black. Tap a piece, then a square.",
  },

  // Шашки.
  "guest.checkers_bot_won": { kz: "Бот жеңді", ru: "Бот победил", en: "The bot won" },
  "guest.checkers_your_turn_white": { kz: "Сіздің кезегіңіз (ақтар)", ru: "Ваш ход (белые)", en: "Your turn (white)" },
  "guest.checkers_bot_thinking": { kz: "Бот ойлануда…", ru: "Бот думает…", en: "The bot is thinking…" },
  "guest.checkers_solo_hint": {
    kz: "Сіз — ақтар (төменде). Ереже бойынша: алу міндетті.",
    ru: "Вы — белые (снизу). Бьём по правилам: взятие обязательно.",
    en: "You're white (bottom). Standard rules: capturing is mandatory.",
  },

  // Морской бой.
  "guest.sea_fleet_ready": { kz: "Флот орналастырылды ✓", ru: "Флот расставлен ✓", en: "Fleet placed ✓" },
  "guest.sea_placing_ship": {
    kz: "Қоямыз: {len} палубалы ({n}/{total})",
    ru: "Ставим: {len}-палубный ({n}/{total})",
    en: "Placing: {len}-deck ship ({n}/{total})",
  },
  "guest.sea_place_hint": {
    kz: "Шаршыны басыңыз — одан {dir} қойылады. Кемелер бір-біріне тимеуі керек.",
    ru: "Тапни клетку — поставится {dir} от неё. Корабли не должны касаться.",
    en: "Tap a cell — the ship goes {dir} from it. Ships must not touch.",
  },
  "guest.sea_dir_h": { kz: "көлденеңінен", ru: "по горизонтали", en: "horizontally" },
  "guest.sea_dir_v": { kz: "тігінен", ru: "по вертикали", en: "vertically" },
  "guest.sea_horizontal": { kz: "↔ Көлденең", ru: "↔ Горизонталь", en: "↔ Horizontal" },
  "guest.sea_vertical": { kz: "↕ Тік", ru: "↕ Вертикаль", en: "↕ Vertical" },
  "guest.sea_random": { kz: "🎲 Кездейсоқ", ru: "🎲 Случайно", en: "🎲 Random" },
  "guest.sea_undo": { kz: "↩ Кері қайтару", ru: "↩ Отменить", en: "↩ Undo" },
  "guest.sea_clear": { kz: "✕ Тазалау", ru: "✕ Очистить", en: "✕ Clear" },
  "guest.sea_ready_fight": { kz: "Дайын — шайқасқа!", ru: "Готово — в бой!", en: "Ready — to battle!" },
  "guest.sea_win_bot": {
    kz: "🏆 Жеңіс! Боттың бүкіл флоты суға батты",
    ru: "🏆 Победа! Весь флот бота потоплен",
    en: "🏆 Victory! The bot's whole fleet is sunk",
  },
  "guest.sea_lose_bot": { kz: "Бот сіздің флотыңызды суға батырды 😔", ru: "Бот потопил ваш флот 😔", en: "The bot sank your fleet 😔" },
  "guest.sea_your_turn_fire": { kz: "Сіздің кезегіңіз — жауға атыңыз", ru: "Ваш ход — бейте по врагу", en: "Your turn — fire at the enemy" },
  "guest.sea_bot_firing": { kz: "Бот атып жатыр…", ru: "Бот стреляет…", en: "The bot is firing…" },
  "guest.sea_enemy_field": { kz: "🎯 Жаудың алаңы", ru: "🎯 Поле врага", en: "🎯 Enemy field" },
  "guest.sea_your_fleet": { kz: "⚓ Сіздің флотыңыз", ru: "⚓ Ваш флот", en: "⚓ Your fleet" },
  "guest.sea_new_battle": { kz: "↺ Жаңа шайқас", ru: "↺ Новый бой", en: "↺ New battle" },
  "guest.sea_waiting_opponent_fleet": {
    kz: "Қарсылас кемелерін орналастырғанша күтудеміз…",
    ru: "Ждём, пока соперник расставит свои корабли…",
    en: "Waiting for the opponent to place their ships…",
  },
  "guest.sea_players_placing": { kz: "Ойыншылар флотын орналастыруда…", ru: "Игроки расставляют флот…", en: "Players are placing their fleets…" },
  "guest.sea_opponent_field": { kz: "🎯 Қарсыластың алаңы", ru: "🎯 Поле соперника", en: "🎯 Opponent's field" },

  // Крокодил.
  "guest.charades_title": { kz: "Крокодил", ru: "Крокодил", en: "Charades" },
  "guest.charades_intro": {
    kz: "Бір адам сөзді көреді — сөзсіз, дыбыссыз ым-ишарамен көрсетеді. Қалғандары дауыстап табады. {sec} секундта {n} сөз.",
    ru: "Один человек видит слово — показывает жестами без слов и звуков. Остальные угадывают вслух. {n} слов за {sec} секунд.",
    en: "One person sees the word and acts it out — no words, no sounds. The others guess out loud. {n} words in {sec} seconds.",
  },
  "guest.charades_start": { kz: "Раундты бастау →", ru: "Начать раунд →", en: "Start round →" },
  "guest.charades_guessed_n": { kz: "{n} табылды", ru: "{n} угадано", en: "{n} guessed" },
  "guest.charades_result_line": {
    kz: "{skipped} өткізілді · {missed} үлгермеді",
    ru: "{skipped} пропущено · {missed} не успели",
    en: "{skipped} skipped · {missed} ran out of time",
  },
  "guest.charades_again": { kz: "Тағы да", ru: "Ещё раз", en: "Again" },
  "guest.charades_done": { kz: "Дайын", ru: "Готово", en: "Done" },
  "guest.charades_show_word": { kz: "Сөзді көрсету", ru: "Показать слово", en: "Show the word" },
  "guest.charades_skip": { kz: "Өткізу", ru: "Пропустить", en: "Skip" },
  "guest.charades_guessed": { kz: "Таптық", ru: "Угадали", en: "Guessed" },
  "guest.charades_hide": { kz: "Жасыру", ru: "Скрыть", en: "Hide" },

  // Огонь в руках (AR).
  "guest.fire_title": { kz: "🔥 Қолдағы от", ru: "🔥 Огонь в руках", en: "🔥 Fire in hands" },
  "guest.fire_intro": {
    kz: "Алақандарыңыздың арасында атыңыз жазылған от шары жанады. Қолыңызды қоссаңыз — жарқыл болады. Клип жазуға болады.",
    ru: "Между ладонями зажжётся огненный шар с вашим именем. Сведите руки — будет вспышка. Можно записать клип.",
    en: "A fireball with your name lights up between your palms. Bring your hands together for a flash. You can record a clip.",
  },
  "guest.fire_name_label": { kz: "Оттағы атыңыз", ru: "Ваше имя в огне", en: "Your name in fire" },
  "guest.fire_name_placeholder": { kz: "мысалы, Ерболат", ru: "например, Ерболат", en: "e.g. Erbolat" },
  "guest.fire_start": { kz: "Отты тұтату", ru: "Зажечь огонь", en: "Light the fire" },
  "guest.fire_empty_hint_name": {
    kz: "Бос қалдыруға болады — «{name}» болады",
    ru: "Можно оставить пустым — будет «{name}»",
    en: "Leave it empty — «{name}» will be used",
  },
  "guest.fire_empty_hint_word": {
    kz: "Бос қалдыруға болады — «{word}» сөзі болады",
    ru: "Можно оставить пустым — будет слово «{word}»",
    en: "Leave it empty — the word «{word}» will be used",
  },
  "guest.fire_default_word": { kz: "от", ru: "огонь", en: "fire" },
  "guest.fire_show_second_palm": { kz: "Екінші алақаныңызды көрсетіңіз ✋", ru: "Покажите вторую ладонь ✋", en: "Show your second palm ✋" },
  "guest.fire_show_two_palms": {
    kz: "Екі алақаныңызды көрсетіп, оларды қосыңыз 🔥",
    ru: "Покажите две ладони и сведите их вместе 🔥",
    en: "Show both palms and bring them together 🔥",
  },
  "guest.fire_rec_start": { kz: "● Жазу", ru: "● Запись", en: "● Record" },
  "guest.fire_rec_stop": { kz: "■ Тоқтату", ru: "■ Стоп", en: "■ Stop" },
  "guest.fire_camera_loading": { kz: "Камера жүктелуде…", ru: "Загрузка камеры…", en: "Loading the camera…" },
  "guest.fire_camera_unavailable": { kz: "Камера қолжетімсіз", ru: "Камера недоступна", en: "Camera unavailable" },
  "guest.fire_camera_hint": {
    kz: "{err} — браузерде камераға рұқсат беріп, қайталап көріңіз.",
    ru: "{err} — разрешите доступ к камере в браузере и попробуйте снова.",
    en: "{err} — allow camera access in your browser and try again.",
  },
  "guest.fire_rec_unsupported": {
    kz: "Бұл браузер жазуды қолдамайды",
    ru: "Запись не поддерживается этим браузером",
    en: "Recording is not supported by this browser",
  },
  "guest.fire_rec_failed": { kz: "Жазу қолжетімсіз: {err}", ru: "Запись недоступна: {err}", en: "Recording unavailable: {err}" },

  // Игра с другом по ссылке (комната game_rooms, /play/:roomId).
  "guest.room_game_chess": { kz: "Шахмат ♟️", ru: "Шахматы ♟️", en: "Chess ♟️" },
  "guest.room_game_checkers": { kz: "Дойбы ⛀", ru: "Шашки ⛀", en: "Checkers ⛀" },
  "guest.room_game_sea": { kz: "Теңіз шайқасы ⚓", ru: "Морской бой ⚓", en: "Sea battle ⚓" },
  "guest.room_header": { kz: "Досыңмен ойын", ru: "Игра с другом", en: "Game with a friend" },
  "guest.room_taken": { kz: "Бөлме бос емес — екі ойыншы бар.", ru: "Комната уже занята — игроков двое.", en: "The room is full — two players already." },
  "guest.room_loading": { kz: "Бөлме жүктелуде…", ru: "Загрузка комнаты…", en: "Loading the room…" },
  "guest.room_not_found": {
    kz: "Бөлме табылмады немесе жабылып қалған.",
    ru: "Комната не найдена или уже закрыта.",
    en: "The room was not found or is already closed.",
  },
  "guest.room_open_failed": { kz: "Бөлмені ашу мүмкін болмады.", ru: "Не удалось открыть комнату.", en: "Could not open the room." },
  "guest.room_friend_not_joined": { kz: "Досыңыз әлі қосылмады", ru: "Друг ещё не подключился", en: "Your friend hasn't joined yet" },
  "guest.room_game_running": { kz: "Ойын жүріп жатыр", ru: "Игра идёт", en: "The game is on" },
  "guest.room_enter": { kz: "Ойынға кіру", ru: "Войти в игру", en: "Join the game" },
  "guest.room_fleet_invalid": {
    kz: "Флот жарамсыз (кемелер тиіп тұр немесе өлшемдері дұрыс емес).",
    ru: "Флот невалиден (корабли касаются или неверные размеры).",
    en: "Invalid fleet (ships touch or have wrong sizes).",
  },
  "guest.room_fleet_failed": { kz: "Флотты орналастыру мүмкін болмады.", ru: "Не удалось расставить флот.", en: "Could not place the fleet." },
  "guest.room_place_fleet": { kz: "Флотты орналастырыңыз.", ru: "Расставьте флот.", en: "Place your fleet." },
  "guest.room_opp_ready": { kz: "Қарсылас дайын ✓", ru: "Соперник готов ✓", en: "Opponent is ready ✓" },
  "guest.room_opp_placing": { kz: "Қарсылас та орналастырып жатыр.", ru: "Соперник тоже расставляет.", en: "The opponent is placing theirs too." },
  "guest.room_field_opponent": { kz: "Қарсыластың алаңы", ru: "Поле соперника", en: "Opponent's field" },
  "guest.room_field_yours": { kz: "Сіздің алаңыңыз", ru: "Ваше поле", en: "Your field" },
  "guest.room_your_turn_fire": { kz: "Сіздің кезегіңіз — атыңыз", ru: "Ваш ход — стреляйте", en: "Your turn — fire" },
  "guest.room_waiting_friend": {
    kz: "Досты күтудеміз… сілтемені бөлісіңіз",
    ru: "Ждём друга… поделитесь ссылкой",
    en: "Waiting for your friend… share the link",
  },
  "guest.room_turn_of": { kz: "Кезек: {name}", ru: "Ход: {name}", en: "Turn: {name}" },
  "guest.room_share_title": { kz: "Ойнаймыз ба?", ru: "Сыграем?", en: "Fancy a game?" },
  "guest.room_share_text": { kz: "Ойынға кір 🎮", ru: "Заходи в игру 🎮", en: "Join the game 🎮" },
  "guest.room_link_copied": { kz: "Сілтеме көшірілді ✓", ru: "Ссылка скопирована ✓", en: "Link copied ✓" },
  "guest.room_share_link": { kz: "🔗 Сілтемені бөлісу", ru: "🔗 Поделиться ссылкой", en: "🔗 Share the link" },

  // ─── Прочие строки, что оставались русскими для всех (2026-07-26) ────────
  "guest.theme_light": { kz: "Ашық", ru: "Светлая", en: "Light" },
  "guest.theme_dark": { kz: "Күңгірт", ru: "Тёмная", en: "Dark" },
  "guest.theme_auto": { kz: "Авто", ru: "Авто", en: "Auto" },
  "guest.theme_aria": { kz: "Тақырып: {mode}", ru: "Тема: {mode}", en: "Theme: {mode}" },
  "guest.cart_you_mark": { kz: "· сіз", ru: "· вы", en: "· you" },
  "guest.cart_offline_guest": { kz: "📵 офлайн қонақ", ru: "📵 оффлайн-гость", en: "📵 offline guest" },
  "guest.cart_loading": { kz: "Себет жүктелуде…", ru: "Загрузка корзины…", en: "Loading the cart…" },
  "guest.cart_order_sending": { kz: "Тапсырыс жіберілуде…", ru: "Отправляем заказ…", en: "Sending the order…" },
  "guest.cart_order_sent_kitchen": {
    kz: "✅ Тапсырыс ас үйге жіберілді",
    ru: "✅ Заказ отправлен на кухню",
    en: "✅ Order sent to the kitchen",
  },
  "guest.cart_moved_toast": { kz: "📦 Себет көшірілді ({n})", ru: "📦 Корзина перенесена ({n})", en: "📦 Cart moved ({n})" },
  "guest.cart_move_failed": {
    kz: "Себетті көшіру мүмкін болмады: {err}",
    ru: "Не удалось перенести корзину: {err}",
    en: "Could not move the cart: {err}",
  },
  "guest.error_generic_short": { kz: "қате", ru: "ошибка", en: "error" },
  // Последний фолбэк названия заведения (когда у тенанта не заполнено ничего).
  // Подставляется в текст политики — там же, где идёт локализованный документ.
  "guest.venue_fallback": { kz: "Мекеме", ru: "Заведение", en: "The venue" },
  "guest.cafe_word": { kz: "Кафе", ru: "Кафе", en: "Cafe" },
  "guest.kaspi_transfer_payer_placeholder": { kz: "Айгерім М.", ru: "Айгерим М.", en: "Aigerim M." },

  // Сайт доставки (/delivery) — вкладка «Корзина и заказы».
  "guest.catalog_loading": { kz: "Каталог жүктелуде…", ru: "Загружаем каталог…", en: "Loading the catalogue…" },
  "guest.catalog_error": {
    kz: "Санаттарды жүктеу мүмкін болмады",
    ru: "Не удалось загрузить категории",
    en: "Could not load the categories",
  },
  "guest.dev_cart_orders_empty": { kz: "Себет пен тапсырыстар бос", ru: "Корзина и заказы пусты", en: "Cart and orders are empty" },
  "guest.dev_cart_orders_empty_hint": {
    kz: "Мәзірден немесе каталогтан тағам таңдаңыз",
    ru: "Выберите блюда из меню или каталога",
    en: "Pick dishes from the menu or the catalogue",
  },
  "guest.delivery_checkout_cta": { kz: "Жеткізуді рәсімдеу", ru: "Оформить доставку", en: "Place a delivery order" },
  "guest.delivery_orders_title": { kz: "Жеткізу тапсырыстары", ru: "Заказы доставки", en: "Delivery orders" },
  "guest.delivery_order_num": { kz: "Тапсырыс #{id}", ru: "Заказ #{id}", en: "Order #{id}" },
  "guest.delivery_order_hide": { kz: "Тізімнен алып тастау", ru: "Убрать из списка", en: "Remove from the list" },
  "guest.delivery_details": { kz: "Толығырақ →", ru: "Подробнее →", en: "Details →" },
  "guest.delivery_review_cta": { kz: "Жеткізуді бағалау", ru: "Оценить доставку", en: "Rate the delivery" },

  // ─── Waiter cabinet (терминальный редизайн, i18n 2026-06-12) ───
  "waiter.language": {
    kz: "Тіл",
    ru: "Язык",
    en: "Language",
  },
  // ─── Сканер штрихкодов (ревизия, 00196) ───
  "waiter.scan_title": { kz: "Штрихкодты сканерлеу", ru: "Сканировать штрихкод", en: "Scan barcode" },
  "waiter.scan_hint": {
    kz: "Камераны тауардың штрихкодына бағыттаңыз",
    ru: "Наведите камеру на штрихкод товара",
    en: "Point the camera at the product barcode",
  },
  "waiter.scan_manual": { kz: "Қолмен енгізу", ru: "Ввести вручную", en: "Enter manually" },
  "waiter.scan_manual_ph": { kz: "Штрихкод сандармен", ru: "Штрихкод цифрами", en: "Barcode digits" },
  "waiter.scan_find": { kz: "Табу", ru: "Найти", en: "Find" },
  "waiter.scan_no_camera": {
    kz: "Камера қолжетімсіз — штрихкодты қолмен енгізіңіз",
    ru: "Камера недоступна — введите штрихкод вручную",
    en: "Camera unavailable — enter the barcode manually",
  },
  "waiter.scan_not_found": {
    kz: "«{code}» штрихкоды ешбір тауарға байланбаған",
    ru: "Штрихкод «{code}» не привязан ни к одному товару",
    en: "Barcode “{code}” isn't linked to any product",
  },
  "waiter.scan_not_in_list": {
    kz: "Тауар табылды, бірақ тізімде жоқ",
    ru: "Товар найден, но его нет в списке",
    en: "Product found, but it's not in the list",
  },
  // ─── Сканер стола (новый заказ → взять стол по QR) ───
  "waiter.scan_table_btn": { kz: "Үстелді сканерлеу", ru: "Сканировать стол", en: "Scan table" },
  "waiter.scan_table_title": { kz: "Үстел QR-ын сканерлеу", ru: "Сканировать QR стола", en: "Scan table QR" },
  "waiter.scan_table_hint": {
    kz: "Камераны үстелдегі QR-кодқа бағыттаңыз",
    ru: "Наведите камеру на QR-код стола",
    en: "Point the camera at the table QR code",
  },
  "waiter.scan_table_invalid": {
    kz: "QR-кодтан үстелді тану мүмкін болмады",
    ru: "Не удалось распознать стол из QR-кода",
    en: "Couldn't recognize a table from the QR code",
  },
  // ─── Вызов гостей: официант жмёт «Иду» (P5) ───
  "waiter.notif_coming": { kz: "Барамын", ru: "Иду", en: "On my way" },
  "waiter.notif_on_way": { kz: "Бара жатыр", ru: "Идёт", en: "On the way" },
  "waiter.toast_coming": { kz: "Үстелге барамын", ru: "Иду к столу", en: "Heading to the table" },
  // ─── Зарплата (tenants.settings.payday, бухгалтер/админ задаёт) ───
  "waiter.payday_title": { kz: "Жалақы", ru: "Зарплата", en: "Payday" },
  "waiter.payday_on": { kz: "Жалақы: {date}", ru: "Зарплата: {date}", en: "Payday: {date}" },
  "waiter.payday_soon": { kz: "Жақында", ru: "Скоро", en: "Soon" },
  // ─── Меню: шаг выбора категории (P2) ───
  "waiter.menu_pick_category": { kz: "Санатты таңдаңыз", ru: "Выберите категорию", en: "Choose a category" },
  "waiter.menu_dishes_n": {
    kz: "{n} тағам",
    ru: "{n} блюд",
    en: "{n} dishes",
  },
  "waiter.menu_dishes_one": { kz: "{n} тағам", ru: "{n} блюдо", en: "{n} dish" },
  "waiter.menu_dishes_few": { kz: "{n} тағам", ru: "{n} блюда", en: "{n} dishes" },
  "waiter.menu_dishes_many": { kz: "{n} тағам", ru: "{n} блюд", en: "{n} dishes" },
  // ─── Кросс-стол заказ + история кто добавил (P7) ───
  "waiter.cross_table_note": {
    kz: "Бұл — басқа даяшының үстелі. Қосылған тағамдар тарихта сіздің атыңызбен сақталады.",
    ru: "Это стол другого официанта. Добавленные блюда сохранятся в истории под вашим именем.",
    en: "This is another waiter's table. Added dishes are saved in history under your name.",
  },
  "waiter.order_added_by": {
    kz: "Қосқан: {name}",
    ru: "Добавил: {name}",
    en: "Added by {name}",
  },
  "waiter.v2_wip_message": {
    kz: "Әзірлеу үстінде — backend әлі дайын емес (openTableAsWaiter / addMenuItemToSession / transferToCashier). Дизайн дайын, RPC күтудеміз.",
    ru: "В разработке — backend пока не готов (openTableAsWaiter / addMenuItemToSession / transferToCashier). Дизайн готов, ждём RPC.",
    en: "In development — backend isn't ready yet (openTableAsWaiter / addMenuItemToSession / transferToCashier). Design is done, waiting for RPC.",
  },
  "waiter.default_user_name": {
    kz: "Даяшы",
    ru: "Официант",
    en: "Waiter",
  },
  "waiter.toast_table_claimed": {
    kz: "Үстел алынды",
    ru: "Стол взят",
    en: "Table claimed",
  },
  "waiter.toast_table_already_claimed": {
    kz: "Үстелді алып қойды: {name}",
    ru: "Стол уже взял: {name}",
    en: "Table already taken by {name}",
  },
  "waiter.toast_error": {
    kz: "Қате",
    ru: "Ошибка",
    en: "Error",
  },
  "waiter.toast_table_released": {
    kz: "Үстел босатылды",
    ru: "Стол освобождён",
    en: "Table released",
  },
  "waiter.toast_no_active_calls": {
    kz: "Белсенді шақырулар жоқ",
    ru: "Активных вызовов нет",
    en: "No active calls",
  },
  "waiter.toast_at_table": {
    kz: "Сіз үстел қасындасыз",
    ru: "Вы у стола",
    en: "You're at the table",
  },
  "waiter.toast_order_served": {
    kz: "Тапсырыс берілді деп белгіленді",
    ru: "Заказ помечен как поданный",
    en: "Order marked as served",
  },
  "waiter.toast_carrying_to_guests": {
    kz: "Қонақтарға апара жатырмын",
    ru: "Понёс гостям",
    en: "Taking it to the guests",
  },
  "waiter.toast_no_ready_orders": {
    kz: "Дайын тапсырыс жоқ",
    ru: "Готовых заказов нет",
    en: "No ready orders",
  },
  "waiter.toast_table_already_open_claim": {
    kz: "Үстел {table} ашық екен — өзіме аламын",
    ru: "Стол {table} уже открыт — беру себе",
    en: "Table {table} is already open — taking it over",
  },
  "waiter.toast_table_opened": {
    kz: "Үстел {table} ашылды",
    ru: "Стол {table} открыт",
    en: "Table {table} opened",
  },
  "waiter.toast_open_table_failed": {
    kz: "Үстелді ашу мүмкін болмады",
    ru: "Не удалось открыть стол",
    en: "Couldn't open the table",
  },
  "waiter.error_session_not_open": {
    kz: "Сессия ашылмаған",
    ru: "Сессия не открыта",
    en: "Session is not open",
  },
  "waiter.toast_done": {
    kz: "Дайын",
    ru: "Готово",
    en: "Done",
  },
  "waiter.toast_no_free_tables": {
    kz: "Бос үстел жоқ",
    ru: "Свободных столов нет",
    en: "No free tables",
  },
  "waiter.toast_table_transferred": {
    kz: "Үстел берілді",
    ru: "Стол передан",
    en: "Table transferred",
  },
  "waiter.toast_table_already_open_join": {
    kz: "Үстел {table} ашық екен — қосыламын",
    ru: "Стол {table} уже открыт — присоединяюсь",
    en: "Table {table} is already open — joining",
  },
  "waiter.toast_sent_to_kitchen": {
    kz: "Асүйге жіберілді: {n} позиция",
    ru: "Отправлено на кухню: {n} позиций",
    en: "Sent to the kitchen: {n} items",
  },
  "waiter.toast_send_order_failed": {
    kz: "Тапсырысты жіберу мүмкін болмады",
    ru: "Не удалось отправить заказ",
    en: "Couldn't send the order",
  },
  "waiter.toast_transferred_to_cashier": {
    kz: "Кассирге берілді — үстелді касса тізімінен іздеңіз",
    ru: "Передано кассиру — ищите стол в кассовом списке",
    en: "Sent to the cashier — look for the table in the cashier list",
  },
  "waiter.toast_session_not_found": {
    kz: "Сессия табылмады",
    ru: "Сессия не найдена",
    en: "Session not found",
  },
  "waiter.toast_split_sent": {
    kz: "Шотты бөлу кассаға жіберілді",
    ru: "Разделение счёта передано кассе",
    en: "Bill split sent to the cashier",
  },
  "waiter.toast_split_save_failed": {
    kz: "Бөлуді сақтау мүмкін болмады",
    ru: "Не удалось сохранить разделение",
    en: "Couldn't save the split",
  },
  "waiter.geo_blocked_title": {
    kz: "Сіз кафеде емессіз",
    ru: "Вы не в кафе",
    en: "You're not at the café",
  },
  "waiter.geo_blocked_desc": {
    kz: "Даяшы қосымшасына кіру геолокация бойынша шектелген.",
    ru: "Доступ к приложению официанта ограничен по геолокации.",
    en: "Access to the waiter app is restricted by geolocation.",
  },
  "waiter.geo_distance_info": {
    kz: "Қашықтық: {distance}м · Рұқсат: {radius}м",
    ru: "Расстояние: {distance}м · Разрешено: {radius}м",
    en: "Distance: {distance}m · Allowed: {radius}m",
  },
  "waiter.geo_recheck": {
    kz: "Қайта тексеру",
    ru: "Проверить снова",
    en: "Check again",
  },
  "waiter.geo_unavailable_title": {
    kz: "Геолокация өшірілген",
    ru: "Геолокация выключена",
    en: "Location is off",
  },
  "waiter.geo_unavailable_desc": {
    kz: "Кабинетке кіру үшін құрылғыда және браузерде геолокацияны қосыңыз.",
    ru: "Включите геолокацию на устройстве и в браузере, чтобы открыть кабинет.",
    en: "Enable location on your device and in the browser to open the cabinet.",
  },
  "waiter.app_label_long": {
    kz: "даяшы қосымшасы",
    ru: "приложение официанта",
    en: "the waiter app",
  },
  "waiter.loading": {
    kz: "Жүктелуде...",
    ru: "Загрузка...",
    en: "Loading...",
  },
  "waiter.audio_notifications": {
    kz: "Дыбыстық хабарламалар",
    ru: "Звуковые уведомления",
    en: "Sound notifications",
  },
  "waiter.audio_state_on": {
    kz: "Қосулы",
    ru: "Включены",
    en: "Enabled",
  },
  "waiter.audio_state_off": {
    kz: "Өшірулі",
    ru: "Выключены",
    en: "Disabled",
  },
  "waiter.audio_hint": {
    kz: "{state} · осы құрылғыдағы барлық app-қа қолданылады",
    ru: "{state} · применяется ко всем app на этом устройстве",
    en: "{state} · applies to all apps on this device",
  },
  "waiter.geo_ok": {
    kz: "Орнында",
    ru: "На месте",
    en: "On site",
  },
  "waiter.geo_ok_hint": {
    kz: "Сіз кафе аумағындасыз — барлық әрекет қолжетімді.",
    ru: "Вы в зоне кафе — все действия доступны.",
    en: "You are in the café zone — all actions are available.",
  },
  "waiter.geo_outside": {
    kz: "Кафеден тыс",
    ru: "Вне кафе",
    en: "Outside café",
  },
  "waiter.geo_outside_hint": {
    kz: "Үйде статистиканы көруге болады. Үстел алу — тек кафеде.",
    ru: "Дома можно смотреть статистику. Брать столы — только в кафе.",
    en: "You can view stats from home. Taking tables — only at the café.",
  },
  "waiter.geo_checking": {
    kz: "Тексерудеміз…",
    ru: "Проверяем…",
    en: "Checking…",
  },
  "waiter.geo_checking_hint": {
    kz: "Орналасқан жеріңізді анықтап жатырмыз.",
    ru: "Определяем вашу позицию.",
    en: "Determining your position.",
  },
  "waiter.geo_denied": {
    kz: "Тыйым салынған",
    ru: "Запрещено",
    en: "Denied",
  },
  "waiter.geo_denied_hint": {
    kz: "Браузер баптауларында геолокацияға рұқсат беріңіз.",
    ru: "Разрешите геолокацию в настройках браузера.",
    en: "Allow geolocation in your browser settings.",
  },
  "waiter.geo_unavailable": {
    kz: "Қолжетімсіз",
    ru: "Недоступно",
    en: "Unavailable",
  },
  "waiter.geo_unavailable_hint": {
    kz: "Браузер орналасқан жерді анықтай алмайды.",
    ru: "Браузер не может определить позицию.",
    en: "The browser cannot determine your position.",
  },
  "waiter.geo_not_configured": {
    kz: "Тексеріссіз",
    ru: "Без проверки",
    en: "No check",
  },
  "waiter.geo_not_configured_hint": {
    kz: "Кафе баптауларында геотексеру қосылмаған.",
    ru: "В настройках кафе геопроверка не включена.",
    en: "Geo check is not enabled in the café settings.",
  },
  "waiter.geo_card_title": {
    kz: "Геолокация · {status}",
    ru: "Геолокация · {status}",
    en: "Geolocation · {status}",
  },
  "waiter.geo_distance": {
    kz: "Кафеге дейінгі қашықтық: {distance}",
    ru: "Расстояние до кафе: {distance}",
    en: "Distance to café: {distance}",
  },
  "waiter.geo_radius": {
    kz: "(радиус {n} м)",
    ru: "(радиус {n} м)",
    en: "(radius {n} m)",
  },
  "waiter.unit_km": {
    kz: "{n} км",
    ru: "{n} км",
    en: "{n} km",
  },
  "waiter.unit_m": {
    kz: "{n} м",
    ru: "{n} м",
    en: "{n} m",
  },
  "waiter.geo_recheck_2": {
    kz: "Орынды тексеру",
    ru: "Проверить позицию",
    en: "Check position",
  },
  "waiter.today": {
    kz: "Бүгін",
    ru: "Сегодня",
    en: "Today",
  },
  "waiter.stat_tables": {
    kz: "Үстел",
    ru: "Столов",
    en: "Tables",
  },
  "waiter.stat_revenue": {
    kz: "Түсім",
    ru: "Выручка",
    en: "Revenue",
  },
  "waiter.stat_tips": {
    kz: "Шайпұл",
    ru: "Чаевые",
    en: "Tips",
  },
  "waiter.stat_shift": {
    kz: "Ауысым",
    ru: "Смена",
    en: "Shift",
  },
  "waiter.shift_since": {
    kz: "{time}-ден бастап",
    ru: "с {time}",
    en: "since {time}",
  },
  "waiter.history": {
    kz: "Тарих",
    ru: "История",
    en: "History",
  },
  "waiter.orders_count_one": {
    kz: "{n} тапсырыс",
    ru: "{n} заказ",
    en: "{n} order",
  },
  "waiter.orders_count_few": {
    kz: "{n} тапсырыс",
    ru: "{n} заказа",
    en: "{n} orders",
  },
  "waiter.orders_count_many": {
    kz: "{n} тапсырыс",
    ru: "{n} заказов",
    en: "{n} orders",
  },
  "waiter.salary": {
    kz: "Жалақы",
    ru: "Зарплата",
    en: "Salary",
  },
  "waiter.coming_soon": {
    kz: "Жақында",
    ru: "Скоро",
    en: "Soon",
  },
  "waiter.orders_today_title": {
    kz: "Бүгінгі тапсырыстар",
    ru: "Заказы за сегодня",
    en: "Today's orders",
  },
  "waiter.orders_today_empty": {
    kz: "Бүгін әзірге тапсырыс болған жоқ",
    ru: "Сегодня заказов ещё не было",
    en: "No orders yet today",
  },
  "waiter.round_n": {
    kz: "раунд {n}",
    ru: "раунд {n}",
    en: "round {n}",
  },
  "waiter.order_cancelled_suffix": {
    kz: "болдырылмады",
    ru: "отменён",
    en: "cancelled",
  },
  "waiter.status_title": {
    kz: "Статус",
    ru: "Статус",
    en: "Status",
  },
  "waiter.availability_free": {
    kz: "Бос",
    ru: "Свободен",
    en: "Free",
  },
  "waiter.availability_busy": {
    kz: "Бос емес",
    ru: "Занят",
    en: "Busy",
  },
  "waiter.availability_dnd": {
    kz: "Мазаламаңыз",
    ru: "Не беспокоить",
    en: "Do not disturb",
  },
  "waiter.push_description": {
    kz: "Қонақ шақырулары, жаңа үстелдер және дайын тапсырыстар туралы хабарламалар.",
    ru: "Уведомления о вызовах гостей, новых столах и готовых заказах.",
    en: "Notifications about guest calls, new tables and ready orders.",
  },
  "waiter.signout_confirm": {
    kz: "Даяшы қосымшасынан шығасыз ба?",
    ru: "Выйти из приложения официанта?",
    en: "Sign out of the waiter app?",
  },
  "waiter.ai_send": {
    kz: "Жіберу",
    ru: "Отправить",
    en: "Send",
  },
  "waiter.signout": {
    kz: "🚪 Шығу",
    ru: "🚪 Выйти",
    en: "🚪 Sign out",
  },
  "waiter.hud_mine": {
    kz: "Менікі",
    ru: "Мои",
    en: "Mine",
  },
  "waiter.hud_new": {
    kz: "Жаңа",
    ru: "Новые",
    en: "New",
  },
  "waiter.hud_urgent": {
    kz: "Шұғыл",
    ru: "Срочно",
    en: "Urgent",
  },
  "waiter.zone_all": {
    kz: "Барлығы",
    ru: "Все",
    en: "All",
  },
  "waiter.revision_banner_title": {
    kz: "🟢 Қойма түгендеуі жүріп жатыр",
    ru: "🟢 Идёт ревизия склада",
    en: "🟢 Stock count in progress",
  },
  "waiter.revision_counted": {
    kz: "Қалдықтарды санауға көмектес · {n} саналды",
    ru: "Помоги посчитать остатки · посчитано {n}",
    en: "Help count the stock · {n} counted",
  },
  "waiter.revision_started_by": {
    kz: "· {name} бастады",
    ru: "· начал {name}",
    en: "· started by {name}",
  },
  "waiter.new_tables_waiting_one": {
    kz: "{n} жаңа үстел күтіп тұр!",
    ru: "{n} новый стол ждёт!",
    en: "{n} new table is waiting!",
  },
  "waiter.new_tables_waiting_many": {
    kz: "{n} жаңа үстел күтіп тұр!",
    ru: "{n} новых стола ждут!",
    en: "{n} new tables are waiting!",
  },
  "waiter.first_to_accept_serves": {
    kz: "Бірінші қабылдаған — қызмет көрсетеді",
    ru: "Первый кто примет — обслуживает",
    en: "First to accept serves the table",
  },
  "waiter.take": {
    kz: "Алу",
    ru: "Взять",
    en: "Take",
  },
  "waiter.floor_map": {
    kz: "Зал картасы",
    ru: "Карта зала",
    en: "Floor map",
  },
  "waiter.pick_my_zones": {
    kz: "Менің залдарымды таңдау",
    ru: "Выбрать мои залы",
    en: "Choose my zones",
  },
  "waiter.my_zones": {
    kz: "Менің залдарым",
    ru: "Мои залы",
    en: "My zones",
  },
  "waiter.zone_empty": {
    kz: "Бұл аймақта үстел жоқ.",
    ru: "В этой зоне столов нет.",
    en: "No tables in this zone.",
  },
  "waiter.zone_empty_hint": {
    kz: "Төмендегі «+ Жаңа тапсырыс» батырмасын бас.",
    ru: "Нажми «+ Новый заказ» внизу.",
    en: "Tap \"+ New order\" below.",
  },
  "waiter.new_order": {
    kz: "Жаңа тапсырыс",
    ru: "Новый заказ",
    en: "New order",
  },
  "waiter.orders_title": {
    kz: "Белсенді тапсырыстар",
    ru: "Активные заказы",
    en: "Active orders",
  },
  "waiter.orders_filter_new": {
    kz: "🆕 Жаңа",
    ru: "🆕 Новые",
    en: "🆕 New",
  },
  "waiter.orders_filter_active": {
    kz: "Белсенді",
    ru: "Активные",
    en: "Active",
  },
  "waiter.orders_filter_ready": {
    kz: "Дайын",
    ru: "Готовые",
    en: "Ready",
  },
  "waiter.orders_round": {
    kz: "{n}-тапсырыс",
    ru: "Заказ {n}",
    en: "Order {n}",
  },
  "waiter.orders_search_placeholder": {
    kz: "Тағам немесе үстел бойынша іздеу…",
    ru: "Поиск по блюду или столу…",
    en: "Search by dish or table…",
  },
  "waiter.orders_empty": {
    kz: "Белсенді тапсырыстар жоқ",
    ru: "Активных заказов нет",
    en: "No active orders",
  },
  "waiter.orders_min_ago": {
    kz: "⏱️ {n}мин бұрын",
    ru: "⏱️ {n}мин назад",
    en: "⏱️ {n}min ago",
  },
  "waiter.orders_kitchen_ready": {
    kz: "Асүй: тапсырыс дайын!",
    ru: "Кухня: заказ готов!",
    en: "Kitchen: order is ready!",
  },
  "waiter.orders_cooking": {
    kz: "Асүйде дайындалып жатыр",
    ru: "Готовится на кухне",
    en: "Cooking in the kitchen",
  },
  "waiter.orders_received": {
    kz: "Қабылданды, аспазды күтуде",
    ru: "Принят, ожидает повара",
    en: "Received, waiting for the cook",
  },
  "waiter.orders_dishes_count": {
    kz: "Тағамдар ({n})",
    ru: "Блюда ({n})",
    en: "Dishes ({n})",
  },
  "waiter.orders_line_shared": {
    kz: "[ортақ]",
    ru: "[общее]",
    en: "[shared]",
  },
  "waiter.orders_pickup_btn": {
    kz: "✅ Қонақтарға апардым",
    ru: "✅ Понёс гостям",
    en: "✅ Taking to guests",
  },
  "waiter.orders_wait_kitchen": {
    kz: "Асүй «Дайын» деп белгілегенше күтіңіз",
    ru: "Дождитесь когда кухня переведёт в «Готов»",
    en: "Wait until the kitchen marks it “Ready”",
  },
  "waiter.menu_temporarily_unavailable": {
    kz: "Уақытша қолжетімсіз",
    ru: "Временно недоступно",
    en: "Temporarily unavailable",
  },
  "waiter.menu_voice_unsupported": {
    kz: "Дауыспен енгізуді браузеріңіз қолдамайды",
    ru: "Голосовой ввод не поддерживается в вашем браузере",
    en: "Voice input is not supported in your browser",
  },
  "waiter.back": {
    kz: "Артқа",
    ru: "Назад",
    en: "Back",
  },
  "waiter.menu_title": {
    kz: "{label} · Мәзір",
    ru: "{label} · Меню",
    en: "{label} · Menu",
  },
  "waiter.menu_subtitle_guest_one": {
    kz: "{n} қонақ · тағам қосу",
    ru: "{n} гость · добавить блюда",
    en: "{n} guest · add dishes",
  },
  "waiter.menu_subtitle_guests_many": {
    kz: "{n} қонақ · тағам қосу",
    ru: "{n} гостей · добавить блюда",
    en: "{n} guests · add dishes",
  },
  "waiter.menu_search_placeholder": {
    kz: "Тағам іздеу...",
    ru: "Поиск блюда...",
    en: "Search dishes...",
  },
  "waiter.clear": {
    kz: "Тазалау",
    ru: "Очистить",
    en: "Clear",
  },
  "waiter.menu_voice_search": {
    kz: "Дауыспен іздеу",
    ru: "Голосовой поиск",
    en: "Voice search",
  },
  "waiter.menu_nothing_found": {
    kz: "Ештеңе табылмады",
    ru: "Ничего не найдено",
    en: "Nothing found",
  },
  "waiter.menu_sending": {
    kz: "Жіберілуде…",
    ru: "Отправляем…",
    en: "Sending…",
  },
  "waiter.menu_send_to_kitchen": {
    kz: "Асүйге жіберу",
    ru: "Отправить на кухню",
    en: "Send to kitchen",
  },
  "waiter.review_order": {
    kz: "Тапсырысты тексеру",
    ru: "Проверить заказ",
    en: "Review order",
  },
  "waiter.menu_exit_lose_cart_confirm": {
    kz: "Себет әлі асүйге жіберілген жоқ. Шығып, себетті тазартамыз ба?",
    ru: "Корзина ещё не отправлена на кухню. Выйти и очистить её?",
    en: "The cart hasn't been sent to the kitchen yet. Leave and discard it?",
  },
  "waiter.menu_load_failed": {
    kz: "Мәзір жүктелмеді. Интернетті тексеріп, қайталап көріңіз.",
    ru: "Меню не загрузилось. Проверьте интернет и повторите.",
    en: "The menu failed to load. Check your connection and retry.",
  },
  "waiter.menu_retry": {
    kz: "Қайталау",
    ru: "Повторить",
    en: "Retry",
  },
  "waiter.show_qr_to_cashier": {
    kz: "QR-ды кассирге көрсету",
    ru: "Показать QR кассиру",
    en: "Show QR to cashier",
  },
  "waiter.show_to_cashier": {
    kz: "Кассирге көрсету",
    ru: "Показать кассиру",
    en: "Show to cashier",
  },
  "waiter.all_categories": {
    kz: "Барлық санаттар",
    ru: "Все категории",
    en: "All categories",
  },
  "waiter.dish_status_received": {
    kz: "Қабылданды",
    ru: "Принят",
    en: "Received",
  },
  "waiter.dish_status_cooking": {
    kz: "Дайындалуда",
    ru: "Готовится",
    en: "Cooking",
  },
  "waiter.dish_status_ready": {
    kz: "Дайын!",
    ru: "Готов!",
    en: "Ready!",
  },
  "waiter.dish_status_served": {
    kz: "Берілді",
    ru: "Подан",
    en: "Served",
  },
  "waiter.cancel_dish": {
    kz: "Тағамды алып тастау",
    ru: "Убрать блюдо",
    en: "Remove dish",
  },
  "waiter.cancel_dish_confirm": {
    kz: "Тағамды тапсырыстан алып тастау керек пе?",
    ru: "Убрать блюдо из заказа?",
    en: "Remove this dish from the order?",
  },
  "waiter.cancel_dish_confirm_yes": {
    kz: "Алып тастау",
    ru: "Убрать",
    en: "Remove",
  },
  "waiter.cancel_dish_confirm_no": {
    kz: "Қалдыру",
    ru: "Оставить",
    en: "Keep",
  },
  "waiter.cancel_dish_too_late": {
    kz: "Ас үй тағамды қабылдап қойды — алып тастау мүмкін емес",
    ru: "Кухня уже приняла блюдо — отменить нельзя",
    en: "The kitchen already accepted the dish — it can no longer be removed",
  },
  "waiter.cancel_dish_error": {
    kz: "Тағамды алып тастау мүмкін болмады. Экранды жаңартыңыз.",
    ru: "Не удалось убрать блюдо. Обновите экран.",
    en: "Couldn’t remove the dish. Refresh the screen.",
  },
  "waiter.order_meta": {
    kz: "⏱ {min}м · {n} поз.",
    ru: "⏱ {min}м · {n} поз.",
    en: "⏱ {min}m · {n} items",
  },
  "waiter.waiting_alert": {
    kz: "Қонақтар {min} мин күтіп отыр. Барып ескерт.",
    ru: "Гости ждут {min} мин. Подойди и предупреди.",
    en: "Guests have been waiting {min} min. Go and let them know.",
  },
  "waiter.course_1": {
    kz: "1️⃣ Бірінші беріліс",
    ru: "1️⃣ Первая подача",
    en: "1️⃣ First course",
  },
  "waiter.course_2": {
    kz: "2️⃣ Екінші беріліс",
    ru: "2️⃣ Вторая подача",
    en: "2️⃣ Second course",
  },
  "waiter.course_3": {
    kz: "3️⃣ Десерт / сусындар",
    ru: "3️⃣ Десерт / напитки",
    en: "3️⃣ Dessert / drinks",
  },
  "waiter.order_empty": {
    kz: "Тапсырыс әзірге бос",
    ru: "Заказ пока пустой",
    en: "No items in the order yet",
  },
  "waiter.order_empty_hint": {
    kz: "Төмендегі «+ Қосу» батырмасын бас",
    ru: "Нажми «+ Добавить» внизу",
    en: "Tap “+ Add” below",
  },
  "waiter.subtotal": {
    kz: "Тапсырыс сомасы",
    ru: "Сумма заказа",
    en: "Subtotal",
  },
  "waiter.service_charge": {
    kz: "Қызмет ақысы ({percent}%)",
    ru: "Обслуживание ({percent}%)",
    en: "Service ({percent}%)",
  },
  "waiter.total": {
    kz: "Барлығы",
    ru: "Итого",
    en: "Total",
  },
  "waiter.order_total_hint": {
    kz: "Қызмет ақысы мен жеңілдіктермен қорытынды соманы касса есептейді.",
    ru: "Сервисный сбор и скидки — итог посчитает касса при оплате.",
    en: "Service charge and discounts — the cashier computes the final total.",
  },
  "waiter.add": {
    kz: "Қосу",
    ru: "Добавить",
    en: "Add",
  },
  "waiter.split": {
    kz: "Бөлу",
    ru: "Разделить",
    en: "Split",
  },
  "waiter.to_cashier": {
    kz: "Кассаға",
    ru: "Кассе",
    en: "To cashier",
  },
  "waiter.new_order_step1": {
    kz: "1-қадам / 2 · үстелді таңдаңыз",
    ru: "Шаг 1 из 2 · выберите стол",
    en: "Step 1 of 2 · choose a table",
  },
  "waiter.select_zone": {
    kz: "Залды таңдаңыз",
    ru: "Выберите зал",
    en: "Choose a hall",
  },
  "waiter.zone_back": {
    kz: "Барлық залдар",
    ru: "Все залы",
    en: "All halls",
  },
  "waiter.guests_count": {
    kz: "Қонақ саны",
    ru: "Количество гостей",
    en: "Number of guests",
  },
  "waiter.guests_fewer": {
    kz: "Азайту",
    ru: "Меньше",
    en: "Fewer",
  },
  "waiter.guests_more": {
    kz: "Көбейту",
    ru: "Больше",
    en: "More",
  },
  "waiter.guest_one": {
    kz: "қонақ",
    ru: "гость",
    en: "guest",
  },
  "waiter.guest_few": {
    kz: "қонақ",
    ru: "гостя",
    en: "guests",
  },
  "waiter.guest_many": {
    kz: "қонақ",
    ru: "гостей",
    en: "guests",
  },
  "waiter.free_tables_n": {
    kz: "Бос үстелдер ({n})",
    ru: "Свободные столы ({n})",
    en: "Free tables ({n})",
  },
  "waiter.no_free_tables_for_n": {
    kz: "{n} қонаққа бос үстел жоқ",
    ru: "Нет свободных столов на {n} гостей",
    en: "No free tables for {n} guests",
  },
  "waiter.combine_small_tables_hint": {
    kz: "Бірнеше шағын үстелді біріктіріңіз",
    ru: "Объедините несколько малых столов",
    en: "Combine several small tables",
  },
  "waiter.small_tables_title": {
    kz: "Шағын үстелдер (біріктіру керек)",
    ru: "Малые столы (нужно объединить)",
    en: "Small tables (need combining)",
  },
  "waiter.open_table_cta": {
    kz: "Үстелді ашу →",
    ru: "Открыть стол →",
    en: "Open table →",
  },
  "waiter.notif_title": {
    kz: "Хабарламалар",
    ru: "Уведомления",
    en: "Notifications",
  },
  "waiter.notif_kind_waiter": {
    kz: "Даяшыны шақыру",
    ru: "Вызов официанта",
    en: "Waiter call",
  },
  "waiter.notif_kind_admin": {
    kz: "Әкімшіні шақыру",
    ru: "Вызов админа",
    en: "Admin call",
  },
  "waiter.notif_kind_bill": {
    kz: "Шот сұрауы",
    ru: "Просьба о счёте",
    en: "Bill request",
  },
  "waiter.notif_calls": {
    kz: "Шақырулар",
    ru: "Вызовы",
    en: "Calls",
  },
  "waiter.notif_calls_active": {
    kz: "· {n} белсенді",
    ru: "· {n} активны",
    en: "· {n} active",
  },
  "waiter.notif_min_ago": {
    kz: "{n}мин бұрын",
    ru: "{n}мин назад",
    en: "{n}min ago",
  },
  "waiter.notif_resolve_done": {
    kz: "✓ Дайын",
    ru: "✓ Готово",
    en: "✓ Done",
  },
  "waiter.notif_shift_feed": {
    kz: "Ауысым таспасы",
    ru: "Лента смены",
    en: "Shift feed",
  },
  "waiter.unit_g": {
    kz: "г",
    ru: "г",
    en: "g",
  },
  "waiter.unit_kg": {
    kz: "кг",
    ru: "кг",
    en: "kg",
  },
  "waiter.unit_ml": {
    kz: "мл",
    ru: "мл",
    en: "ml",
  },
  "waiter.unit_l": {
    kz: "л",
    ru: "л",
    en: "l",
  },
  "waiter.unit_pcs": {
    kz: "дана",
    ru: "шт",
    en: "pcs",
  },
  "waiter.error_load_failed": {
    kz: "Жүктеу мүмкін болмады",
    ru: "Не удалось загрузить",
    en: "Failed to load",
  },
  "waiter.error_save_failed": {
    kz: "Сақтау мүмкін болмады",
    ru: "Не удалось сохранить",
    en: "Failed to save",
  },
  "waiter.you": {
    kz: "Сіз",
    ru: "Вы",
    en: "You",
  },
  "waiter.inventory_title": {
    kz: "Қойма түгендеуі",
    ru: "Ревизия склада",
    en: "Stock count",
  },
  "waiter.inventory_not_active": {
    kz: "Түгендеу жүріп жатқан жоқ",
    ru: "Ревизия не идёт",
    en: "No stock count in progress",
  },
  "waiter.inventory_not_active_hint": {
    kz: "Қоймашы әлі түгендеуді бастаған жоқ. Бастаған кезде — санауға арналған ингредиенттер тізімі осында шығады.",
    ru: "Кладовщик пока не начал ревизию. Когда начнёт — здесь появится список ингредиентов для подсчёта.",
    en: "The storekeeper hasn't started a count yet. Once it starts, the list of ingredients to count will appear here.",
  },
  "waiter.back_to_home": {
    kz: "Басты бетке оралу",
    ru: "Вернуться на главную",
    en: "Back to home",
  },
  "waiter.inventory_help_title": {
    kz: "Қоймаға көмек",
    ru: "Помощь со складом",
    en: "Stock count help",
  },
  "waiter.inventory_active": {
    kz: "Түгендеу жүріп жатыр",
    ru: "Ревизия идёт",
    en: "Count in progress",
  },
  "waiter.inventory_started_by": {
    kz: "Бастаған: {name} · {total} ішінен {done} саналды",
    ru: "Начал: {name} · посчитано {done} из {total}",
    en: "Started by: {name} · {done} of {total} counted",
  },
  "waiter.inventory_in_system": {
    kz: "Жүйеде: {qty} {unit}",
    ru: "В системе: {qty} {unit}",
    en: "In system: {qty} {unit}",
  },
  "waiter.inventory_counted_by": {
    kz: "✓ {name} санады: {qty} {unit}",
    ru: "✓ {name} посчитал: {qty} {unit}",
    en: "✓ {name} counted: {qty} {unit}",
  },
  "waiter.inventory_recheck_placeholder": {
    kz: "Қайта тексересіз бе?",
    ru: "Перепроверить?",
    en: "Recheck?",
  },
  "waiter.inventory_qty_placeholder": {
    kz: "Нақты қанша?",
    ru: "Сколько по факту?",
    en: "Actual amount?",
  },
  "waiter.save": {
    kz: "Сақтау",
    ru: "Сохранить",
    en: "Save",
  },
  "waiter.inventory_footer_hint": {
    kz: "ℹ️ Түгендеуді тек қоймашы немесе администратор жаба алады. Сенің санағандарың автоматты түрде сақталады және түгендеу жабылған кезде қолданылады.",
    ru: "ℹ️ Закрыть ревизию может только кладовщик или администратор. Твои подсчёты сохраняются автоматически и применятся когда ревизию закроют.",
    en: "ℹ️ Only the storekeeper or an admin can close the count. Your counts are saved automatically and will be applied when the count is closed.",
  },
  "waiter.close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "waiter.stat_opened": {
    kz: "Ашылды",
    ru: "Открыт",
    en: "Opened",
  },
  "waiter.stat_active": {
    kz: "Белсенді",
    ru: "Активно",
    en: "Active",
  },
  "waiter.stat_active_min": {
    kz: "{n}мин",
    ru: "{n}м",
    en: "{n}m",
  },
  "waiter.stat_total": {
    kz: "Сома",
    ru: "Сумма",
    en: "Total",
  },
  "waiter.btn_menu": {
    kz: "Тағам қосу",
    ru: "Добавить блюдо",
    en: "Add dish",
  },
  "waiter.btn_detail": {
    kz: "Толығырақ",
    ru: "Детально",
    en: "Details",
  },
  "waiter.guests_count_2": {
    kz: "Қонақтар ({n})",
    ru: "Гости ({n})",
    en: "Guests ({n})",
  },
  // Плюрализация мест — через pluralCategory (apps/waiter/src/lib/plural.ts):
  // «1 место / 4 места / 6 мест» (QA 2026-08-05: было «4 мест»).
  "waiter.seats_one": {
    kz: "{n} орын",
    ru: "{n} место",
    en: "{n} seat",
  },
  "waiter.seats_few": {
    kz: "{n} орын",
    ru: "{n} места",
    en: "{n} seats",
  },
  "waiter.seats_many": {
    kz: "{n} орын",
    ru: "{n} мест",
    en: "{n} seats",
  },
  "waiter.actions": {
    kz: "Әрекеттер",
    ru: "Действия",
    en: "Actions",
  },
  "waiter.current_order": {
    kz: "Ағымдағы тапсырыс",
    ru: "Текущий заказ",
    en: "Current order",
  },
  "waiter.full_order": {
    kz: "Толық тапсырыс",
    ru: "Весь заказ",
    en: "Full order",
  },
  "waiter.guests_empty": {
    kz: "Қонақтар белгіленбеген",
    ru: "Гости не отмечены",
    en: "No guests checked in",
  },
  "waiter.guest_no_name": {
    kz: "Атаусыз",
    ru: "Без имени",
    en: "No name",
  },
  "waiter.guest_name_placeholder": {
    kz: "Қонақтың аты",
    ru: "Имя гостя",
    en: "Guest name",
  },
  "waiter.guest_add_ok": {
    kz: "Ок",
    ru: "Ок",
    en: "OK",
  },
  "waiter.cart_guests_count": {
    kz: "Қонақтар себеті ({n})",
    ru: "Корзина гостей ({n})",
    en: "Guests' cart ({n})",
  },
  "waiter.order_ready_kitchen": {
    kz: "Тапсырыс асүйде дайын!",
    ru: "Заказ готов на кухне!",
    en: "Order is ready in the kitchen!",
  },
  "waiter.btn_pickup": {
    kz: "Алып кеттім",
    ru: "Понёс",
    en: "Picked up",
  },
  "waiter.btn_at_table": {
    kz: "🔔 Мен үстелдемін",
    ru: "🔔 Я у стола",
    en: "🔔 I'm at the table",
  },
  "waiter.btn_already_served": {
    kz: "✅ Беріп қойдым",
    ru: "✅ Уже подал",
    en: "✅ Already served",
  },
  "waiter.btn_claim_table": {
    kz: "Үстелді алу",
    ru: "Взять стол",
    en: "Claim table",
  },
  "waiter.btn_transfer": {
    kz: "↔ Беру",
    ru: "↔ Передать",
    en: "↔ Transfer",
  },
  "waiter.btn_release": {
    kz: "Босату",
    ru: "Освободить",
    en: "Release",
  },
  "waiter.btn_release_confirm": {
    kz: "Шынымен босату?",
    ru: "Точно освободить?",
    en: "Release for sure?",
  },
  "waiter.popup_blocked": {
    kz: "Браузер жаңа терезені бөгеді. Рұқсат етіңіз.",
    ru: "Браузер заблокировал окно. Разрешите всплывающие окна.",
    en: "Browser blocked the window. Allow pop-ups.",
  },
  "waiter.served_by_other": {
    kz: "Басқа даяшы қызмет көрсетуде",
    ru: "Обслуживается другим официантом",
    en: "Served by another waiter",
  },
  "waiter.open_guest_ui": {
    kz: "Үстелдің қонақ интерфейсін ашу",
    ru: "Открыть гостевой UI стола",
    en: "Open table's guest UI",
  },
  "waiter.table_free_hint": {
    kz: "Үстел бос. Оны қонақтарға ашыңыз — немесе олар QR-ды өздері сканерлейді.",
    ru: "Стол свободен. Откройте его для гостей — или они сами отсканируют QR.",
    en: "Table is free. Open it for guests — or they can scan the QR themselves.",
  },
  "waiter.btn_open_table_manual": {
    kz: "Үстелді қолмен ашу",
    ru: "Открыть стол вручную",
    en: "Open table manually",
  },
  "waiter.elapsed_hm": {
    kz: "{h}сағ {m}мин",
    ru: "{h}ч {m}м",
    en: "{h}h {m}m",
  },
  "waiter.elapsed_m": {
    kz: "{m}мин",
    ru: "{m}м",
    en: "{m}m",
  },
  "waiter.owner_mine": {
    kz: "сіздікі",
    ru: "ваш",
    en: "yours",
  },
  "waiter.owner_other": {
    kz: "басқанікі",
    ru: "чужой",
    en: "other's",
  },
  "waiter.owner_pool": {
    kz: "пулда",
    ru: "в пуле",
    en: "in pool",
  },
  "waiter.table_free": {
    kz: "бос",
    ru: "свободен",
    en: "free",
  },
  "waiter.swipe_transfer": {
    kz: "Беру",
    ru: "Передать",
    en: "Transfer",
  },
  "waiter.ai_title_waiter": {
    kz: "🤝 Даяшы көмекшісі",
    ru: "🤝 Помощник официанта",
    en: "🤝 Waiter assistant",
  },
  "waiter.ai_title_cashier": {
    kz: "💳 Кассир көмекшісі",
    ru: "💳 Помощник кассира",
    en: "💳 Cashier assistant",
  },
  "waiter.ai_title_admin": {
    kz: "📊 Аналитик көмекші",
    ru: "📊 Аналитик-помощник",
    en: "📊 Analytics assistant",
  },
  "waiter.ai_title_owner": {
    kz: "👑 Аналитик көмекші",
    ru: "👑 Аналитик-помощник",
    en: "👑 Analytics assistant",
  },
  "waiter.ai_title_manager": {
    kz: "📋 Менеджер көмекшісі",
    ru: "📋 Помощник менеджера",
    en: "📋 Manager assistant",
  },
  "waiter.ai_title_cook": {
    kz: "👨‍🍳 Аспаз көмекшісі",
    ru: "👨‍🍳 Помощник повара",
    en: "👨‍🍳 Cook assistant",
  },
  "waiter.ai_title_host": {
    kz: "🎩 Хостес көмекшісі",
    ru: "🎩 Помощник хостес",
    en: "🎩 Host assistant",
  },
  "waiter.ai_title_courier": {
    kz: "🛵 Курьер көмекшісі",
    ru: "🛵 Помощник курьера",
    en: "🛵 Courier assistant",
  },
  "waiter.ai_suggest_my_tables": {
    kz: "Қазір менде қандай үстелдер бар?",
    ru: "Какие у меня сейчас столы?",
    en: "Which tables do I have right now?",
  },
  "waiter.ai_suggest_dessert": {
    kz: "Десертке не ұсынайын?",
    ru: "Что предложить как десерт?",
    en: "What can I suggest for dessert?",
  },
  "waiter.ai_suggest_open_requests": {
    kz: "Қандай ашық сұраулар бар?",
    ru: "Какие открытые запросы?",
    en: "Any open requests?",
  },
  "waiter.ai_suggest_open_orders": {
    kz: "Қазір қанша ашық тапсырыс бар?",
    ru: "Сколько открытых заказов сейчас?",
    en: "How many open orders right now?",
  },
  "waiter.ai_suggest_shift_revenue": {
    kz: "Ауысым түсімі қанша?",
    ru: "Какая выручка за смену?",
    en: "What's the shift revenue?",
  },
  "waiter.ai_suggest_change_calc": {
    kz: "3850 ₸ тапсырысқа 5000 ₸-ден қайтарымы қанша?",
    ru: "Сколько сдачи с 5000 ₸ за заказ 3850 ₸?",
    en: "How much change from 5000 ₸ for a 3850 ₸ order?",
  },
  "waiter.ai_suggest_top5_today": {
    kz: "Бүгінгі топ-5 тағам?",
    ru: "Топ-5 блюд за сегодня?",
    en: "Top 5 dishes today?",
  },
  "waiter.ai_suggest_today_revenue": {
    kz: "Бүгінгі түсім қанша?",
    ru: "Какая выручка за сегодня?",
    en: "What's today's revenue?",
  },
  "waiter.ai_suggest_remove_dishes": {
    kz: "Мәзірден қай тағамдарды алып тастаған жөн?",
    ru: "Какие блюда стоит убрать из меню?",
    en: "Which dishes should be removed from the menu?",
  },
  "waiter.ai_suggest_best_sellers": {
    kz: "Қай тағамдар жақсы сатылып жатыр?",
    ru: "Какие блюда лучше всего идут?",
    en: "Which dishes sell best?",
  },
  "waiter.ai_suggest_open_tables": {
    kz: "Қанша үстел ашық?",
    ru: "Сколько столов открыто?",
    en: "How many tables are open?",
  },
  "waiter.ai_suggest_top_dishes": {
    kz: "Бүгінгі топ тағамдар?",
    ru: "Топ блюд сегодня?",
    en: "Top dishes today?",
  },
  "waiter.ai_suggest_waiters_on_shift": {
    kz: "Ауысымда қай даяшылар бар?",
    ru: "Какие официанты на смене?",
    en: "Which waiters are on shift?",
  },
  "waiter.ai_suggest_active_orders": {
    kz: "Қанша белсенді тапсырыс бар?",
    ru: "Сколько активных заказов?",
    en: "How many active orders?",
  },
  "waiter.ai_suggest_cook_first": {
    kz: "Алдымен нені дайындайын?",
    ru: "Что приготовить первым?",
    en: "What should be cooked first?",
  },
  "waiter.ai_suggest_stop_list": {
    kz: "Стоп-листе қандай тағамдар бар?",
    ru: "Какие блюда в стоп-листе?",
    en: "Which dishes are on the stop list?",
  },
  "waiter.ai_suggest_bookings_tonight": {
    kz: "Бүгін кешке қанша брондау бар?",
    ru: "Сколько броней сегодня вечером?",
    en: "How many bookings tonight?",
  },
  "waiter.ai_suggest_free_tables_19": {
    kz: "19:00-ге бос үстелдерді көрсет",
    ru: "Покажи свободные столы на 19:00",
    en: "Show free tables for 19:00",
  },
  "waiter.ai_suggest_guests_today": {
    kz: "Бүгін қай қонақтар келді?",
    ru: "Кто из гостей пришёл сегодня?",
    en: "Which guests came today?",
  },
  "waiter.ai_suggest_my_deliveries": {
    kz: "Бүгін менде қанша жеткізу бар?",
    ru: "Сколько доставок у меня сегодня?",
    en: "How many deliveries do I have today?",
  },
  "waiter.ai_suggest_nearest_order": {
    kz: "Ең жақын тапсырыс қайда?",
    ru: "Где ближайший заказ?",
    en: "Where is the nearest order?",
  },
  "waiter.ai_suggest_change_due": {
    kz: "Қайтарым сомасы қанша?",
    ru: "Какая сумма к сдаче?",
    en: "How much change is due?",
  },
  "waiter.ai_empty_hint": {
    kz: "Бірдеңе сұра — мысалы:",
    ru: "Спроси что-нибудь — например:",
    en: "Ask something — for example:",
  },
  "waiter.ai_thinking": {
    kz: "⏳ Ойланып жатырмын…",
    ru: "⏳ Думаю…",
    en: "⏳ Thinking…",
  },
  "waiter.ai_error": {
    kz: "Қате: {msg}",
    ru: "Ошибка: {msg}",
    en: "Error: {msg}",
  },
  "waiter.ai_listening_placeholder": {
    kz: "🎤 Тыңдап тұрмын...",
    ru: "🎤 Слушаю...",
    en: "🎤 Listening...",
  },
  "waiter.ai_input_placeholder": {
    kz: "Хабарлама...",
    ru: "Сообщение...",
    en: "Message...",
  },
  "waiter.ai_stop_recording": {
    kz: "Жазуды тоқтату",
    ru: "Остановить запись",
    en: "Stop recording",
  },
  "waiter.ai_voice_input": {
    kz: "Дауыспен енгізу",
    ru: "Голосовой ввод",
    en: "Voice input",
  },
  "waiter.ai_stop_tts": {
    kz: "Дауыстауды тоқтату",
    ru: "Остановить озвучку",
    en: "Stop speech",
  },
  "waiter.ai_fab_title": {
    kz: "AI-көмекші",
    ru: "AI-помощник",
    en: "AI assistant",
  },
  "waiter.split_title": {
    kz: "Шотты бөлу",
    ru: "Разделить счёт",
    en: "Split the bill",
  },
  "waiter.split_total": {
    kz: "Барлығы: {sum}",
    ru: "Всего: {sum}",
    en: "Total: {sum}",
  },
  "waiter.split_mode_equal": {
    kz: "Тең бөлу",
    ru: "Поровну",
    en: "Equally",
  },
  "waiter.split_mode_equal_desc": {
    kz: "Соманы барлық қонаққа тең бөлеміз",
    ru: "Делим сумму на всех гостей",
    en: "Split the total between all guests",
  },
  "waiter.split_mode_by_items": {
    kz: "Тағам бойынша",
    ru: "По блюдам",
    en: "By items",
  },
  "waiter.split_mode_by_items_desc": {
    kz: "Әркім өз тағамына төлейді",
    ru: "Каждый платит за свои блюда",
    en: "Each guest pays for their own items",
  },
  "waiter.split_mode_custom": {
    kz: "Еркін",
    ru: "Произвольно",
    en: "Custom",
  },
  "waiter.split_mode_custom_desc": {
    kz: "Сомаларды қолмен енгізу",
    ru: "Вручную задать суммы",
    en: "Set amounts manually",
  },
  "waiter.split_people_count": {
    kz: "Адам саны",
    ru: "Количество человек",
    en: "Number of people",
  },
  "waiter.split_people_unit": {
    kz: "адам",
    ru: "человек",
    en: "people",
  },
  "waiter.split_each_pays": {
    kz: "Әркім төлейді",
    ru: "Каждый платит",
    en: "Each pays",
  },
  "waiter.split_assign_items": {
    kz: "Тағамдарды бөліп беріңіз",
    ru: "Распределите блюда",
    en: "Assign the items",
  },
  "waiter.split_guest_n": {
    kz: "Қонақ {n}",
    ru: "Гость {n}",
    en: "Guest {n}",
  },
  "waiter.split_custom_hint": {
    kz: "Еркін бөлуді төлем кезінде кассир енгізеді",
    ru: "Произвольное разделение задаст кассир при оплате",
    en: "Custom split will be set by the cashier at payment",
  },
  "waiter.split_confirm": {
    kz: "Кассирге жіберу →",
    ru: "Передать кассиру →",
    en: "Send to cashier →",
  },
  "waiter.popular_hit": {
    kz: "Сұранысқа ие",
    ru: "Хит продаж",
    en: "Bestseller",
  },
  "waiter.stop_badge": {
    kz: "СТОП",
    ru: "СТОП",
    en: "STOP",
  },
  "waiter.weight_g": {
    kz: "{n}г",
    ru: "{n}г",
    en: "{n}g",
  },
  "waiter.cook_min": {
    kz: "{n} мин",
    ru: "{n} мин",
    en: "{n} min",
  },
  "waiter.no_cooking": {
    kz: "Дайындаусыз",
    ru: "Без приготовления",
    en: "No prep needed",
  },
  "waiter.left_count": {
    kz: "⚠ {n} қалды",
    ru: "⚠ Осталось {n}",
    en: "⚠ {n} left",
  },
  "waiter.unavailable": {
    kz: "қолжетімсіз",
    ru: "недоступно",
    en: "unavailable",
  },
  "waiter.remove_one": {
    kz: "Алып тастау",
    ru: "Убрать",
    en: "Remove",
  },
  "waiter.add_one": {
    kz: "Тағы біреу",
    ru: "Добавить ещё",
    en: "Add one more",
  },
  "waiter.add_to_cart": {
    kz: "+ Қосу",
    ru: "+ Добавить",
    en: "+ Add",
  },
  "waiter.zones_hint": {
    kz: "Таңдалған залдардың үстелдері зал картасында бірінші көрсетіледі. Кез келген үстелмен жұмыс істей беруге болады — бұл таңдау ештеңеге тыйым салмайды.",
    ru: "Столы выбранных залов показываются первыми на карте зала. Работать можно с любым столом — привязка ничего не запрещает.",
    en: "Tables from your selected zones are shown first on the floor map. You can still work with any table — this doesn't restrict anything.",
  },
  "waiter.zones_empty": {
    kz: "Үстелдерге залдар әлі қойылмаған — оларды иесі админкадағы «Үстелдер» бөлімінде баптайды.",
    ru: "У столов заведения пока не заданы залы — владелец настраивает их в админке («Столы»).",
    en: "No zones are set for the venue's tables yet — the owner configures them in the admin panel (\"Tables\").",
  },
  "waiter.zones_error_offline": {
    kz: "Байланыс жоқ — желі қалпына келгенде қайталап көр",
    ru: "Нет связи — попробуй когда сеть восстановится",
    en: "No connection — try again once you're back online",
  },
  "waiter.zones_error_save": {
    kz: "Сақтау сәтсіз болды, тағы бір рет көр",
    ru: "Не удалось сохранить, попробуй ещё раз",
    en: "Couldn't save, try again",
  },
  "waiter.cancel": {
    kz: "Болдырмау",
    ru: "Отмена",
    en: "Cancel",
  },
  "waiter.saving": {
    kz: "Сақталуда…",
    ru: "Сохраняем…",
    en: "Saving…",
  },
  "waiter.transfer_title": {
    kz: "Столды басқаға беру",
    ru: "Передать стол другому",
    en: "Transfer table",
  },
  "waiter.transfer_hint": {
    kz: "Таңдаңыз: коллега экранында стол «менің» болып көрінеді.",
    ru: "Выберите коллегу: на его экране стол будет отображаться как «мой».",
    en: "Pick a colleague: the table will show as \"mine\" on their screen.",
  },
  "waiter.transfer_empty": {
    kz: "Басқа официанттар тізімі бос. Экранда әрқайсысы өз статусын жаңартуы керек.",
    ru: "Список других официантов пуст. Каждый должен обновить свой статус на своём экране.",
    en: "No other waiters in the list. Each waiter must update their status on their screen.",
  },
  "waiter.transfer_tables_count": {
    kz: "{n} үстел",
    ru: "{n} стол.",
    en: "{n} tables",
  },
  "waiter.geo_checking_2": {
    kz: "Орынды анықтап жатырмыз…",
    ru: "Определяем позицию…",
    en: "Locating you…",
  },
  "waiter.geo_outside_title": {
    kz: "Сіз кафе аумағынан тыссыз",
    ru: "Вы вне зоны кафе",
    en: "You're outside the café area",
  },
  "waiter.geo_denied_title": {
    kz: "Геолокацияға рұқсат жоқ",
    ru: "Геолокация запрещена",
    en: "Location access denied",
  },
  "waiter.geo_error_title": {
    kz: "Орынды анықтау мүмкін болмады",
    ru: "Не удалось определить позицию",
    en: "Couldn't determine your position",
  },
  "waiter.geo_outside_subtitle": {
    kz: "Кафеден {distance} қашықтасыз (радиус {radius} м). Үстелдер қолжетімсіз.",
    ru: "{distance} от кафе (радиус {radius} м). Столы недоступны.",
    en: "{distance} from the café (radius {radius} m). Tables unavailable.",
  },
  "waiter.geo_denied_subtitle": {
    kz: "Браузер баптауларында геолокацияға рұқсат беріңіз, әйтпесе үстел алуға болмайды.",
    ru: "Разрешите доступ к геолокации в настройках браузера, иначе нельзя брать столы.",
    en: "Allow location access in your browser settings, otherwise you can't take tables.",
  },
  "waiter.geo_error_subtitle": {
    kz: "Браузерде геолокация жұмыс істемейді немесе уақыт өтіп кетті. Тек статистика қолжетімді.",
    ru: "Геолокация не работает в браузере или истёк таймаут. Только статистика доступна.",
    en: "Location isn't working in this browser or the request timed out. Only stats are available.",
  },
  "waiter.geo_open_stats": {
    kz: "📊 Статистиканы ашу",
    ru: "📊 Открыть статистику",
    en: "📊 Open stats",
  },
  "waiter.theme_title": {
    kz: "Безендіру тақырыбы",
    ru: "Тема оформления",
    en: "Appearance theme",
  },
  "waiter.theme_active": {
    kz: "Белсенді тақырып",
    ru: "Активная тема",
    en: "Active theme",
  },
  // ─── Режим темы: Авто (по времени) / Тёмная / Светлая (редизайн 2026-06-14) ───
  "waiter.theme_mode_auto_name": { kz: "Авто", ru: "Авто", en: "Auto" },
  "waiter.theme_mode_auto_desc": {
    kz: "Тәулік уақыты бойынша",
    ru: "По времени суток",
    en: "By time of day",
  },
  "waiter.theme_mode_light_name": { kz: "Жарық", ru: "Светлая", en: "Light" },
  "waiter.theme_mode_light_desc": { kz: "Күндізгі зал", ru: "Дневной зал", en: "Daytime hall" },
  "waiter.theme_mode_dark_name": { kz: "Қараңғы", ru: "Тёмная", en: "Dark" },
  "waiter.theme_mode_dark_desc": { kz: "Кеш / түн", ru: "Вечер / ночь", en: "Evening / night" },
  "waiter.theme_now_dark": { kz: "Қазір: қараңғы", ru: "Сейчас: тёмная", en: "Now: dark" },
  "waiter.theme_now_light": { kz: "Қазір: жарық", ru: "Сейчас: светлая", en: "Now: light" },
  // ─── Приветственный баннер (редизайн 2026-06-14) ───
  "waiter.greeting_title": { kz: "Сәтті күн!", ru: "Хорошего дня!", en: "Have a great day!" },
  "waiter.greeting_sub": {
    kz: "Сіз тамаша жұмыс істеп жатырсыз 💪",
    ru: "Вы отлично справляетесь 💪",
    en: "You're doing great 💪",
  },
  // ─── Темы оформления: name (бренд-имена не переводятся) + описание ───
  "waiter.theme_terminal_name": { kz: "Терминал", ru: "Терминал", en: "Terminal" },
  "waiter.theme_terminal_desc": {
    kz: "Жұмыс кокпиті — charcoal + алтын, түс = күй",
    ru: "Рабочий кокпит — charcoal + золото, цвет = статус",
    en: "Work cockpit — charcoal + gold, color = status",
  },
  "waiter.theme_hospitality_warm_name": { kz: "Hospitality Warm", ru: "Hospitality Warm", en: "Hospitality Warm" },
  "waiter.theme_hospitality_warm_desc": {
    kz: "Жылы мейрамхана — крем + қызыл + алтын",
    ru: "Тёплый ресторан — крем + красный + золото",
    en: "Warm restaurant — cream + red + gold",
  },
  "waiter.theme_swiss_pro_name": { kz: "Swiss Pro", ru: "Swiss Pro", en: "Swiss Pro" },
  "waiter.theme_swiss_pro_desc": {
    kz: "Минималды-кәсіби — ақ + қара + алтын",
    ru: "Минимально-проф — белый + чёрный + золото",
    en: "Minimal pro — white + black + gold",
  },
  "waiter.theme_ops_cockpit_name": { kz: "Ops Cockpit", ru: "Ops Cockpit", en: "Ops Cockpit" },
  "waiter.theme_ops_cockpit_desc": {
    kz: "Bloomberg terminal — dark + amber + моно",
    ru: "Bloomberg terminal — dark + amber + моно",
    en: "Bloomberg terminal — dark + amber + mono",
  },
  "waiter.theme_hybrid_light_name": { kz: "Hybrid Light", ru: "Hybrid Light", en: "Hybrid Light" },
  "waiter.theme_hybrid_light_desc": {
    kz: "B+C light — крем + қызыл + дөңгелек",
    ru: "B+C light — крем + красный + округлый",
    en: "B+C light — cream + red + rounded",
  },
  "waiter.theme_hybrid_dark_name": { kz: "Hybrid Dark", ru: "Hybrid Dark", en: "Hybrid Dark" },
  "waiter.theme_hybrid_dark_desc": {
    kz: "B+C dark — warm slate + жұмсақ қызыл + cream",
    ru: "B+C dark — warm slate + softer red + cream",
    en: "B+C dark — warm slate + softer red + cream",
  },
  "waiter.theme_stripe_name": { kz: "Stripe", ru: "Stripe", en: "Stripe" },
  "waiter.theme_stripe_desc": {
    kz: "SaaS admin — off-white + күлгін",
    ru: "SaaS admin — off-white + purple",
    en: "SaaS admin — off-white + purple",
  },
  "waiter.theme_linear_name": { kz: "Linear", ru: "Linear", en: "Linear" },
  "waiter.theme_linear_desc": {
    kz: "Premium dark — таза қара + Linear violet",
    ru: "Premium dark — pure black + Linear violet",
    en: "Premium dark — pure black + Linear violet",
  },
  // ─── Статусы вызова (service_request) — бейдж у не-pending ───
  "waiter.sr_status_pending": { kz: "Күтуде", ru: "Ожидает", en: "Pending" },
  "waiter.sr_status_acknowledged": { kz: "Қабылданды", ru: "Принят", en: "Acknowledged" },
  "waiter.sr_status_resolved": { kz: "Шешілді", ru: "Решён", en: "Resolved" },
  "waiter.goals_title": {
    kz: "Ауысым мақсаттары",
    ru: "Цели смены",
    en: "Shift goals",
  },
  "waiter.goal_tables": {
    kz: "Үстелдер",
    ru: "Столы",
    en: "Tables",
  },
  "waiter.goal_not_set": {
    kz: "мақсат қойылмаған",
    ru: "цель не задана",
    en: "no goal set",
  },
  "waiter.goal_done": {
    kz: "орындалды · {percent}%",
    ru: "выполнено · {percent}%",
    en: "done · {percent}%",
  },
  "waiter.goal_remaining": {
    kz: "{percent}% · {remaining} қалды",
    ru: "{percent}% · осталось {remaining}",
    en: "{percent}% · {remaining} left",
  },
  "waiter.team_now": {
    kz: "Қазіргі команда",
    ru: "Команда сейчас",
    en: "Team right now",
  },
  "waiter.team_summary": {
    kz: "{total} ауысымда · {free} бос",
    ru: "{total} на смене · {free} свободны",
    en: "{total} on shift · {free} free",
  },
  "waiter.team_alone": {
    kz: "Қазір ауысымда жалғыз өзіңізсіз",
    ru: "Вы сейчас один на смене",
    en: "You're the only one on shift right now",
  },
  "waiter.peer_busy": {
    kz: "бос емес",
    ru: "занят",
    en: "busy",
  },
  "waiter.peer_dnd": {
    kz: "мазаламаңыз",
    ru: "не беспокоить",
    en: "do not disturb",
  },
  "waiter.peer_tables_one": {
    kz: "{n} үстел",
    ru: "{n} стол",
    en: "{n} table",
  },
  "waiter.peer_tables_few": {
    kz: "{n} үстел",
    ru: "{n} стола",
    en: "{n} tables",
  },
  "waiter.peer_tables_many": {
    kz: "{n} үстел",
    ru: "{n} столов",
    en: "{n} tables",
  },
  "waiter.nav_halls": {
    kz: "Залдар",
    ru: "Залы",
    en: "Halls",
  },
  "waiter.nav_orders": {
    kz: "Тапсырыстар",
    ru: "Заказы",
    en: "Orders",
  },
  "waiter.nav_profile": {
    kz: "Профиль",
    ru: "Профиль",
    en: "Profile",
  },
  "waiter.quick_search": {
    kz: "Іздеу",
    ru: "Поиск",
    en: "Search",
  },
  "waiter.quick_summary": {
    kz: "Қорытынды",
    ru: "Сводка",
    en: "Summary",
  },
  "waiter.cart_positions_one": {
    kz: "{n} позиция",
    ru: "{n} позиция",
    en: "{n} item",
  },
  "waiter.cart_positions_few": {
    kz: "{n} позиция",
    ru: "{n} позиции",
    en: "{n} items",
  },
  "waiter.cart_positions_many": {
    kz: "{n} позиция",
    ru: "{n} позиций",
    en: "{n} items",
  },
  "waiter.status_in_progress": {
    kz: "Жұмыста",
    ru: "В работе",
    en: "In progress",
  },
  "waiter.status_calling": {
    kz: "Шақыру",
    ru: "Вызов",
    en: "Calling",
  },
  "waiter.priority_urgent": {
    kz: "ШҰҒЫЛ",
    ru: "СРОЧНО",
    en: "URGENT",
  },
  "waiter.zone_main": {
    kz: "Зал",
    ru: "Зал",
    en: "Hall",
  },
  "waiter.zone_cabins": {
    kz: "Кабиналар",
    ru: "Кабины",
    en: "Booths",
  },
  "waiter.zone_billiard": {
    kz: "Бильярд",
    ru: "Бильярд",
    en: "Billiards",
  },
  "waiter.zone_karaoke": {
    kz: "Караоке",
    ru: "Караоке",
    en: "Karaoke",
  },
  "waiter.zone_vip": {
    kz: "VIP",
    ru: "VIP",
    en: "VIP",
  },

  // ── Курьер (apps/courier) ────────────────────────────────────────
  // Кабинет курьера доставки: главная, очередь, чат с гостем, история,
  // профиль, маршруты, SOS. Плюрализация заказов — courier.orders_* через
  // ruPluralCategory (kz одна форма во всех трёх, en — one/many).
  "courier.loading": {
    kz: "Жүктелуде…",
    ru: "Загрузка…",
    en: "Loading…",
  },
  "courier.app_label": {
    kz: "Курьер",
    ru: "Курьер",
    en: "Courier",
  },
  "courier.role_label": {
    kz: "Курьер",
    ru: "Курьер",
    en: "Courier",
  },
  "courier.app_for_noaccount": {
    kz: "курьер қосымшасы",
    ru: "приложение курьера",
    en: "courier app",
  },

  // Заказы (плюрализация)
  "courier.orders_one": {
    kz: "тапсырыс",
    ru: "заказ",
    en: "order",
  },
  "courier.orders_few": {
    kz: "тапсырыс",
    ru: "заказа",
    en: "orders",
  },
  "courier.orders_many": {
    kz: "тапсырыс",
    ru: "заказов",
    en: "orders",
  },
  "courier.deliveries_one": {
    kz: "жеткізу",
    ru: "доставка",
    en: "delivery",
  },
  "courier.deliveries_few": {
    kz: "жеткізу",
    ru: "доставки",
    en: "deliveries",
  },
  "courier.deliveries_many": {
    kz: "жеткізу",
    ru: "доставок",
    en: "deliveries",
  },

  // Геоблок (вне зоны доставки)
  "courier.geo_blocked_title": {
    kz: "Сіз жеткізу аймағында емессіз",
    ru: "Вы не в зоне доставки",
    en: "You are outside the delivery zone",
  },
  "courier.geo_blocked_distance": {
    kz: "Сіз кафеден {distance} м қашықтасыз. Тапсырыс алу үшін жақындаңыз.",
    ru: "Вы находитесь в {distance} м от кафе. Приблизьтесь для получения заказов.",
    en: "You are {distance} m from the café. Move closer to receive orders.",
  },
  "courier.geo_blocked_denied": {
    kz: "Геолокацияға рұқсат жоқ. Браузер баптауларынан рұқсат беріңіз.",
    ru: "Доступ к геолокации запрещён. Разрешите в настройках браузера.",
    en: "Location access is denied. Allow it in your browser settings.",
  },
  "courier.geo_blocked_unknown": {
    kz: "Орналасуыңызды анықтау мүмкін болмады.",
    ru: "Не удалось определить местоположение.",
    en: "Could not determine your location.",
  },
  "courier.geo_allowed_radius": {
    kz: "Рұқсат етілген радиус: {radius} м",
    ru: "Допустимый радиус: {radius} м",
    en: "Allowed radius: {radius} m",
  },
  "courier.geo_recheck": {
    kz: "Қайта тексеру",
    ru: "Проверить снова",
    en: "Check again",
  },
  "courier.geo_checking": {
    kz: "Тексерудеміз…",
    ru: "Проверяем…",
    en: "Checking…",
  },
  "courier.geo_checking_hint": {
    kz: "Орналасқан жеріңізді анықтап жатырмыз.",
    ru: "Определяем ваше местоположение.",
    en: "Determining your location.",
  },
  "courier.sign_out_account": {
    kz: "Аккаунттан шығу",
    ru: "Выйти из аккаунта",
    en: "Sign out",
  },

  // Тосты / статусы (App.tsx)
  "courier.toast_picked_up": {
    kz: "Алдым — қонаққа бара жатырмын",
    ru: "Забрал — в пути к гостю",
    en: "Picked up — on the way to the guest",
  },
  "courier.toast_delivered": {
    kz: "Жеткізілді · төлем расталды",
    ru: "Доставлено · оплата подтверждена",
    en: "Delivered · payment confirmed",
  },
  "courier.toast_not_on_shift": {
    kz: "Сіз ауысымда емессіз — жоғарыдан «Жұмысқа дайын» қосыңыз",
    ru: "Вы не на смене — включите «Готов к работе» сверху",
    en: "You are off shift — turn on “Ready to work” above",
  },
  "courier.toast_order_claimed": {
    kz: "{id} тапсырысы қабылданды",
    ru: "Заказ {id} принят",
    en: "Order {id} claimed",
  },
  "courier.toast_order_already_yours": {
    kz: "{id} тапсырысы әлдеқашан сізде",
    ru: "Заказ {id} уже у тебя",
    en: "Order {id} is already yours",
  },
  "courier.toast_order_taken_by_other": {
    kz: "{id} тапсырысын басқа курьер алып үлгерді",
    ru: "Заказ {id} забрал другой курьер",
    en: "Order {id} was taken by another courier",
  },
  "courier.toast_batch_partial": {
    kz: "{count} / {total} алынды — {lost} басқаларда",
    ru: "Взято {count} из {total} — {lost} уже у других",
    en: "Claimed {count} of {total} — {lost} already taken",
  },
  "courier.toast_batch_all": {
    kz: "Алынған тапсырыстар: {count}",
    ru: "Взято заказов: {count}",
    en: "Orders claimed: {count}",
  },
  "courier.toast_batch_none": {
    kz: "Таңдалғандардың барлығын басқа курьерлер алып үлгерді",
    ru: "Все выбранные уже забрали другие курьеры",
    en: "All selected orders were already taken",
  },
  "courier.confirm_release": {
    kz: "Тапсырысты басқа курьерлерге босатасыз ба?",
    ru: "Освободить заказ для других курьеров?",
    en: "Release this order for other couriers?",
  },
  "courier.toast_released": {
    kz: "Тапсырыс босатылды",
    ru: "Заказ освобождён",
    en: "Order released",
  },
  "courier.toast_connecting": {
    kz: "Серверге қосылудамыз…",
    ru: "Подключаемся к серверу…",
    en: "Connecting to the server…",
  },

  // HomeScreen — погода
  "courier.weather_clear": {
    kz: "Ашық",
    ru: "Ясно",
    en: "Clear",
  },
  "courier.weather_cloudy": {
    kz: "Бұлтты",
    ru: "Облачно",
    en: "Cloudy",
  },
  "courier.weather_fog": {
    kz: "Тұман — жолда сақ болыңыз",
    ru: "Туман — осторожно на дороге",
    en: "Fog — drive carefully",
  },
  "courier.weather_rain": {
    kz: "Жаңбыр — жаңбыр киімін алыңыз",
    ru: "Дождь — возьми дождевик",
    en: "Rain — take a raincoat",
  },
  "courier.weather_snow": {
    kz: "Қар — тайғақ",
    ru: "Снег — скользко",
    en: "Snow — slippery",
  },
  "courier.weather_shower": {
    kz: "Нөсер — жаңбыр киімін алыңыз",
    ru: "Ливень — возьми дождевик",
    en: "Heavy rain — take a raincoat",
  },
  "courier.weather_storm": {
    kz: "Найзағай — абай болыңыз",
    ru: "Гроза — будь осторожен",
    en: "Thunderstorm — be careful",
  },
  "courier.weather_ok": {
    kz: "Ауа райы қалыпты",
    ru: "Погода в норме",
    en: "Weather is fine",
  },
  "courier.weather_city_now": {
    kz: "Алматы · қазір",
    ru: "Алматы · сейчас",
    en: "Almaty · now",
  },

  // HomeScreen — hero
  "courier.hero_brand": {
    kz: "Café Әлем · Курьер",
    ru: "Café Әлем · Курьер",
    en: "Café Әlem · Courier",
  },
  "courier.hero_greeting": {
    kz: "Сәлем, {name}",
    ru: "Сәлем, {name}",
    en: "Hi, {name}",
  },
  "courier.hero_deliveries_shift": {
    kz: "{count} жеткізу · {shift}",
    ru: "{count} доставок · {shift}",
    en: "{count} deliveries · {shift}",
  },
  "courier.hero_active_queue": {
    kz: "{active} · кезекте {queue}",
    ru: "{active} · {queue} в очереди",
    en: "{active} · {queue} in queue",
  },
  "courier.active_one": {
    kz: "1 белсенді",
    ru: "1 активный",
    en: "1 active",
  },
  "courier.active_zero": {
    kz: "0 белсенді",
    ru: "0 активных",
    en: "0 active",
  },
  "courier.stat_delivered": {
    kz: "Жеткізілді",
    ru: "Доставлено",
    en: "Delivered",
  },
  "courier.stat_avg_time": {
    kz: "Орт. уақыт",
    ru: "Ср. срок",
    en: "Avg. time",
  },
  "courier.stat_on_time": {
    kz: "Уақытында",
    ru: "Вовремя",
    en: "On time",
  },
  "courier.minutes_short": {
    kz: "{n} мин",
    ru: "{n} мин",
    en: "{n} min",
  },
  "courier.shift_duration": {
    kz: "{h} сағ {m} мин",
    ru: "{h} ч {m} мин",
    en: "{h} h {m} min",
  },

  // HomeScreen — online toggle
  "courier.online_ready": {
    kz: "🟢 Жұмысқа дайын",
    ru: "🟢 Готов к работе",
    en: "🟢 Ready to work",
  },
  "courier.online_off": {
    kz: "🔴 Ауысымда емес",
    ru: "🔴 Не на смене",
    en: "🔴 Off shift",
  },

  // HomeScreen — групповой совет
  "courier.group_suggestion_title": {
    kz: "Ақылды кеңес: топтап ал",
    ru: "Умный совет: сгруппируй",
    en: "Smart tip: group them",
  },
  "courier.group_suggestion_body": {
    kz: "Кезекте {count} {word}. Бірге қабылда — уақыт үнемдейсің!",
    ru: "В очереди {count} {word}. Прими вместе — сэкономишь время!",
    en: "{count} {word} in queue. Claim them together to save time!",
  },
  "courier.group_suggestion_view": {
    kz: "Қарау ⚡",
    ru: "Посмотреть ⚡",
    en: "View ⚡",
  },

  // HomeScreen — active empty + batch
  "courier.active_empty_title": {
    kz: "Бос",
    ru: "Свободно",
    en: "Free",
  },
  "courier.active_empty_hint": {
    kz: "Кезектен тапсырыс алыңыз",
    ru: "Возьмите заказ из очереди",
    en: "Claim an order from the queue",
  },
  "courier.batch_title": {
    kz: "📦 Сіздің батч · маршрутта тағы {count}",
    ru: "📦 Твой батч · ещё {count} по маршруту",
    en: "📦 Your batch · {count} more on the route",
  },

  // HomeScreen — очередь preview
  "courier.off_shift_hidden": {
    kz: "🔴 Сіз ауысымда емессіз — жаңа тапсырыстар жасырылған. Жеткізуді қабылдау үшін жоғарыдан «Жұмысқа дайын» түймесін басыңыз.",
    ru: "🔴 Вы не на смене — новые заказы скрыты. Нажмите «Готов к работе» сверху, чтобы принимать доставки.",
    en: "🔴 You are off shift — new orders are hidden. Tap “Ready to work” above to accept deliveries.",
  },
  "courier.available_orders": {
    kz: "🟢 Қолжетімді тапсырыстар",
    ru: "🟢 Доступные заказы",
    en: "🟢 Available orders",
  },
  "courier.in_queue_count": {
    kz: "кезекте {count}",
    ru: "{count} в очереди",
    en: "{count} in queue",
  },
  "courier.queue_empty": {
    kz: "Кезек бос",
    ru: "Очередь пуста",
    en: "Queue is empty",
  },
  "courier.delivered_today": {
    kz: "Бүгін жеткізілді",
    ru: "Сегодня доставлено",
    en: "Delivered today",
  },

  // HomeScreen — SOS + footer
  "courier.sos_button": {
    kz: "SOS — Көмек шақыру",
    ru: "SOS — Вызвать помощь",
    en: "SOS — Call for help",
  },
  "courier.shift_since": {
    kz: "Café Әлем · ауысым {time}-ден",
    ru: "Café Әлем · смена с {time}",
    en: "Café Әlem · shift since {time}",
  },

  // QueueScreen
  "courier.queue_title": {
    kz: "Жеткізу кезегі",
    ru: "Очередь доставок",
    en: "Delivery queue",
  },
  "courier.queue_off_shift": {
    kz: "Сіз ауысымда емессіз. Тапсырыстарды көру және алу үшін басты бетте «Жұмысқа дайын» қосыңыз.",
    ru: "Вы не на смене. Включите «Готов к работе» на главной, чтобы видеть и брать заказы.",
    en: "You are off shift. Turn on “Ready to work” on the home screen to see and claim orders.",
  },
  "courier.queue_batch_button": {
    kz: "Топтап алу",
    ru: "Взять пачкой",
    en: "Claim batch",
  },
  "courier.queue_take_all": {
    kz: "Барлығын алу",
    ru: "Взять все",
    en: "Claim all",
  },
  "courier.queue_select_some": {
    kz: "Таңдау",
    ru: "Выбрать",
    en: "Select",
  },
  "courier.scheduled_to": {
    kz: "Уақытқа жеткізу",
    ru: "Доставка ко времени",
    en: "Scheduled delivery",
  },
  "courier.scheduled_today": {
    kz: "Бүгін",
    ru: "Сегодня",
    en: "Today",
  },
  "courier.address_may_be_inaccurate": {
    kz: "Мекенжай/нүкте дәл болмауы мүмкін — қонақ қолмен қойған не GPS қателескен. Жеткізер алдында қонаққа қоңырау шалып нақтылаңыз.",
    ru: "Адрес/точка могут быть неточными — гость ставил вручную или сбой GPS. Перед доставкой уточните у гостя по телефону.",
    en: "Address/pin may be inaccurate — set by the guest manually or GPS glitch. Confirm with the guest by phone before delivering.",
  },
  "courier.scheduled_tomorrow": {
    kz: "Ертең",
    ru: "Завтра",
    en: "Tomorrow",
  },
  "courier.cancel": {
    kz: "Болдырмау",
    ru: "Отмена",
    en: "Cancel",
  },
  "courier.queue_no_orders": {
    kz: "Тапсырыстар жоқ",
    ru: "Заказов нет",
    en: "No orders",
  },
  "courier.queue_select_hint": {
    kz: "Тапсырыстарды белгілеп, «Таңдалғандарды алу» түймесін басыңыз",
    ru: "Отметьте заказы и нажмите «Взять выбранные»",
    en: "Select orders and tap “Claim selected”",
  },
  "courier.queue_waiting_count": {
    kz: "{count} {word} жөнелтуді күтуде",
    ru: "{count} {word} ждут отправки",
    en: "{count} {word} waiting to be dispatched",
  },
  "courier.queue_empty_full": {
    kz: "Бос. Кезекте тапсырыстар жоқ.",
    ru: "Свободно. Заказов в очереди нет.",
    en: "Free. No orders in the queue.",
  },
  "courier.batch_select_orders": {
    kz: "Тапсырыстарды таңдаңыз",
    ru: "Выберите заказы",
    en: "Select orders",
  },
  "courier.batch_claim_selected": {
    kz: "Таңдалғандарды алу ({count})",
    ru: "Взять выбранные ({count})",
    en: "Claim selected ({count})",
  },

  // ChatScreen
  "courier.chat_title": {
    kz: "Қонақпен чат",
    ru: "Чат с гостем",
    en: "Chat with guest",
  },
  "courier.chat_subtitle_active_delivery": {
    kz: "Белсенді жеткізу бойынша хабарлар",
    ru: "Сообщения по активной доставке",
    en: "Messages for the active delivery",
  },
  "courier.chat_no_active_order": {
    kz: "Белсенді тапсырыс жоқ",
    ru: "Нет активного заказа",
    en: "No active order",
  },
  "courier.chat_no_active_hint": {
    kz: "Кезектен тапсырыс алыңыз — сонда қонақпен нақты уақытта жазыса аласыз.",
    ru: "Возьми заказ из очереди — и сможешь переписываться с гостем в реальном времени.",
    en: "Claim an order from the queue — then you can message the guest in real time.",
  },
  "courier.chat_order_label": {
    kz: "Тапсырыс D-{id}",
    ru: "Заказ D-{id}",
    en: "Order D-{id}",
  },
  "courier.chat_empty": {
    kz: "Әзірге хабар жоқ. Бірінші болып жазыңыз.",
    ru: "Сообщений пока нет. Напиши первым.",
    en: "No messages yet. Be the first to write.",
  },
  "courier.chat_quick_soon": {
    kz: "Жақын жердемін",
    ru: "Скоро буду",
    en: "Almost there",
  },
  "courier.chat_quick_arrived": {
    kz: "Жеттім",
    ru: "Уже на месте",
    en: "I've arrived",
  },
  "courier.chat_quick_cant_find": {
    kz: "Таба алмай жатырмын",
    ru: "Не могу найти",
    en: "Can't find it",
  },
  "courier.chat_quick_come_down": {
    kz: "Төмен түсіңіз",
    ru: "Спускайтесь",
    en: "Please come down",
  },
  "courier.chat_input_placeholder": {
    kz: "Хабар…",
    ru: "Сообщение…",
    en: "Message…",
  },
  "courier.chat_voice_message": {
    kz: "Дауыстық хабар",
    ru: "Голосовое сообщение",
    en: "Voice message",
  },
  "courier.chat_send": {
    kz: "Жіберу",
    ru: "Отправить",
    en: "Send",
  },
  "courier.chat_record": {
    kz: "Дауыстық жазу",
    ru: "Записать голосовое",
    en: "Record voice",
  },
  "courier.chat_recording": {
    kz: "Жазылып жатыр",
    ru: "Запись",
    en: "Recording",
  },
  "courier.chat_audio_cancel": {
    kz: "Болдырмау",
    ru: "Отменить",
    en: "Cancel",
  },
  "courier.chat_audio_unsupported": {
    kz: "Браузер дауыстық жазуды қолдамайды",
    ru: "Браузер не поддерживает запись голоса",
    en: "Browser doesn't support voice recording",
  },
  "courier.chat_audio_denied": {
    kz: "Микрофонға рұқсат жоқ",
    ru: "Нет доступа к микрофону",
    en: "Microphone access denied",
  },
  "courier.chat_call_aria": {
    kz: "Клиентке қоңырау шалу",
    ru: "Позвонить клиенту",
    en: "Call customer",
  },

  // HistoryScreen
  "courier.history_title": {
    kz: "Ауысым тарихы",
    ru: "История смены",
    en: "Shift history",
  },
  "courier.history_summary": {
    kz: "{count} {word}",
    ru: "{count} {word}",
    en: "{count} {word}",
  },
  "courier.history_summary_extra": {
    kz: " · ⭐ {rating} · орт. {avg} мин",
    ru: " · ⭐ {rating} · ср. {avg} мин",
    en: " · ⭐ {rating} · avg. {avg} min",
  },
  "courier.filter_today": {
    kz: "Бүгін",
    ru: "Сегодня",
    en: "Today",
  },
  "courier.filter_yesterday": {
    kz: "Кеше",
    ru: "Вчера",
    en: "Yesterday",
  },
  "courier.filter_week": {
    kz: "Апта",
    ru: "Неделя",
    en: "Week",
  },
  "courier.filter_month": {
    kz: "Ай",
    ru: "Месяц",
    en: "Month",
  },
  "courier.history_rating_banner": {
    kz: "🏆 Жеткізу рейтингі",
    ru: "🏆 Рейтинг доставок",
    en: "🏆 Delivery rating",
  },
  "courier.history_of_ratings": {
    kz: "{count} бағадан",
    ru: "Из {count} оценок",
    en: "Out of {count} ratings",
  },
  "courier.history_avg_time": {
    kz: "Орташа уақыт",
    ru: "Среднее время",
    en: "Average time",
  },
  "courier.history_empty_period": {
    kz: "Бұл кезеңде жеткізу жоқ",
    ru: "Нет доставок за этот период",
    en: "No deliveries for this period",
  },

  // ProfileScreen
  "courier.profile_today": {
    kz: "Бүгін",
    ru: "Сегодня",
    en: "Today",
  },
  "courier.profile_shift": {
    kz: "Ауысым",
    ru: "Смена",
    en: "Shift",
  },
  "courier.profile_shift_since": {
    kz: "{time}-ден",
    ru: "с {time}",
    en: "since {time}",
  },
  "courier.end_shift": {
    kz: "🚪 Ауысымды аяқтау",
    ru: "🚪 Завершить смену",
    en: "🚪 End shift",
  },

  // ActiveOrderCard
  "courier.active_order": {
    kz: "Белсенді тапсырыс",
    ru: "Активный заказ",
    en: "Active order",
  },
  "courier.distance_eta": {
    kz: "{km} км · {min} мин",
    ru: "{km} км · {min} мин",
    en: "{km} km · {min} min",
  },
  "courier.route": {
    kz: "Маршрут",
    ru: "Маршрут",
    en: "Route",
  },
  "courier.approaching": {
    kz: "Жақындап келесіз! ({m} м)",
    ru: "Подъезжаете! ({m} м)",
    en: "Approaching! ({m} m)",
  },
  "courier.guest_notified": {
    kz: "Қонақ хабарландыру алып қойды",
    ru: "Гость уже получил уведомление",
    en: "The guest has already been notified",
  },
  "courier.pickup_at_cafe": {
    kz: "Кафеден алу",
    ru: "Забрать в кафе",
    en: "Pick up at the café",
  },
  "courier.pickup_window": {
    kz: "«{window}» терезесі",
    ru: "Окно «{window}»",
    en: "Window “{window}”",
  },
  "courier.pickup_code": {
    kz: " · код {code}",
    ru: " · код {code}",
    en: " · code {code}",
  },
  "courier.pickup_ready_in": {
    kz: " · {min} мин ішінде дайын",
    ru: " · готов через {min} мин",
    en: " · ready in {min} min",
  },
  "courier.deliver_to_guest": {
    kz: "Қонаққа жеткізу",
    ru: "Доставить гостю",
    en: "Deliver to the guest",
  },
  "courier.guest_note": {
    kz: "Қонақ ескертпесі",
    ru: "Заметка гостя",
    en: "Guest note",
  },
  "courier.order_contents": {
    kz: "📦 Тапсырыс құрамы",
    ru: "📦 Состав заказа",
    en: "📦 Order contents",
  },
  "courier.confirm_pickup_action": {
    kz: "Қабылдауды растау",
    ru: "Подтвердить приём",
    en: "Confirm receipt",
  },
  "courier.confirm_deliver_action": {
    kz: "Жеткізуді растау",
    ru: "Подтвердить передачу",
    en: "Confirm handover",
  },
  "courier.confirm_deliver_prompt": {
    kz: "Тапсырыс қонаққа табысталды ма? Бұл әрекетті кері қайтару мүмкін емес.",
    ru: "Заказ передан гостю? Это действие нельзя отменить.",
    en: "Order handed to the guest? This cannot be undone.",
  },
  "courier.payment_collect_cash": {
    kz: "Қолма-қол алу",
    ru: "Взять наличными",
    en: "Collect in cash",
  },
  "courier.payment_already_paid": {
    kz: "Тапсырыс төленген — қолма-қол ақша алмаңыз",
    ru: "Заказ уже оплачен — наличные не брать",
    en: "Already paid — do not collect cash",
  },
  "courier.batch_tap_hint": {
    kz: "Ауысу үшін тапсырысты басыңыз",
    ru: "Нажмите на заказ, чтобы переключиться на него",
    en: "Tap an order to switch to it",
  },
  "courier.chat_history_order": {
    kz: "Тапсырыс #{id} · {date}",
    ru: "Заказ #{id} · {date}",
    en: "Order #{id} · {date}",
  },
  "courier.chat_history_current": {
    kz: "Ағымдағы тапсырыс #{id}",
    ru: "Текущий заказ #{id}",
    en: "Current order #{id}",
  },
  "courier.chat_delivered_note": {
    kz: "Тапсырыс жеткізілді — қонақпен чат әлі біраз уақыт ашық",
    ru: "Заказ доставлен — чат с гостем ещё доступен некоторое время",
    en: "Order delivered — chat with the guest is still open for a while",
  },
  "courier.map_no_coords": {
    kz: "Мекенжай координаттары қолжетімсіз",
    ru: "Координаты адреса недоступны",
    en: "Address coordinates unavailable",
  },
  "courier.active_many": {
    kz: "{n} белсенді",
    ru: "{n} активных",
    en: "{n} active",
  },
  "courier.batch_current": {
    kz: "Ағымдағы",
    ru: "Текущий",
    en: "Current",
  },
  "courier.chat_with_guest_aria": {
    kz: "Қонақпен чат",
    ru: "Чат с гостем",
    en: "Chat with guest",
  },

  // Статусы доставки (StatusStepper + statusLabel)
  "courier.status_picking_up": {
    kz: "аламын",
    ru: "забираю",
    en: "picking up",
  },
  "courier.status_in_transit": {
    kz: "жолда",
    ru: "в пути",
    en: "in transit",
  },
  "courier.status_on_site": {
    kz: "орнында",
    ru: "на месте",
    en: "on site",
  },
  "courier.status_delivered": {
    kz: "берілді",
    ru: "передано",
    en: "delivered",
  },
  "courier.step_picking_up": {
    kz: "Аламын",
    ru: "Забираю",
    en: "Picking up",
  },
  "courier.step_in_transit": {
    kz: "Жолда",
    ru: "В пути",
    en: "In transit",
  },
  "courier.step_on_site": {
    kz: "Орнында",
    ru: "На месте",
    en: "On site",
  },
  "courier.step_delivered": {
    kz: "Берілді",
    ru: "Передано",
    en: "Delivered",
  },

  // QueueItemCard
  "courier.ready_at": {
    kz: "⏱ {time}-де дайын",
    ru: "⏱ готов в {time}",
    en: "⏱ ready at {time}",
  },
  "courier.kitchen_waiting": {
    kz: "🍳 күтуде",
    ru: "🍳 ждёт",
    en: "🍳 waiting",
  },
  "courier.cafe_name": {
    kz: "Café Әлем",
    ru: "Café Әлем",
    en: "Café Әlem",
  },
  "courier.distance_km": {
    kz: "📏 {km} км",
    ru: "📏 {km} км",
    en: "📏 {km} km",
  },
  "courier.claim": {
    kz: "Алу",
    ru: "Взять",
    en: "Claim",
  },

  // BottomNav
  "courier.nav_home": {
    kz: "Басты",
    ru: "Главная",
    en: "Home",
  },
  "courier.nav_queue": {
    kz: "Кезек",
    ru: "Очередь",
    en: "Queue",
  },
  "courier.nav_chat": {
    kz: "Чат",
    ru: "Чат",
    en: "Chat",
  },
  "courier.nav_history": {
    kz: "Тарих",
    ru: "История",
    en: "History",
  },
  "courier.nav_profile": {
    kz: "Профиль",
    ru: "Профиль",
    en: "Profile",
  },

  // RouteSheet
  "courier.route_open": {
    kz: "Маршрутты ашу",
    ru: "Открыть маршрут",
    en: "Open route",
  },
  "courier.route_close_aria": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "courier.route_2gis_desc": {
    kz: "Нақты кірме жолдар мен тұйықталмаған жолдар",
    ru: "Точные подъезды и проезды, детальные карты",
    en: "Precise approaches, driveways and detailed maps",
  },
  "courier.route_yandex_label": {
    kz: "Яндекс Карты",
    ru: "Яндекс Карты",
    en: "Yandex Maps",
  },
  "courier.route_yandex_desc": {
    kz: "Кептелістер + балама маршруттар",
    ru: "Пробки + альтернативные маршруты",
    en: "Traffic + alternative routes",
  },
  "courier.route_google_desc": {
    kz: "Әмбебап, дауыстық навигация",
    ru: "Универсальный, голосовая навигация",
    en: "Universal, voice navigation",
  },
  "courier.route_hint_full": {
    kz: "Кафеден қонаққа дейінгі дайын маршрут ашылады. Телефонда — орнатылған болса, түпнұсқа қосымша.",
    ru: "Откроется готовый маршрут от кафе до гостя. На телефоне — родное приложение если установлено.",
    en: "A ready route from the café to the guest will open. On a phone — the native app if installed.",
  },
  "courier.route_hint_search": {
    kz: "Мекенжай бойынша іздеу. Телефонда түпнұсқа қосымша ашылады.",
    ru: "Поиск по адресу. На телефоне откроется родное приложение.",
    en: "Search by address. On a phone the native app will open.",
  },

  // SOSModal
  "courier.sos_call_cafe": {
    kz: "Кафеге қоңырау шалу",
    ru: "Позвонить в кафе",
    en: "Call the café",
  },
  "courier.sos_whatsapp_cafe": {
    kz: "Кафеге WhatsApp жазу",
    ru: "Написать кафе в WhatsApp",
    en: "WhatsApp the café",
  },
  "courier.sos_emergency": {
    kz: "Төтенше қызметтер",
    ru: "Экстренные службы",
    en: "Emergency services",
  },
  "courier.sos_no_phone_hint": {
    kz: "Кафе телефоны көрсетілмеген — иесі оны админкадағы баптауларда қоса алады",
    ru: "Телефон кафе не указан — владелец может добавить его в настройках админки",
    en: "Café phone is not set — the owner can add it in admin settings",
  },
  "courier.sos_need_help": {
    kz: "Көмек керек пе?",
    ru: "Нужна помощь?",
    en: "Need help?",
  },
  "courier.sos_choose_who": {
    kz: "Кімге қоңырау шалатыныңызды таңдаңыз",
    ru: "Выбери кому позвонить",
    en: "Choose who to call",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Кухня (kitchen app — KDS, таймеры, стоп-лист, склад, статистика, профиль).
  // Голосовой парсер (voiceCommands.ts) и его команды НЕ локализованы — он
  // матчит только русскую речь. Здесь только отображаемый UI.
  // ─────────────────────────────────────────────────────────────────────────

  // App shell / общие
  "kitchen.app_title": {
    kz: "Ас үй",
    ru: "Кухня",
    en: "Kitchen",
  },
  "kitchen.loading": {
    kz: "Жүктелуде…",
    ru: "Загрузка…",
    en: "Loading…",
  },
  "kitchen.loading_items": {
    kz: "Позициялар жүктелуде…",
    ru: "Загрузка позиций...",
    en: "Loading items...",
  },
  "kitchen.new_order_arrived": {
    kz: "🆕 Жаңа тапсырыс келді!",
    ru: "🆕 Новый заказ!",
    en: "🆕 New order!",
  },

  // Гео-блокировка
  "kitchen.geo_not_in_cafe": {
    kz: "Сіз кафеде емессіз",
    ru: "Вы не в кафе",
    en: "You are not at the cafe",
  },
  "kitchen.geo_distance": {
    kz: "Сіз мекемеден {dist} м қашықтасыз. Жұмыс істеу үшін кафе аймағына кіріңіз.",
    ru: "Вы находитесь в {dist} м от заведения. Войдите в зону кафе для работы.",
    en: "You are {dist} m from the venue. Enter the cafe area to start working.",
  },
  "kitchen.geo_denied": {
    kz: "Геолокацияға рұқсат жоқ. Браузер баптауларынан рұқсат беріңіз.",
    ru: "Доступ к геолокации запрещён. Разрешите в настройках браузера.",
    en: "Location access denied. Allow it in your browser settings.",
  },
  "kitchen.geo_unknown": {
    kz: "Орналасуды анықтау мүмкін болмады.",
    ru: "Не удалось определить местоположение.",
    en: "Could not determine your location.",
  },
  "kitchen.geo_allowed_radius": {
    kz: "Рұқсат етілген радиус: {radius} м",
    ru: "Допустимый радиус: {radius} м",
    en: "Allowed radius: {radius} m",
  },
  "kitchen.geo_recheck": {
    kz: "Қайта тексеру",
    ru: "Проверить снова",
    en: "Check again",
  },
  "kitchen.geo_checking": {
    kz: "Тексерудеміз…",
    ru: "Проверяем…",
    en: "Checking…",
  },
  "kitchen.geo_checking_hint": {
    kz: "Орналасқан жеріңізді анықтап жатырмыз.",
    ru: "Определяем ваше местоположение.",
    en: "Determining your location.",
  },
  "kitchen.logout": {
    kz: "Аккаунттан шығу",
    ru: "Выйти из аккаунта",
    en: "Sign out",
  },
  "kitchen.app_label": {
    kz: "Ас үй",
    ru: "Кухня",
    en: "Kitchen",
  },
  "kitchen.staff_label": {
    kz: "Аспаз:",
    ru: "Повар:",
    en: "Cook:",
  },
  "kitchen.stop_until_end_of_day": {
    kz: "күн соңына дейін",
    ru: "до конца дня",
    en: "until end of day",
  },

  // Голосовые тосты (фидбек после команды — показывается, не матчится)
  "kitchen.voice_no_new_orders": {
    kz: "Жаңа тапсырыс жоқ",
    ru: "Нет новых заказов",
    en: "No new orders",
  },
  "kitchen.voice_to_work": {
    kz: "✅ Жұмысқа: {count}",
    ru: "✅ В работу: {count}",
    en: "✅ Started: {count}",
  },
  "kitchen.voice_no_cooking": {
    kz: "Дайындалып жатқан тағам жоқ",
    ru: "Нет блюд в готовке",
    en: "No dishes cooking",
  },
  "kitchen.voice_ready_count": {
    kz: "✅ Дайын: {count}",
    ru: "✅ Готово: {count}",
    en: "✅ Ready: {count}",
  },
  "kitchen.voice_added_to_stop": {
    kz: "🚫 {dish} — стоп-параққа",
    ru: "🚫 {dish} — в стоп-лист",
    en: "🚫 {dish} — added to stop-list",
  },
  "kitchen.voice_not_in_stop": {
    kz: "«{dish}» стоп-парақта жоқ",
    ru: "«{dish}» нет в стоп-листе",
    en: "“{dish}” is not in the stop-list",
  },
  "kitchen.voice_removed_from_stop": {
    kz: "✓ {dish} — стоптан алынды",
    ru: "✓ {dish} — снят со стопа",
    en: "✓ {dish} — removed from stop-list",
  },
  "kitchen.voice_timer_set": {
    kz: "⏱ Таймер {minutes} мин",
    ru: "⏱ Таймер {minutes} мин",
    en: "⏱ Timer {minutes} min",
  },
  "kitchen.voice_timer_default_dish": {
    kz: "Таймер {minutes}м",
    ru: "Таймер {minutes}м",
    en: "Timer {minutes}m",
  },
  "kitchen.voice_not_understood": {
    kz: "Команданы түсінбедім",
    ru: "Не понял команду",
    en: "Didn't understand the command",
  },

  // Навигация (сайдбар / нижнее меню)
  "kitchen.nav_kds": {
    kz: "KDS",
    ru: "KDS",
    en: "KDS",
  },
  "kitchen.nav_timers": {
    kz: "Таймерлер",
    ru: "Таймеры",
    en: "Timers",
  },
  "kitchen.nav_stoplist": {
    kz: "Стоп-парақ",
    ru: "Стоп-лист",
    en: "Stop-list",
  },
  "kitchen.nav_stoplist_short": {
    kz: "Стоп",
    ru: "Стоп",
    en: "Stop",
  },
  "kitchen.nav_inventory": {
    kz: "Қойма",
    ru: "Склад",
    en: "Inventory",
  },
  "kitchen.nav_stats": {
    kz: "Статистика",
    ru: "Статистика",
    en: "Stats",
  },
  "kitchen.nav_profile": {
    kz: "Профиль",
    ru: "Профиль",
    en: "Profile",
  },
  "kitchen.nav_profile_short": {
    kz: "Мен",
    ru: "Я",
    en: "Me",
  },

  // Сайдбар
  "kitchen.sidebar_expand": {
    kz: "Мәзірді жаю",
    ru: "Развернуть меню",
    en: "Expand menu",
  },
  "kitchen.sidebar_collapse": {
    kz: "Мәзірді жию",
    ru: "Свернуть меню",
    en: "Collapse menu",
  },
  "kitchen.sidebar_shift": {
    kz: "Ауысым",
    ru: "Смена",
    en: "Shift",
  },
  "kitchen.sidebar_voice": {
    kz: "Дауыс",
    ru: "Голос",
    en: "Voice",
  },
  "kitchen.sidebar_notifications": {
    kz: "Хабарламалар",
    ru: "Уведомления",
    en: "Notifications",
  },
  "kitchen.sidebar_new_order": {
    kz: "Жаңа тапсырыс!",
    ru: "Новый заказ!",
    en: "New order!",
  },
  "kitchen.sidebar_end_shift": {
    kz: "Ауысымды аяқтау",
    ru: "Завершить смену",
    en: "End shift",
  },

  // KDS экран
  "kitchen.kds_all_orders": {
    kz: "Барлық тапсырыстар",
    ru: "Все заказы",
    en: "All orders",
  },
  "kitchen.kds_subtitle": {
    kz: "{active} белсенді · {cooking} дайындалуда",
    ru: "{active} активных · {cooking} готовится",
    en: "{active} active · {cooking} cooking",
  },
  "kitchen.kds_col_new": {
    kz: "🔔 Жаңа",
    ru: "🔔 Новые",
    en: "🔔 New",
  },
  "kitchen.kds_col_cooking": {
    kz: "🔥 Дайындалуда",
    ru: "🔥 Готовится",
    en: "🔥 Cooking",
  },
  "kitchen.kds_col_ready": {
    kz: "✅ Дайын",
    ru: "✅ Готово",
    en: "✅ Ready",
  },
  "kitchen.kds_empty": {
    kz: "Бос",
    ru: "Пусто",
    en: "Empty",
  },
  "kitchen.station_move_to": {
    kz: "Цехке",
    ru: "В цех",
    en: "To station",
  },
  "kitchen.station_other": {
    kz: "Бөтен цехтар",
    ru: "Чужие цехи",
    en: "Other stations",
  },
  "kitchen.station_pull_title": {
    kz: "Басқа цехтардың тағамдары — өзіңе ал",
    ru: "Блюда других цехов — забрать к себе",
    en: "Dishes from other stations — pull to yourself",
  },
  "kitchen.station_pull_empty": {
    kz: "Басқа цехтарда белсенді тағам жоқ",
    ru: "Нет активных блюд в других цехах",
    en: "No active dishes in other stations",
  },
  "kitchen.station_pull_here": {
    kz: "Өзіме алу",
    ru: "Забрать сюда",
    en: "Pull here",
  },
  "kitchen.stations_list": {
    kz: "Цехтар: {list}",
    ru: "Цехи: {list}",
    en: "Stations: {list}",
  },
  "kitchen.kds_empty_here": {
    kz: "Мұнда бос",
    ru: "Здесь пусто",
    en: "Nothing here",
  },
  "kitchen.order_label": {
    kz: "Тапсырыс {num}",
    ru: "Заказ {num}",
    en: "Order {num}",
  },
  "kitchen.kds_table_fallback": {
    kz: "Үстел",
    ru: "Стол",
    en: "Table",
  },
  "kitchen.kds_takeaway": {
    kz: "Алып кету",
    ru: "Навынос",
    en: "Takeaway",
  },
  "kitchen.delivery_title": {
    kz: "Жеткізу",
    ru: "Доставки",
    en: "Deliveries",
  },
  "kitchen.delivery_summary": {
    kz: "{p} дайындалуда · {r} курьерді күтуде",
    ru: "{p} готовится · {r} ждут выдачи",
    en: "{p} cooking · {r} awaiting handover",
  },
  "kitchen.delivery_loading": {
    kz: "Realtime жүктелуде…",
    ru: "Загрузка realtime…",
    en: "Loading realtime…",
  },
  "kitchen.delivery_empty": {
    kz: "Жеткізуге тапсырыс жоқ.",
    ru: "Заказов на доставку нет.",
    en: "No delivery orders.",
  },
  "kitchen.delivery_badge_cooking": {
    kz: "ДАЙЫНДАЛУДА",
    ru: "ГОТОВИТСЯ",
    en: "COOKING",
  },
  "kitchen.delivery_badge_ready": {
    kz: "ДАЙЫН — КУРЬЕРДІ КҮТУДЕ",
    ru: "ГОТОВ — ЖДЁТ КУРЬЕРА",
    en: "READY — AWAITING COURIER",
  },
  "kitchen.delivery_badge_ready_pickup": {
    kz: "ДАЙЫН — ӨЗІ АЛАДЫ",
    ru: "ГОТОВ — САМОВЫВОЗ",
    en: "READY — PICKUP",
  },
  "kitchen.delivery_pickup": {
    kz: "Өзімен алып кету",
    ru: "Самовывоз",
    en: "Pickup",
  },
  "kitchen.delivery_scheduled": {
    kz: "Уақытына: {t} — ерте дайындамаңыз",
    ru: "Ко времени: {t} — раньше не готовить",
    en: "Scheduled: {t} — don't cook early",
  },
  "kitchen.delivery_today": {
    kz: "бүгін",
    ru: "сегодня",
    en: "today",
  },
  "kitchen.delivery_tomorrow": {
    kz: "ертең",
    ru: "завтра",
    en: "tomorrow",
  },
  "kitchen.delivery_items": {
    kz: "Құрамы",
    ru: "Состав",
    en: "Items",
  },
  "kitchen.delivery_pay_cash": {
    kz: "Қолма-қол",
    ru: "Наличные",
    en: "Cash",
  },
  "kitchen.delivery_confirm": {
    kz: "Беруге дайын",
    ru: "Готово к выдаче",
    en: "Ready for handover",
  },
  "kitchen.delivery_new_toast": {
    kz: "Жаңа жеткізу тапсырысы!",
    ru: "Новый заказ на доставку!",
    en: "New delivery order!",
  },
  "kitchen.kds_rounds_dishes": {
    kz: "{r} рет · {d} тағам",
    ru: "{r} зак. · {d} блюд",
    en: "{r} orders · {d} dishes",
  },
  "kitchen.kds_sub_new": {
    kz: "Жаңа",
    ru: "Новые",
    en: "New",
  },
  "kitchen.kds_sub_cooking": {
    kz: "Дайындалуда",
    ru: "Готовятся",
    en: "Cooking",
  },
  "kitchen.kds_sub_ready": {
    kz: "Дайын",
    ru: "Готовы",
    en: "Ready",
  },
  "kitchen.kds_act_start": {
    kz: "Бастау",
    ru: "Начать",
    en: "Start",
  },
  "kitchen.kds_act_ready": {
    kz: "Дайын",
    ru: "Готово",
    en: "Ready",
  },
  "kitchen.kds_act_serve": {
    kz: "Берілді",
    ru: "Подать",
    en: "Serve",
  },
  "kitchen.kds_accept_all_new": {
    kz: "Барлық жаңасын қабылдау ({n})",
    ru: "Принять все новые ({n})",
    en: "Accept all new ({n})",
  },
  "kitchen.kds_serve_all_ready": {
    kz: "Барлық дайынды беру ({n})",
    ru: "Подать все готовые ({n})",
    en: "Serve all ready ({n})",
  },
  "kitchen.kds_ready_all_cooking": {
    kz: "Барлығы дайын ({n})",
    ru: "Готовы все ({n})",
    en: "All ready ({n})",
  },
  "kitchen.kds_sub_mass_received": {
    kz: "Бәрін бастау",
    ru: "Начать всё",
    en: "Start all",
  },
  "kitchen.kds_sub_mass_cooking": {
    kz: "Бәрі дайын",
    ru: "Всё готово",
    en: "All ready",
  },
  "kitchen.kds_sub_mass_ready": {
    kz: "Бәрін беру",
    ru: "Подать всё",
    en: "Serve all",
  },
  "kitchen.kds_col_tables": {
    kz: "{n} үстел",
    ru: "{n} стол.",
    en: "{n} tables",
  },
  "kitchen.kds_card_accept": {
    kz: "Үстелді қабылдау",
    ru: "Принять стол",
    en: "Accept table",
  },
  "kitchen.kds_card_ready": {
    kz: "Үстел дайын",
    ru: "Стол готов",
    en: "Table ready",
  },
  "kitchen.kds_card_serve": {
    kz: "Үстелді беру",
    ru: "Подать стол",
    en: "Serve table",
  },
  "kitchen.kds_ready_to_serve": {
    kz: "Беруге дайын",
    ru: "Готов к подаче",
    en: "Ready to serve",
  },
  "kitchen.kds_no_active": {
    kz: "Белсенді тапсырыстар жоқ",
    ru: "Нет активных заказов",
    en: "No active orders",
  },
  "kitchen.order_minutes_short": {
    kz: "{n}мин",
    ru: "{n}м",
    en: "{n}m",
  },
  "kitchen.order_items_count": {
    kz: "{n} поз.",
    ru: "{n} поз.",
    en: "{n} items",
  },
  "kitchen.btn_start_cooking": {
    kz: "🔥 Дайындауды бастау",
    ru: "🔥 Начать готовить",
    en: "🔥 Start cooking",
  },
  "kitchen.btn_ready": {
    kz: "✓ Дайын",
    ru: "✓ Готово",
    en: "✓ Ready",
  },
  "kitchen.status_waiting_waiter": {
    kz: "Даяшыны күтуде",
    ru: "Ждёт официанта",
    en: "Waiting for waiter",
  },

  // Деталь заказа
  "kitchen.order_not_found": {
    kz: "Тапсырыс табылмады",
    ru: "Заказ не найден",
    en: "Order not found",
  },
  "kitchen.btn_back": {
    kz: "← Артқа",
    ru: "← Назад",
    en: "← Back",
  },
  "kitchen.minutes_ago": {
    kz: "{n} мин бұрын",
    ru: "{n} мин назад",
    en: "{n} min ago",
  },
  "kitchen.status_cooking": {
    kz: "🔥 Дайындалуда",
    ru: "🔥 Готовится",
    en: "🔥 Cooking",
  },
  "kitchen.status_ready": {
    kz: "✅ Дайын",
    ru: "✅ Готово",
    en: "✅ Ready",
  },
  "kitchen.order_note": {
    kz: "ⓘ Ескертпе: «{text}»",
    ru: "ⓘ Заметка: «{text}»",
    en: "ⓘ Note: “{text}”",
  },
  "kitchen.btn_start_whole_order": {
    kz: "🔥 Бүкіл тапсырысты бастау",
    ru: "🔥 Начать весь заказ",
    en: "🔥 Start whole order",
  },
  "kitchen.btn_whole_order_ready": {
    kz: "✅ Бүкіл тапсырыс дайын",
    ru: "✅ Весь заказ готов",
    en: "✅ Whole order ready",
  },

  // Таймеры
  "kitchen.timers_active": {
    kz: "⏱ Белсенді таймерлер",
    ru: "⏱ Активные таймеры",
    en: "⏱ Active timers",
  },
  "kitchen.timers_title_short": {
    kz: "⏱ Таймерлер",
    ru: "⏱ Таймеры",
    en: "⏱ Timers",
  },
  "kitchen.timers_dishes_cooking": {
    kz: "{count} тағам дайындалуда",
    ru: "{count} блюд готовится",
    en: "{count} dishes cooking",
  },
  "kitchen.timers_new": {
    kz: "Жаңа таймер",
    ru: "Новый таймер",
    en: "New timer",
  },
  "kitchen.timers_add": {
    kz: "Таймер қосу",
    ru: "Добавить таймер",
    en: "Add timer",
  },
  "kitchen.timers_quick": {
    kz: "Жылдам таймерлер",
    ru: "Быстрые таймеры",
    en: "Quick timers",
  },
  "kitchen.timer_add_minute": {
    kz: "+1мин",
    ru: "+1м",
    en: "+1m",
  },
  "kitchen.timer_station_hot": {
    kz: "🔥 Ыстық",
    ru: "🔥 Горячий",
    en: "🔥 Hot",
  },
  "kitchen.timer_station_cold": {
    kz: "🥗 Суық",
    ru: "🥗 Холодный",
    en: "🥗 Cold",
  },
  "kitchen.timer_station_bar": {
    kz: "🍹 Бар",
    ru: "🍹 Бар",
    en: "🍹 Bar",
  },
  "kitchen.timer_field_dish": {
    kz: "Тағам",
    ru: "Блюдо",
    en: "Dish",
  },
  "kitchen.timer_dish_placeholder": {
    kz: "Мысалы: Бешбармақ",
    ru: "Например: Бешбармак",
    en: "e.g. Beshbarmak",
  },
  "kitchen.timer_field_station": {
    kz: "Цех",
    ru: "Цех",
    en: "Station",
  },
  "kitchen.timer_field_minutes": {
    kz: "Минут",
    ru: "Минут",
    en: "Minutes",
  },
  "kitchen.timer_unit_min": {
    kz: "мин",
    ru: "мин",
    en: "min",
  },
  "kitchen.timer_unit_minutes": {
    kz: "минут",
    ru: "минут",
    en: "minutes",
  },
  "kitchen.timer_quick_dish": {
    kz: "Тағам {min}м",
    ru: "Блюдо {min}м",
    en: "Dish {min}m",
  },
  "kitchen.btn_cancel": {
    kz: "Болдырмау",
    ru: "Отмена",
    en: "Cancel",
  },
  "kitchen.btn_start_timer": {
    kz: "⏱ Іске қосу",
    ru: "⏱ Запустить",
    en: "⏱ Start",
  },

  // Профиль
  "kitchen.profile_title": {
    kz: "Профиль",
    ru: "Профиль",
    en: "Profile",
  },
  "kitchen.profile_subtitle": {
    kz: "Сіздің баптауларыңыз",
    ru: "Ваши настройки",
    en: "Your settings",
  },
  "kitchen.profile_on_shift": {
    kz: "Ауысымда · {shift}",
    ru: "На смене · {shift}",
    en: "On shift · {shift}",
  },
  "kitchen.profile_sound_title": {
    kz: "Хабарлама дыбысы",
    ru: "Звук уведомлений",
    en: "Notification sound",
  },
  "kitchen.profile_sound_desc": {
    kz: "Жаңа тапсырыс пен таймерде сигнал",
    ru: "Сигнал при новом заказе и таймере",
    en: "Beep on new order and timer",
  },
  "kitchen.profile_voice_title": {
    kz: "Дауыспен басқару",
    ru: "Голосовое управление",
    en: "Voice control",
  },
  "kitchen.profile_voice_desc": {
    kz: "«окей, повар» командалары",
    ru: "Команды «окей, повар»",
    en: "“okay, chef” commands",
  },
  "kitchen.profile_timer_title": {
    kz: "Әдепкі таймер уақыты",
    ru: "Время таймера по умолчанию",
    en: "Default timer duration",
  },
  "kitchen.profile_timer_desc": {
    kz: "«Жаңа таймер» түймесі үшін",
    ru: "Для кнопки «Новый таймер»",
    en: "For the “New timer” button",
  },
  "kitchen.profile_timer_value": {
    kz: "{n} мин",
    ru: "{n} мин",
    en: "{n} min",
  },
  "kitchen.profile_exit": {
    kz: "Шығу",
    ru: "Выйти",
    en: "Sign out",
  },
  "kitchen.profile_exit_desc": {
    kz: "Ауысымды аяқтау",
    ru: "Закончить смену",
    en: "End the shift",
  },

  // Склад / ревизия
  "kitchen.inventory_title_short": {
    kz: "Қойма",
    ru: "Склад",
    en: "Inventory",
  },
  "kitchen.inventory_title": {
    kz: "Ас үй қоймасы",
    ru: "Кухонный склад",
    en: "Kitchen inventory",
  },
  "kitchen.inventory_subtitle": {
    kz: "Не таусылды + ревизияға көмек",
    ru: "Что закончилось + помощь в ревизии",
    en: "What ran out + revision help",
  },
  "kitchen.inventory_scan_hint": {
    kz: "Тауардың штрих-кодына бағыттаңыз",
    ru: "Наведите на штрихкод товара",
    en: "Point at the product barcode",
  },
  "kitchen.inventory_out_title": {
    kz: "Таусылды — сатып алу керек",
    ru: "Закончилось — нужно докупить",
    en: "Out of stock — need to restock",
  },
  "kitchen.inventory_request_warehouse": {
    kz: "📞 Қоймадан сұрау",
    ru: "📞 Запросить у склада",
    en: "📞 Request from warehouse",
  },
  "kitchen.inventory_request_sent": {
    kz: "📞 Сұраныс қоймашыға жіберілді",
    ru: "📞 Запрос отправлен кладовщику",
    en: "📞 Request sent to the storekeeper",
  },
  "kitchen.inventory_no_revision": {
    kz: "Ревизия басталмаған",
    ru: "Ревизия не начата",
    en: "Revision not started",
  },
  "kitchen.inventory_no_revision_hint": {
    kz: "Қоймашы ревизияны бастағанда мұнда тауарлар тізімі шығады — өз аймағыңыздың қалдығын санауға көмектесесіз.",
    ru: "Когда кладовщик начнёт ревизию, здесь появится список товаров — поможете посчитать остатки своей зоны.",
    en: "When the storekeeper starts a revision, the item list will appear here — you'll help count your area's stock.",
  },
  "kitchen.inventory_in_progress": {
    kz: "Ревизия жүруде · {done} / {total} саналды",
    ru: "Идёт ревизия · посчитано {done} из {total}",
    en: "Revision in progress · {done} of {total} counted",
  },
  "kitchen.inventory_started_by": {
    kz: " · бастаған {name}",
    ru: " · начал {name}",
    en: " · started by {name}",
  },
  "kitchen.inventory_scan": {
    kz: "▦ Сканерлеу",
    ru: "▦ Сканировать",
    en: "▦ Scan",
  },
  "kitchen.inventory_in_system": {
    kz: "Жүйеде: {qty} {unit}",
    ru: "В системе: {qty} {unit}",
    en: "In system: {qty} {unit}",
  },
  "kitchen.inventory_recheck_placeholder": {
    kz: "Қайта тексеру…",
    ru: "Перепроверить…",
    en: "Re-check…",
  },
  "kitchen.inventory_actual_placeholder": {
    kz: "Нақты қанша",
    ru: "Сколько по факту",
    en: "Actual amount",
  },
  "kitchen.inventory_saved": {
    kz: "Сақталды",
    ru: "Сохранено",
    en: "Saved",
  },
  "kitchen.inventory_save_failed": {
    kz: "Сақтау мүмкін болмады",
    ru: "Не удалось сохранить",
    en: "Failed to save",
  },
  "kitchen.inventory_barcode_unlinked": {
    kz: "«{code}» штрих-коды тауарға байланбаған",
    ru: "Штрихкод «{code}» не привязан к товару",
    en: "Barcode “{code}” is not linked to a product",
  },
  "kitchen.inventory_found_not_in_list": {
    kz: "Тауар табылды, бірақ тізімде жоқ",
    ru: "Товар найден, но его нет в списке",
    en: "Product found, but not in the list",
  },
  "kitchen.inventory_barcode_error": {
    kz: "Штрих-код бойынша іздеу қатесі",
    ru: "Ошибка поиска по штрихкоду",
    en: "Barcode lookup error",
  },

  // Единицы измерения (склад)
  "kitchen.unit_g": { kz: "г", ru: "г", en: "g" },
  "kitchen.unit_kg": { kz: "кг", ru: "кг", en: "kg" },
  "kitchen.unit_ml": { kz: "мл", ru: "мл", en: "ml" },
  "kitchen.unit_l": { kz: "л", ru: "л", en: "l" },
  "kitchen.unit_pcs": { kz: "дана", ru: "шт", en: "pcs" },

  // Стоп-лист
  "kitchen.stoplist_title": {
    kz: "🚫 Стоп-парақ",
    ru: "🚫 Стоп-лист",
    en: "🚫 Stop-list",
  },
  "kitchen.stoplist_count": {
    kz: "{count} позиция қолжетімсіз",
    ru: "{count} позиций недоступны",
    en: "{count} items unavailable",
  },
  "kitchen.stoplist_add": {
    kz: "+ Қосу",
    ru: "+ Добавить",
    en: "+ Add",
  },
  // Без примера блюда: любой конкретный пример в стоп-листе читается как
  // готовая подсказка, повар подтверждает её не глядя (2026-07-27).
  "kitchen.stoplist_dish_placeholder": {
    kz: "Тағам атауы",
    ru: "Название блюда",
    en: "Dish name",
  },
  "kitchen.stoplist_all_available": {
    kz: "Барлық тағам қолжетімді!",
    ru: "Все блюда доступны!",
    en: "All dishes available!",
  },
  "kitchen.stoplist_empty": {
    kz: "Стоп-парақ бос",
    ru: "Стоп-лист пустой",
    en: "Stop-list is empty",
  },
  "kitchen.stoplist_in_list": {
    kz: "Стоп-парақта",
    ru: "В стоп-листе",
    en: "In stop-list",
  },
  "kitchen.stoplist_until": {
    kz: "{time} дейін",
    ru: "До {time}",
    en: "Until {time}",
  },
  "kitchen.stoplist_remove": {
    kz: "✓ Алып тастау",
    ru: "✓ Снять",
    en: "✓ Remove",
  },
  "kitchen.stoplist_reasons": {
    kz: "Әдеттегі себептер",
    ru: "Типичные причины",
    en: "Common reasons",
  },
  "kitchen.stoplist_modal_title": {
    kz: "🚫 Стоп-параққа қосу",
    ru: "🚫 Добавить в стоп-лист",
    en: "🚫 Add to stop-list",
  },
  "kitchen.stoplist_field_dish": {
    kz: "Тағам",
    ru: "Блюдо",
    en: "Dish",
  },
  "kitchen.stoplist_field_until": {
    kz: "Қай уақытқа дейін",
    ru: "До какого времени",
    en: "Until what time",
  },
  "kitchen.stoplist_field_reason": {
    kz: "Себебі (міндетті емес)",
    ru: "Причина (необязательно)",
    en: "Reason (optional)",
  },
  "kitchen.stoplist_reason_placeholder": {
    kz: "Ет таусылды",
    ru: "Закончилось мясо",
    en: "Ran out of meat",
  },
  "kitchen.stoplist_enter_dish": {
    kz: "Тағам атауын енгізіңіз",
    ru: "Введите название блюда",
    en: "Enter the dish name",
  },
  "kitchen.stoplist_added": {
    kz: "✅ {name} стоп-парақта",
    ru: "✅ {name} в стоп-листе",
    en: "✅ {name} added to stop-list",
  },
  "kitchen.stoplist_add_failed": {
    kz: "Сәтсіз: {error}",
    ru: "Не удалось: {error}",
    en: "Failed: {error}",
  },
  "kitchen.stoplist_removed": {
    kz: "✓ {name} алынды",
    ru: "✓ {name} убран",
    en: "✓ {name} removed",
  },
  "kitchen.stoplist_btn_add": {
    kz: "🚫 Қосу",
    ru: "🚫 Добавить",
    en: "🚫 Add",
  },
  "kitchen.error_generic": {
    kz: "Қате",
    ru: "Ошибка",
    en: "Error",
  },

  // Статистика
  "kitchen.stats_title": {
    kz: "📊 Статистика",
    ru: "📊 Статистика",
    en: "📊 Stats",
  },
  "kitchen.stats_shift": {
    kz: "Ауысым",
    ru: "Смена",
    en: "Shift",
  },
  "kitchen.stats_now_in_work": {
    kz: "Қазір жұмыста",
    ru: "Сейчас в работе",
    en: "Now in progress",
  },
  "kitchen.stats_new": {
    kz: "Жаңа",
    ru: "Новых",
    en: "New",
  },
  "kitchen.stats_cooking": {
    kz: "Дайындалуда",
    ru: "Готовится",
    en: "Cooking",
  },
  "kitchen.stats_ready": {
    kz: "Дайын",
    ru: "Готово",
    en: "Ready",
  },
  "kitchen.stats_total": {
    kz: "Барлығы",
    ru: "Всего",
    en: "Total",
  },
  "kitchen.stats_wait_time": {
    kz: "Күту уақыты",
    ru: "Время ожидания",
    en: "Wait time",
  },
  "kitchen.stats_avg_wait": {
    kz: "Орташа күту",
    ru: "Средн. ожидание",
    en: "Avg. wait",
  },
  "kitchen.stats_longest_wait": {
    kz: "Ең ұзақ күтуде",
    ru: "Дольше всех ждёт",
    en: "Longest wait",
  },
  "kitchen.stats_shift_today": {
    kz: "Бүгінгі ауысым",
    ru: "За смену (сегодня)",
    en: "This shift (today)",
  },
  "kitchen.stats_served": {
    kz: "Берілді",
    ru: "Подано",
    en: "Served",
  },
  "kitchen.stats_cooked": {
    kz: "Дайындалды",
    ru: "Приготовлено",
    en: "Cooked",
  },
  "kitchen.stats_unit_dishes": {
    kz: "тағам",
    ru: "блюд",
    en: "dishes",
  },

  // Голосовой оверлей (только не-командный UI; примеры команд — RU, см. компонент)
  "kitchen.voice_unsupported": {
    kz: "Дауыс қолдау көрсетілмейді",
    ru: "Голос не поддерживается",
    en: "Voice not supported",
  },
  "kitchen.voice_mic_blocked": {
    kz: "Микрофон бұғатталған",
    ru: "Микрофон заблокирован",
    en: "Microphone blocked",
  },
  "kitchen.voice_mic_no_access": {
    kz: "Микрофонға қол жеткізу жоқ",
    ru: "Нет доступа к микрофону",
    en: "No microphone access",
  },
  "kitchen.voice_unsupported_hint": {
    kz: "Ас үйді планшетте/Android-та Chrome-да ашыңыз — онда сөзді тану жұмыс істейді.",
    ru: "Откройте кухню в Chrome на планшете/Android — там работает распознавание речи.",
    en: "Open the kitchen in Chrome on a tablet/Android — speech recognition works there.",
  },
  "kitchen.voice_mic_blocked_hint": {
    kz: "Браузер бұл сайт үшін микрофонды бұғаттады — сұраныс енді шықпайды. Сайт баптауларын ашыңыз (мекенжайдың сол жағындағы 🔒/ⓘ белгісі → «Рұқсаттар» → «Микрофон» → «Рұқсат ету»), содан кейін «Қайталау» түймесін басыңыз. Егер ас үй қосымша ретінде ашылса — Android баптаулары → Қосымшалар → осы ас үй → Рұқсаттар → Микрофон.",
    ru: "Браузер заблокировал микрофон для этого сайта — запрос больше не появится. Откройте настройки сайта (значок 🔒/ⓘ слева от адреса → «Разрешения» → «Микрофон» → «Разрешить»), затем нажмите «Повторить». Если кухня открыта как приложение — Настройки Android → Приложения → эта кухня → Разрешения → Микрофон.",
    en: "The browser blocked the microphone for this site — the prompt won't appear again. Open site settings (the 🔒/ⓘ icon left of the address → “Permissions” → “Microphone” → “Allow”), then tap “Retry”. If the kitchen is installed as an app — Android Settings → Apps → this kitchen → Permissions → Microphone.",
  },
  "kitchen.voice_mic_allow_hint": {
    kz: "«Микрофонға рұқсат ету» түймесін басыңыз — браузер рұқсат сұрайды.",
    ru: "Нажмите «Разрешить микрофон» — браузер запросит доступ.",
    en: "Tap “Allow microphone” — the browser will request access.",
  },
  "kitchen.voice_allow_mic": {
    kz: "🎤 Микрофонға рұқсат ету",
    ru: "🎤 Разрешить микрофон",
    en: "🎤 Allow microphone",
  },
  "kitchen.voice_allowed_retry": {
    kz: "🔄 Рұқсат бердім — қайталау",
    ru: "🔄 Я разрешил — повторить",
    en: "🔄 I allowed it — retry",
  },
  "kitchen.voice_listening": {
    kz: "Тыңдап тұрмын...",
    ru: "Слушаю...",
    en: "Listening...",
  },
  "kitchen.voice_starting": {
    kz: "Іске қосылуда...",
    ru: "Запуск...",
    en: "Starting...",
  },
  "kitchen.voice_commands_label": {
    kz: "Командалар:",
    ru: "Команды:",
    en: "Commands:",
  },
  "kitchen.voice_close": {
    kz: "Жабу",
    ru: "Закрыть",
    en: "Close",
  },
  "kitchen.voice_done": {
    kz: "Дайын",
    ru: "Готово",
    en: "Done",
  },
  "kitchen.voice_recognition_lang": {
    kz: "Тану: Орысша",
    ru: "Распознавание: Русский",
    en: "Recognition: Russian",
  },

  // Кухня — прошедшее время (KDS-карточка и formatElapsed)
  "kitchen.elapsed_min_short": { kz: "{n} мин", ru: "{n}м", en: "{n}m" },
  "kitchen.elapsed_hour_min_short": { kz: "{h} сағ {m} мин", ru: "{h}ч {m}м", en: "{h}h {m}m" },
  "kitchen.elapsed_sec": { kz: "{n} сек", ru: "{n} сек", en: "{n} sec" },
  "kitchen.elapsed_min": { kz: "{n} мин", ru: "{n} мин", en: "{n} min" },
  "kitchen.elapsed_hour": { kz: "{n} сағ", ru: "{n} ч", en: "{n} h" },
  "kitchen.elapsed_hour_min": { kz: "{h} сағ {m} мин", ru: "{h} ч {m} мин", en: "{h} h {m} min" },

  // Стоп-лист — «до конца дня» как подпись карточки (без предлога дважды)
  "kitchen.stoplist_until_end_of_day": {
    kz: "Күн соңына дейін",
    ru: "До конца дня",
    en: "Until end of day",
  },
  "kitchen.stoplist_reason_ingredient": {
    kz: "Ингредиент таусылды",
    ru: "Закончился ингредиент",
    en: "Ran out of an ingredient",
  },
  "kitchen.stoplist_reason_equipment": {
    kz: "Жабдық бұзылды",
    ru: "Сломалось оборудование",
    en: "Equipment broke down",
  },
  "kitchen.stoplist_reason_no_time": {
    kz: "Үлгермей жатырмыз",
    ru: "Не успеваем",
    en: "We can't keep up",
  },
  "kitchen.stoplist_reason_dough": {
    kz: "Қамыр таусылды",
    ru: "Тесто закончилось",
    en: "Ran out of dough",
  },
  "kitchen.stoplist_reason_meat": {
    kz: "Ет жоқ",
    ru: "Мяса нет",
    en: "No meat",
  },
  "kitchen.stoplist_reason_chef": {
    kz: "Аспазбасының шешімімен",
    ru: "По решению шефа",
    en: "Chef's decision",
  },

  // ─────────────────────────────────────────────────────────────────────
  // AI-помощник персонала (StaffAiSheet / StaffAiFab). Кабинеты — kz/ru/en.
  // ─────────────────────────────────────────────────────────────────────
  "staff_ai.title_waiter": { kz: "🤝 Даяшы көмекшісі", ru: "🤝 Помощник официанта", en: "🤝 Waiter assistant" },
  "staff_ai.title_cashier": { kz: "💳 Кассир көмекшісі", ru: "💳 Помощник кассира", en: "💳 Cashier assistant" },
  "staff_ai.title_admin": { kz: "📊 Талдау көмекшісі", ru: "📊 Аналитик-помощник", en: "📊 Analytics assistant" },
  "staff_ai.title_owner": { kz: "👑 Талдау көмекшісі", ru: "👑 Аналитик-помощник", en: "👑 Analytics assistant" },
  "staff_ai.title_manager": { kz: "📋 Менеджер көмекшісі", ru: "📋 Помощник менеджера", en: "📋 Manager assistant" },
  "staff_ai.title_cook": { kz: "👨‍🍳 Аспаз көмекшісі", ru: "👨‍🍳 Помощник повара", en: "👨‍🍳 Cook assistant" },
  "staff_ai.title_host": { kz: "🎩 Хостес көмекшісі", ru: "🎩 Помощник хостес", en: "🎩 Host assistant" },
  "staff_ai.title_courier": { kz: "🛵 Курьер көмекшісі", ru: "🛵 Помощник курьера", en: "🛵 Courier assistant" },

  "staff_ai.suggest_waiter_1": {
    kz: "Қазір менде қандай үстелдер бар?",
    ru: "Какие у меня сейчас столы?",
    en: "Which tables are mine right now?",
  },
  "staff_ai.suggest_waiter_2": {
    kz: "Десерт ретінде не ұсынуға болады?",
    ru: "Что предложить как десерт?",
    en: "What dessert should I suggest?",
  },
  "staff_ai.suggest_waiter_3": {
    kz: "Қандай ашық сұраныстар бар?",
    ru: "Какие открытые запросы?",
    en: "What requests are open?",
  },
  "staff_ai.suggest_cashier_1": {
    kz: "Қазір қанша ашық тапсырыс бар?",
    ru: "Сколько открытых заказов сейчас?",
    en: "How many open orders are there?",
  },
  "staff_ai.suggest_cashier_2": {
    kz: "Ауысым бойынша түсім қанша?",
    ru: "Какая выручка за смену?",
    en: "What is the revenue for this shift?",
  },
  "staff_ai.suggest_cashier_3": {
    kz: "3850 ₸ тапсырысқа 5000 ₸-ден қайтым қанша?",
    ru: "Сколько сдачи с 5000 ₸ за заказ 3850 ₸?",
    en: "How much change from ₸5000 for a ₸3850 order?",
  },
  "staff_ai.suggest_admin_1": {
    kz: "Бүгінгі топ-5 тағам?",
    ru: "Топ-5 блюд за сегодня?",
    en: "Top 5 dishes today?",
  },
  "staff_ai.suggest_admin_2": {
    kz: "Бүгінгі түсім қанша?",
    ru: "Какая выручка за сегодня?",
    en: "What is today's revenue?",
  },
  "staff_ai.suggest_admin_3": {
    kz: "Мәзірден қай тағамдарды алып тастаған жөн?",
    ru: "Какие блюда стоит убрать из меню?",
    en: "Which dishes should be removed from the menu?",
  },
  "staff_ai.suggest_owner_1": {
    kz: "Бүгінгі топ-5 тағам?",
    ru: "Топ-5 блюд за сегодня?",
    en: "Top 5 dishes today?",
  },
  "staff_ai.suggest_owner_2": {
    kz: "Бүгінгі түсім қанша?",
    ru: "Какая выручка за сегодня?",
    en: "What is today's revenue?",
  },
  "staff_ai.suggest_owner_3": {
    kz: "Қай тағамдар ең жақсы сатылып жатыр?",
    ru: "Какие блюда лучше всего идут?",
    en: "Which dishes sell best?",
  },
  "staff_ai.suggest_manager_1": {
    kz: "Қанша үстел ашық?",
    ru: "Сколько столов открыто?",
    en: "How many tables are open?",
  },
  "staff_ai.suggest_manager_2": {
    kz: "Бүгінгі үздік тағамдар?",
    ru: "Топ блюд сегодня?",
    en: "Top dishes today?",
  },
  "staff_ai.suggest_manager_3": {
    kz: "Ауысымда қай даяшылар бар?",
    ru: "Какие официанты на смене?",
    en: "Which waiters are on shift?",
  },
  "staff_ai.suggest_cook_1": {
    kz: "Қанша белсенді тапсырыс бар?",
    ru: "Сколько активных заказов?",
    en: "How many active orders?",
  },
  "staff_ai.suggest_cook_2": {
    kz: "Алдымен нені дайындау керек?",
    ru: "Что приготовить первым?",
    en: "What should I cook first?",
  },
  "staff_ai.suggest_cook_3": {
    kz: "Стоп-парақта қандай тағамдар бар?",
    ru: "Какие блюда в стоп-листе?",
    en: "Which dishes are on the stop-list?",
  },
  "staff_ai.suggest_host_1": {
    kz: "Бүгін кешке қанша брондау бар?",
    ru: "Сколько броней сегодня вечером?",
    en: "How many bookings tonight?",
  },
  "staff_ai.suggest_host_2": {
    kz: "19:00-ге бос үстелдерді көрсет",
    ru: "Покажи свободные столы на 19:00",
    en: "Show free tables for 19:00",
  },
  "staff_ai.suggest_host_3": {
    kz: "Бүгін қай қонақтар келді?",
    ru: "Кто из гостей пришёл сегодня?",
    en: "Which guests arrived today?",
  },
  "staff_ai.suggest_courier_1": {
    kz: "Бүгін менде қанша жеткізу бар?",
    ru: "Сколько доставок у меня сегодня?",
    en: "How many deliveries do I have today?",
  },
  "staff_ai.suggest_courier_2": {
    kz: "Ең жақын тапсырыс қайда?",
    ru: "Где ближайший заказ?",
    en: "Where is the nearest order?",
  },
  "staff_ai.suggest_courier_3": {
    kz: "Тапсыруға қанша сома жиналды?",
    ru: "Какая сумма к сдаче?",
    en: "How much cash do I owe?",
  },

  "staff_ai.clear": { kz: "Тазалау", ru: "Очистить", en: "Clear" },
  "staff_ai.close": { kz: "Жабу", ru: "Закрыть", en: "Close" },
  "staff_ai.send": { kz: "Жіберу", ru: "Отправить", en: "Send" },
  "staff_ai.ask_prompt": {
    kz: "Бірдеңе сұра — мысалы:",
    ru: "Спроси что-нибудь — например:",
    en: "Ask something — for example:",
  },
  "staff_ai.thinking": { kz: "⏳ Ойланып жатырмын…", ru: "⏳ Думаю…", en: "⏳ Thinking…" },
  "staff_ai.error": { kz: "Қате: {error}", ru: "Ошибка: {error}", en: "Error: {error}" },
  "staff_ai.listening": { kz: "🎤 Тыңдап тұрмын…", ru: "🎤 Слушаю…", en: "🎤 Listening…" },
  "staff_ai.message_placeholder": { kz: "Хабарлама…", ru: "Сообщение…", en: "Message…" },
  "staff_ai.voice_start": { kz: "Дауыспен енгізу", ru: "Голосовой ввод", en: "Voice input" },
  "staff_ai.voice_stop": { kz: "Жазуды тоқтату", ru: "Остановить запись", en: "Stop recording" },
  "staff_ai.tts_stop": { kz: "Дыбыстауды тоқтату", ru: "Остановить озвучку", en: "Stop speaking" },
  "staff_ai.fab_title": { kz: "AI-көмекші", ru: "AI-помощник", en: "AI assistant" },

};

// Lane D: мёрж сгенерированных zh/ky/uz переводов гостевых строк (guest.*) в
// strings. Оверлей guestLangOverlay.ts регенерируется скриптами scripts/
// (extract → перевод → build-overlay). Ядро kz/ru/en остаётся инлайном выше;
// здесь дописываются только новые языки. Недостающие ключи деградируют на ru.
for (const [key, overlayLangs] of Object.entries(GUEST_LANG_OVERLAY)) {
  const row = strings[key];
  if (row) Object.assign(row, overlayLangs);
}

// ─────────────────────────────────────────────────────────────────────────
// SUPPORTED_LANGS — ЕДИНЫЙ canonical источник списка языков. Читают: гостевой
// LangSwitcher, useGuestLang.parseLang (через isLang), admin-пикер языков,
// тест полноты. Раньше список дублировался в LangSwitcher + parseLang (хардкод
// kz|ru|en) — добавление языка требовало правки 4 мест. Теперь — только здесь.
//
//   code  — код языка (ключ в strings / LocalizedName)
//   label — native-название (на самом языке, чтобы носитель нашёл свой пункт)
//   flag  — эмодзи-флаг для свитчера
// ─────────────────────────────────────────────────────────────────────────
export const SUPPORTED_LANGS: ReadonlyArray<{
  code: Lang;
  label: string;
  flag: string;
}> = [
  { code: "kz", label: "Қазақша", flag: "🇰🇿" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "ky", label: "Кыргызча", flag: "🇰🇬" },
  { code: "uz", label: "Oʻzbekcha", flag: "🇺🇿" }, // uz-Latn (современный стандарт)
];

/** Базовые языки — для них полнота перевода ГАРАНТИРУЕТСЯ (тест-инвариант). */
export const CORE_LANGS: ReadonlyArray<Lang> = ["kz", "ru", "en"];

const LANG_CODES: ReadonlyArray<string> = SUPPORTED_LANGS.map((l) => l.code);

/** Runtime-guard языка. Единый источник для parseLang / admin-coerce / фильтра. */
export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && LANG_CODES.includes(v);
}

/** BCP-47 locale каждого языка — для toLocaleDateString / toLocaleString. */
const LANG_LOCALES: Readonly<Record<Lang, string>> = {
  kz: "kk-KZ",
  ru: "ru-RU",
  en: "en-US",
  zh: "zh-CN",
  ky: "ky-KG",
  uz: "uz-UZ",
};

/**
 * Locale для форматирования дат/чисел на языке гостя.
 *
 * Компоненты писали маппинг руками тернаром `lang === "kz" ? "kk-KZ" : ...`,
 * и всё, что не kz/en, падало на ru-RU: гость с интерфейсом на китайском видел
 * месяцы по-русски. Единый источник — здесь, рядом со списком языков.
 */
export function localeForLang(lang: Lang): string {
  return LANG_LOCALES[lang] ?? "ru-RU";
}

/**
 * Dev/тест-инвариант: ключи, потерявшие любой из CORE-языков (kz/ru/en).
 * Пустой массив = ядро полное. Возвращает заменяет компайл-тайм гарантию,
 * утраченную при переходе strings на Partial-тип.
 */
export function missingCoreLangKeys(): Array<{ key: string; lang: Lang }> {
  const out: Array<{ key: string; lang: Lang }> = [];
  for (const key of Object.keys(strings)) {
    const row = strings[key];
    for (const lang of CORE_LANGS) {
      if (!row[lang]) out.push({ key, lang });
    }
  }
  return out;
}

/**
 * Тест-инвариант гостевых языков: ключи `guest.*`, у которых нет zh/ky/uz.
 *
 * Гость выбирает язык из всех шести (SUPPORTED_LANGS), поэтому недостающий
 * перевод в гостевом слое — это не «деградация», а русский текст на экране
 * китайца. Ядро kz/ru/en страхует missingCoreLangKeys(), этот — новые языки.
 *
 * Ключи, у которых в ru нет ни одной буквы (только плейсхолдеры вроде
 * `{label}` или цифры/пунктуация), пропускаются: переводить там нечего.
 */
export function missingGuestLangKeys(): Array<{ key: string; lang: Lang }> {
  const extraLangs = SUPPORTED_LANGS.map((l) => l.code).filter(
    (code) => !CORE_LANGS.includes(code),
  );
  const out: Array<{ key: string; lang: Lang }> = [];
  for (const key of Object.keys(strings)) {
    if (!key.startsWith("guest.")) continue;
    const row = strings[key];
    // Убираем плейсхолдеры — если после этого букв не осталось, строка
    // языконезависима ("{label}", "{n} ₸") и перевод ей не нужен.
    const translatable = (row.ru ?? "").replace(/\{[^}]*\}/g, "");
    if (!/\p{L}/u.test(translatable)) continue;
    for (const lang of extraLangs) {
      if (!row[lang]) out.push({ key, lang });
    }
  }
  return out;
}

export function pickLocalized(name: LocalizedName, lang: Lang): string {
  return name[lang] ?? name.ru ?? name.kz ?? name.en ?? "—";
}

export function t(key: string, lang: Lang, vars?: Record<string, string>): string {
  const row = strings[key];
  let out = row?.[lang] ?? row?.ru ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      out = out.replaceAll(`{${k}}`, v);
    }
  }
  return out;
}
